import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { risk_portal_dashboard, risk_portal_heatmap, risk_portal_risks, risk_portal_risk_detail, risk_portal_assess, risk_portal_create_treatment, risk_portal_update_treatment, risk_portal_kri_list, risk_portal_create_kri, risk_portal_add_control, risk_portal_categories, risk_portal_create_category, risk_portal_appetite, risk_portal_save_appetite, risk_portal_workflow_config, risk_portal_update_workflow, risk_portal_users, risk_portal_invite, risk_portal_update_user_role, risk_portal_roles_list, risk_portal_role_create, risk_portal_role_update, risk_portal_role_delete, risk_portal_role_resend_invite, risk_portal_roles_audit_log, risk_portal_bus_list, risk_portal_bu_tree, risk_portal_bu_create, risk_portal_bu_update, risk_portal_bu_delete, risk_portal_bu_breakdown, risk_portal_get_settings, risk_portal_update_settings, risk_portal_projects_list, risk_portal_project_create, risk_portal_project_update, risk_portal_project_delete, risk_portal_project_breakdown, risk_portal_portfolio_trend, risk_portal_field_changes, risk_portal_sla_escalations, risk_portal_dispatch_escalations, risk_portal_login, risk_portal_compliance_mappings, risk_portal_map_compliance, risk_portal_delete_compliance_mapping, risk_portal_compliance_coverage, risk_portal_add_comment, risk_portal_upload_evidence, risk_portal_activity, risk_portal_approve_treatment, risk_portal_reject_treatment, risk_portal_overdue_treatments, risk_portal_update_kri, risk_portal_delete_kri, risk_portal_framework_controls, risk_portal_framework_list, risk_portal_update_control, risk_portal_delete_control, risk_portal_appetite_breaches, risk_portal_velocity_summary, risk_portal_delete_treatment, risk_portal_delete_category, risk_portal_delete_appetite, risk_portal_delete_evidence, risk_portal_delete_comment, risk_portal_pending_treatments, risk_portal_share_risk, risk_portal_list_shares, risk_portal_revoke_share, risk_portal_workflow_state, risk_portal_advance_stage, risk_portal_accept_risk, risk_portal_close_risk, risk_portal_seed_defaults, risk_portal_bulk_import, risk_portal_request_ack, risk_portal_get_ack, risk_portal_get_quantification, risk_portal_save_quantification, risk_portal_monte_carlo, risk_portal_get_bowtie, risk_portal_save_bowtie, risk_portal_list_scenarios, risk_portal_create_scenario, risk_portal_delete_scenario, risk_portal_ai_categorize, risk_portal_ai_duplicates, risk_portal_ai_risk_statement, risk_portal_ai_suggest_treatment, risk_portal_board_report, risk_portal_list_review_schedules, risk_portal_create_review_schedule, risk_portal_delete_review_schedule, add_new_risk, list_all_risk, total_risk_created, get_by_id_risk, risk_by_stats, risk_by_severity, download_risk_registry_template, delete_risk, update_risk_registry } from '../services';
import './RiskPortal.css';
import FullPageLoader from '../Components/FullPageLoader';

const fmtTime = (ts) => {
    if (!ts) return null;
    try {
        const d = new Date(ts);
        return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) + ', ' +
            d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
    } catch { return ts; }
};
const fmtDate = (ts) => {
    if (!ts) return null;
    try { return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return ts; }
};

// ═══════════════════════════════════════════════
// DEFAULT STAGE LABELS & COLORS
// ═══════════════════════════════════════════════
const DEFAULT_STAGE_LABELS = {
    identification: 'Identification', assessment: 'Assessment', treatment: 'Treatment Planning',
    review: 'Review', acceptance: 'Acceptance', monitoring: 'Monitoring', closed: 'Closed',
};
const DEFAULT_STAGE_COLORS = {
    identification: '#1b2a4a', assessment: '#0ea5e9', treatment: '#d97706',
    review: '#a855f7', acceptance: '#059669', monitoring: '#6366f1', closed: '#8492a6',
};

// ═══════════════════════════════════════════════
// ROLE-BASED UI CONFIGS (defaults, overridden by workflow config)
// ═══════════════════════════════════════════════
const DEFAULT_ROLE_CONFIGS = {
    risk_admin: {
        color: '#a855f7', sidebar: ['pm_home', 'settings', 'users', 'audit_log', 'business_units', 'projects', 'workflow', 'categories', 'appetite', 'compliance', 'review_schedules', 'guide', 'nova_ai'],
        dashboard_title: 'Risk Registry Administration',
        kpis: ['total_risks', 'critical_risks', 'kri_breaches', 'overdue_treatments'],
        permissions: { can_configure: true, can_assess: true, can_treat: true, can_approve: true },
    },
    risk_manager: {
        color: '#1b2a4a', sidebar: ['dashboard', 'risks', 'business_units', 'projects', 'heatmap', 'kris', 'treatments', 'appetite', 'compliance', 'scenarios', 'guide', 'nova_ai'],
        dashboard_title: 'Risk Manager Dashboard',
        kpis: ['total_risks', 'critical_risks', 'high_risks', 'treatment_progress', 'overdue_treatments', 'new_this_month'],
        permissions: { can_configure: false, can_assess: true, can_treat: true, can_approve: false },
    },
    risk_owner: {
        color: '#d97706', sidebar: ['dashboard', 'my_risks', 'risks', 'my_treatments', 'guide'],
        dashboard_title: 'My Risk Tasks',
        kpis: ['my_risks', 'pending_actions', 'upcoming_reviews', 'score_changes'],
        permissions: { can_configure: false, can_assess: false, can_treat: true, can_approve: false },
    },
    risk_reviewer: {
        color: '#0ea5e9', sidebar: ['dashboard', 'pending_reviews', 'risks', 'heatmap', 'guide'],
        dashboard_title: 'Review Dashboard',
        kpis: ['pending_reviews', 'approved_this_month', 'rejected', 'total_reviewed'],
        permissions: { can_configure: false, can_assess: false, can_treat: false, can_approve: true },
    },
    business_owner: {
        color: '#059669', sidebar: ['dashboard', 'pending_acceptance', 'bu_risks', 'heatmap', 'guide'],
        dashboard_title: 'Business Unit Risk Posture',
        kpis: ['bu_risk_count', 'bu_critical', 'acceptance_pending', 'risk_posture'],
        permissions: { can_configure: false, can_assess: false, can_treat: false, can_approve: true },
    },
    risk_committee: {
        color: '#be123c', sidebar: ['dashboard', 'pending_acceptance', 'risks', 'heatmap', 'guide'],
        dashboard_title: 'Risk Committee: High & Critical Acceptance',
        kpis: ['critical_risks', 'high_risks', 'acceptance_pending', 'risks_exceeding_appetite'],
        permissions: { can_configure: false, can_assess: false, can_treat: false, can_approve: true },
    },
    ciso: {
        color: '#7c3aed', sidebar: ['executive_dashboard', 'risks', 'heatmap', 'kris', 'appetite', 'compliance', 'audit_log', 'guide', 'reports'],
        dashboard_title: 'Executive Risk Dashboard (Read-Only)',
        kpis: ['risk_posture_score', 'total_risks', 'critical_risks', 'risks_exceeding_appetite', 'kri_breaches', 'treatment_completion'],
        permissions: { can_configure: false, can_assess: false, can_treat: false, can_approve: false },
    },
};

const getRoleConfig = (wfConfig, role) => {
    const defaults = DEFAULT_ROLE_CONFIGS[role] || DEFAULT_ROLE_CONFIGS.risk_owner;
    const override = wfConfig?.role_config?.[role];
    if (!override) return defaults;
    // Sidebar is hardcoded per role in DEFAULT_ROLE_CONFIGS — not customer-configurable
    // per 06-customisation.md (the 11 documented keys do not include sidebar).
    // Strip any stale role_config.sidebar from older backend data so v2 entries
    // (pending_acceptance, guide, audit_log, business_units, projects, compliance)
    // don't get hidden by an out-of-date config.
    const { sidebar: _ignoredSidebar, ...rest } = override;
    return { ...defaults, ...rest };
};

// Sidebar label mapping
const SIDEBAR_LABELS = {
    pm_home: 'Administration', dashboard: 'Dashboard', executive_dashboard: 'Executive Dashboard',
    risks: 'Risk Register', my_risks: 'My Risks', bu_risks: 'BU Risks',
    heatmap: 'Heat Map', kris: 'Key Risk Indicators', treatments: 'Treatment Plans',
    my_treatments: 'My Treatments', pending_reviews: 'Pending Reviews',
    appetite: 'Risk Appetite', categories: 'Categories', settings: 'Settings',
    users: 'User Management', workflow: 'Workflow', compliance: 'Compliance Coverage', scenarios: 'Scenarios', review_schedules: 'Review Schedules', nova_ai: 'NOVA AI', reports: 'Reports',
    guide: 'RBAC Guide', audit_log: 'Role Audit Log', pending_acceptance: 'Pending Acceptance',
    business_units: 'Business Units',
    projects: 'Projects',
};

// SVG icons (Feather-style)
const SvgIcon = ({ d, ...props }) => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>{typeof d === 'string' ? <path d={d} /> : d}</svg>;

// ───────────────────────────────────────────────
// CENTERED TOAST - enterprise-style status modal
// ───────────────────────────────────────────────
const TOAST_THEMES = {
    success: {
        bg: '#ecfdf5', border: '#10b981', text: '#065f46', accent: '#10b981',
        icon: <><circle cx="12" cy="12" r="10" /><path d="M9 12l2 2 4-4" /></>
    },
    error: {
        bg: '#fef2f2', border: '#ef4444', text: '#991b1b', accent: '#ef4444',
        icon: <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>
    },
    warning: {
        bg: '#fffbeb', border: '#f59e0b', text: '#92400e', accent: '#f59e0b',
        icon: <><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>
    },
    info: {
        bg: '#eff6ff', border: '#3b82f6', text: '#1e40af', accent: '#3b82f6',
        icon: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>
    },
};

// Field change history table - fetched lazily when the user expands the details
function FieldChangeHistory({ riskId, riskKey }) {
    const [rows, setRows] = useState(null);
    const [err, setErr] = useState('');
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await risk_portal_field_changes(riskId);
                if (cancelled) return;
                const data = res?.response_data?.data || res?.data?.data || res?.data || res || {};
                setRows(Array.isArray(data.changes) ? data.changes : []);
            } catch (e) {
                if (!cancelled) setErr(e?.message || 'Failed to load history');
            }
        })();
        return () => { cancelled = true; };
    }, [riskId]);
    if (err) return <div style={{ color: '#dc2626', padding: 8 }}>{err}</div>;
    if (rows == null) return <div style={{ padding: 8, color: '#64748b' }}>Loading...</div>;
    if (rows.length === 0) return <div style={{ padding: 8, color: '#64748b' }}>No tracked field changes for {riskKey} yet.</div>;
    return (
        <table className="rp-table rp-table-sm" style={{ marginTop: 8 }}>
            <thead><tr><th>When</th><th>Field</th><th>Old</th><th>New</th><th>By</th><th>Reason</th></tr></thead>
            <tbody>
                {rows.map(c => (
                    <tr key={c.id}>
                        <td style={{ whiteSpace: 'nowrap', color: '#64748b' }}>{c.created_at ? new Date(c.created_at).toLocaleString() : '-'}</td>
                        <td><code>{c.field}</code></td>
                        <td style={{ color: '#94a3b8' }}>{c.old || '-'}</td>
                        <td><b>{c.new || '-'}</b></td>
                        <td style={{ color: '#475569', fontSize: 12 }}>{c.actor_email || '-'}</td>
                        <td style={{ fontSize: 12 }}>{c.reason || '-'}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

// Compact empty-state placeholder used in chart cards and tables.
// Keeps the card from looking broken when data is unavailable.
function EmptyState({ title, hint, action }) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '20px 12px', textAlign: 'center', minHeight: 80 }}>
            <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--rp-surface-2)', border: '1px solid var(--rp-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--rp-text-3)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
            </div>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--rp-text)', marginBottom: hint ? 4 : 0 }}>{title}</div>
            {hint && <div style={{ fontSize: 12, color: 'var(--rp-text-3)', maxWidth: 280, lineHeight: 1.4 }}>{hint}</div>}
            {action && <div style={{ marginTop: 10 }}>{action}</div>}
        </div>
    );
}

// Inline notice banner - Atlassian Section Message style.
// Variants: info (default), warning, success, error. The ribbon is a single
// 3px coloured stripe - nothing more - so it looks like every other banner
// across the app. Replaces ad-hoc tinted divs with bright fills.
const BANNER_THEME = {
    info: { stripe: '#0052cc', fg: '#172b4d', bg: '#deebff20' },
    warning: { stripe: '#ff8b00', fg: '#172b4d', bg: '#fff7e620' },
    success: { stripe: '#006644', fg: '#172b4d', bg: '#e3fcef20' },
    error: { stripe: '#de350b', fg: '#172b4d', bg: '#ffebe620' },
};
function Banner({ variant = 'info', children, style }) {
    const t = BANNER_THEME[variant] || BANNER_THEME.info;
    return (
        <div style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            padding: '10px 14px', marginBottom: 12,
            background: t.bg, borderLeft: `3px solid ${t.stripe}`,
            borderTop: '1px solid var(--rp-border)', borderRight: '1px solid var(--rp-border)', borderBottom: '1px solid var(--rp-border)',
            borderRadius: 'var(--rp-r)',
            fontSize: 13, color: t.fg, lineHeight: 1.5,
            ...style,
        }}>{children}</div>
    );
}

function CenteredToast({ notification, onClose }) {
    const theme = TOAST_THEMES[notification.type] || TOAST_THEMES.info;
    const TITLE = { success: 'Success', error: 'Error', warning: 'Heads up', info: 'Info' }[notification.type] || 'Notice';
    return (
        <div
            role="status"
            aria-live="polite"
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 9999,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(15, 23, 42, 0.32)',
                backdropFilter: 'blur(2px)',
                animation: 'rp-toast-fade-in 160ms ease-out',
                pointerEvents: 'auto',
            }}>
            <style>{`
                @keyframes rp-toast-fade-in { from { opacity:0; transform: translateY(-6px) scale(.98); } to { opacity:1; transform:none; } }
                @keyframes rp-toast-progress { from { width:100%; } to { width:0%; } }
            `}</style>
            <div
                onClick={(e) => e.stopPropagation()}
                style={{
                    minWidth: 360, maxWidth: 520,
                    background: '#ffffff',
                    borderRadius: 12,
                    boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25), 0 4px 12px rgba(15, 23, 42, 0.10)',
                    overflow: 'hidden',
                    borderTop: `4px solid ${theme.accent}`,
                    animation: 'rp-toast-fade-in 200ms ease-out',
                }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '20px 24px 18px' }}>
                    <div style={{
                        flex: '0 0 auto', width: 40, height: 40, borderRadius: '50%',
                        background: theme.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.accent}
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            {theme.icon}
                        </svg>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: theme.text, letterSpacing: '0.02em', textTransform: 'uppercase', marginBottom: 4 }}>
                            {TITLE}
                        </div>
                        <div style={{ fontSize: 14, color: '#1e293b', lineHeight: 1.5, wordBreak: 'break-word' }}>
                            {notification.text}
                        </div>
                    </div>
                    <button onClick={onClose}
                        aria-label="Dismiss"
                        style={{
                            flex: '0 0 auto', background: 'transparent', border: 0, padding: 4, cursor: 'pointer',
                            color: '#94a3b8', borderRadius: 4,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#475569'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
                {/* progress bar that drains */}
                <div style={{ height: 3, background: '#f1f5f9', position: 'relative', overflow: 'hidden' }}>
                    <div key={notification.id} style={{
                        position: 'absolute', top: 0, left: 0, height: '100%',
                        background: theme.accent,
                        animation: `rp-toast-progress ${notification.type === 'error' ? 6000 : 3500}ms linear forwards`,
                    }} />
                </div>
            </div>
        </div>
    );
}

const SIDEBAR_ICONS = {
    pm_home: <SvgIcon d={<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>} />,
    dashboard: <SvgIcon d={<><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></>} />,
    executive_dashboard: <SvgIcon d={<><path d="M18 20V10" /><path d="M12 20V4" /><path d="M6 20v-6" /></>} />,
    risks: <SvgIcon d={<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>} />,
    my_risks: <SvgIcon d={<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>} />,
    bu_risks: <SvgIcon d={<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>} />,
    heatmap: <SvgIcon d={<><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" /><line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" /></>} />,
    kris: <SvgIcon d={<><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></>} />,
    treatments: <SvgIcon d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></>} />,
    my_treatments: <SvgIcon d={<><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>} />,
    pending_reviews: <SvgIcon d={<><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>} />,
    appetite: <SvgIcon d={<><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></>} />,
    categories: <SvgIcon d={<><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></>} />,
    settings: <SvgIcon d={<><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></>} />,
    users: <SvgIcon d={<><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>} />,
    workflow: <SvgIcon d={<><polyline points="16 3 21 3 21 8" /><line x1="4" y1="20" x2="21" y2="3" /><polyline points="21 16 21 21 16 21" /><line x1="15" y1="15" x2="21" y2="21" /><line x1="4" y1="4" x2="9" y2="9" /></>} />,
    scenarios: <SvgIcon d={<><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><path d="M12 8h.01" /></>} />,
    review_schedules: <SvgIcon d={<><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>} />,
    nova_ai: <SvgIcon d={<><path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" /></>} />,
    reports: <SvgIcon d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>} />,
    guide: <SvgIcon d={<><circle cx="12" cy="12" r="10" /><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" /><line x1="12" y1="17" x2="12.01" y2="17" /></>} />,
    audit_log: <SvgIcon d={<><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="9" y1="13" x2="15" y2="13" /><line x1="9" y1="17" x2="15" y2="17" /></>} />,
    pending_acceptance: <SvgIcon d={<><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></>} />,
    business_units: <SvgIcon d={<><path d="M3 21h18" /><path d="M5 21V7l7-4 7 4v14" /><path d="M9 9h.01" /><path d="M9 12h.01" /><path d="M9 15h.01" /><path d="M9 18h.01" /></>} />,
    projects: <SvgIcon d={<><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" /></>} />,
    compliance: <SvgIcon d={<><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><polyline points="9 12 11 14 15 10" /></>} />,
};

// ═══════════════════════════════════════════════
// HEAT MAP COLORS
// ═══════════════════════════════════════════════
// ISO 31000 5x5 Risk Matrix Colors (matched to dashboard severity palette)
// Low=#468C64 (green), Medium=#FDE047 (yellow), High=#BF4545 (red), Critical=#644891 (purple)
// Score = L × I: Low (1-4), Medium (5-9), High (10-15), Critical (16-25)
// ISO 31000 standard colors: Green(Low) -> Yellow(Medium) -> Orange(High) -> Red(Critical)
const HEATMAP_COLORS = [
    // L=1(Rare):     I=1(1) I=2(2) I=3(3) I=4(4) I=5(5)
    ['#19A07E', '#19A07E', '#19A07E', '#D9BF21', '#D9BF21'],
    // L=2(Unlikely):  I=1(2) I=2(4) I=3(6) I=4(8) I=5(10)
    ['#19A07E', '#19A07E', '#D9BF21', '#D9BF21', '#DE9E30'],
    // L=3(Possible):  I=1(3) I=2(6) I=3(9) I=4(12) I=5(15)
    ['#19A07E', '#D9BF21', '#D9BF21', '#DE9E30', '#DE9E30'],
    // L=4(Likely):    I=1(4) I=2(8) I=3(12) I=4(16) I=5(20)
    ['#D9BF21', '#D9BF21', '#DE9E30', '#D24646', '#D24646'],
    // L=5(Almost Certain): I=1(5) I=2(10) I=3(15) I=4(20) I=5(25)
    ['#D9BF21', '#DE9E30', '#DE9E30', '#D24646', '#D24646'],
];
// ISO 31000 standard severity colors: Green->Yellow->Orange->Red.
// Critical and High MUST be visibly distinct (not both red) - they sit next to
// each other on every dashboard.
const RATING_COLORS = { Low: '#16a34a', Medium: '#eab308', High: '#ea580c', Critical: '#b91c1c' };
// Backend payloads sometimes return rating as lowercase (model.severity) and
// sometimes title-case (RiskAssessment.inherent_rating). Normalise to title-case
// for consistent display + reliable RATING_COLORS lookup.
const normalizeRating = (r) => {
    const v = (r || '').toString();
    if (!v) return '';
    return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase();
};
// Lower-case alias used by some payload keys
const SEVERITY_COLORS = { low: '#16a34a', medium: '#eab308', high: '#ea580c', critical: '#b91c1c' };
// KPI card colors mirror the severity ladder so dashboards read the same regardless of role
const KPI_COLOR = {
    total: '#1b2a4a',          // neutral navy for totals
    critical: '#b91c1c',       // dark red
    high: '#ea580c',           // orange
    medium: '#eab308',         // amber
    low: '#16a34a',            // green
    overdue: '#b91c1c',        // dark red - same as critical (it IS critical)
    info: '#0ea5e9',           // sky blue for "info" KPIs
    pending: '#7c3aed',        // purple for review/acceptance pending
};

// Enterprise chart palette - Tableau-10 inspired categorical scheme used for
// "by type" / "by category" charts where colours separate slices but don't
// imply severity. Severity charts continue to use RATING_COLORS / SEVERITY_COLORS.
const RP_CATEGORICAL = [
    '#4e79a7', '#f28e2b', '#59a14f', '#e15759', '#76b7b2',
    '#edc948', '#b07aa1', '#ff9da7', '#9c755f', '#bab0ac',
];
// State colours used on the portfolio trend chart
const RP_TREND = { inflow: '#16a34a', outflow: '#0ea5e9', open: '#1e3a8a', net_pos: '#dc2626', net_neg: '#16a34a' };

// ISO 31000:2018 standard labels
const LIKELIHOOD_LABELS = ['Rare', 'Unlikely', 'Possible', 'Likely', 'Almost Certain'];
const IMPACT_LABELS = ['Insignificant', 'Minor', 'Moderate', 'Major', 'Catastrophic'];

// ISO 31000 rating calculator - unified 5x5 scale for both dashboard and portal
// Low (1-4), Medium (5-9), High (10-15), Critical (16-25)
const ratingOf5x5 = (score) => score >= 16 ? 'Critical' : score >= 10 ? 'High' : score >= 5 ? 'Medium' : 'Low';
const ratingOf4x4 = ratingOf5x5; // Dashboard now uses same 5x5 scale

// ═══════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════
export default function RiskPortal() {
    const navigate = useNavigate();

    // Auth & role
    const [role, setRole] = useState(null);
    const [userName, setUserName] = useState('');
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState(null);  // { text, type } | null
    const [loader, setLoader] = useState(false); // { text } | null

    // Navigation
    const [view, setView] = useState('dashboard');
    const [selectedRisk, setSelectedRisk] = useState(null);

    // Data
    const [dashData, setDashData] = useState({ total_risks: 0, by_state: {}, by_severity: {}, by_type: [], by_inherent_rating: {}, by_residual_rating: {}, treatment_by_status: {}, overdue_actions: 0, top_risks: [], kri_breaches: 0 });
    const [heatmapData, setHeatmapData] = useState(null);
    const [heatmapType, setHeatmapType] = useState('inherent');
    const [risks, setRisks] = useState({ results: [], count: 0, page: 1, pages: 1 });
    const [riskDetail, setRiskDetail] = useState(null);
    const [kriList, setKriList] = useState([]);
    const [categories, setCategories] = useState([]);
    const [appetites, setAppetites] = useState([]);
    const [wfConfig, setWfConfig] = useState(null);
    const [portalUsers, setPortalUsers] = useState([]);
    const [roleAuditLog, setRoleAuditLog] = useState([]);
    const [businessUnits, setBusinessUnits] = useState([]);
    const [orgSettings, setOrgSettings] = useState(null);
    // Dashboard scope filter - BU and/or Project. Persisted in component state
    // so it sticks while the user navigates between sub-pages and roles.
    const [dashScope, setDashScope] = useState({ bu_id: '', project_id: '' });
    // Customer-configurable severity colours, falling back to ISO 31000 defaults
    // (RATING_COLORS). Use this everywhere a severity badge is rendered so brand
    // / accessibility overrides flow through automatically.
    const ratingColor = (rating) => {
        const key = (rating || '').toString().toLowerCase();
        const custom = orgSettings?.rating_colors || {};
        if (custom[key]) return custom[key];
        const titled = key.charAt(0).toUpperCase() + key.slice(1);
        return RATING_COLORS[titled] || '#6366f1';
    };
    const [projects, setProjects] = useState([]);
    const [projectFilter, setProjectFilter] = useState('');
    const [projectBreakdown, setProjectBreakdown] = useState({ unassigned: { counts: {} }, projects: [] });
    const [portfolioTrend, setPortfolioTrend] = useState({ months: 6, series: [] });
    const [buBreakdown, setBuBreakdown] = useState({ organization_level: { counts: {} }, business_units: [] });
    const [buFilter, setBuFilter] = useState('');  // selected BU id for risk list filter
    const [showBuModal, setShowBuModal] = useState(false);
    const [buForm, setBuForm] = useState({ id: null, name: '', code: '', location: '', parent_id: '', risk_tier: 'medium' });

    // Project CRUD modal state
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [projectForm, setProjectForm] = useState({ id: null, name: '', code: '', project_type: 'medium', project_lifecycle: 'standard', business_unit_id: '', location: '', project_manager: '' });

    // Filters
    const [riskFilters, setRiskFilters] = useState({});
    const [riskPage, setRiskPage] = useState(1);
    const [viewAllOrgs, setViewAllOrgs] = useState(false);

    // Modals
    const [showAssessModal, setShowAssessModal] = useState(false);
    const [showTreatmentModal, setShowTreatmentModal] = useState(false);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [showCategoryModal, setShowCategoryModal] = useState(false);

    // Forms
    const [assessForm, setAssessForm] = useState({ inherent_likelihood: 1, inherent_impact: 1, residual_likelihood: 1, residual_impact: 1, control_effectiveness: 0, risk_velocity: 'months', notes: '' });
    const [treatmentForm, setTreatmentForm] = useState({ strategy: 'mitigate', description: '', actions: [] });
    const [inviteForm, setInviteForm] = useState({ email: '', first_name: '', last_name: '', role: 'risk_owner' });
    const [categoryForm, setCategoryForm] = useState({ name: '', description: '', color: '#6366f1' });
    const [commentText, setCommentText] = useState('');

    // Phase 2 state
    const [overdueTreatments, setOverdueTreatments] = useState([]);
    const [frameworkControls, setFrameworkControls] = useState([]);
    const [frameworks, setFrameworks] = useState([]);
    const [appetiteBreaches, setAppetiteBreaches] = useState({ total_breaches: 0, breaches: [] });
    const [controlSearch, setControlSearch] = useState('');
    const [controlFrameworkId, setControlFrameworkId] = useState('');
    const [showControlMapModal, setShowControlMapModal] = useState(false);
    const [showKriEditModal, setShowKriEditModal] = useState(false);
    const [editingKri, setEditingKri] = useState(null);
    const [showKriCreateModal, setShowKriCreateModal] = useState(false);
    const [kriForm, setKriForm] = useState({ name: '', description: '', unit: '', current_value: 0, threshold_warning: '', threshold_breach: '' });
    const [pendingTreatments, setPendingTreatments] = useState([]);
    const [workflowState, setWorkflowState] = useState(null);
    // Phase 3 state
    const [quantification, setQuantification] = useState(null);
    const [monteCarloResults, setMonteCarloResults] = useState(null);
    const [bowtieData, setBowtieData] = useState(null);
    const [scenarios, setScenarios] = useState([]);
    const [showScenarioModal, setShowScenarioModal] = useState(false);
    const [complianceMappings, setComplianceMappings] = useState([]);
    const [complianceCoverage, setComplianceCoverage] = useState([]);
    const [showRejectModal, setShowRejectModal] = useState(null); // treatment id
    const [scenarioForm, setScenarioForm] = useState({ name: '', description: '', likelihood: 3, impact_financial: '', impact_operational: 3, impact_reputational: 3, impact_regulatory: 3, assumptions: '', mitigation_strategy: '' });
    // AI state
    const [reviewSchedules, setReviewSchedules] = useState([]);
    const [boardReport, setBoardReport] = useState(null);
    const [aiLoading, setAiLoading] = useState(null);
    const [aiResult, setAiResult] = useState(null);
    const [aiInput, setAiInput] = useState('');
    const [aiError, setAiError] = useState(null);
    const [portalBackendAvailable, setPortalBackendAvailable] = useState(null); // null=unknown, true/false
    const [ackStatus, setAckStatus] = useState(null);
    const [closeBlockers, setCloseBlockers] = useState(null);
    const [showShareModal, setShowShareModal] = useState(false);
    const [shareForm, setShareForm] = useState({ recipient_email: '', recipient_name: '', permission: 'update', message: '', expires_days: 30 });
    const [riskShares, setRiskShares] = useState([]);
    const [shareUrl, setShareUrl] = useState('');
    const [showCreateRiskModal, setShowCreateRiskModal] = useState(false);
    const [riskForm, setRiskForm] = useState({ risk: '', threat: '', vulnerability: '', likelihood: 1, impact: 1, state: 'open', risk_type: '', risk_owner: '', target_date: '' });
    const [showEditRiskModal, setShowEditRiskModal] = useState(false);
    const [editRiskForm, setEditRiskForm] = useState(null);

    // Drill-down cell
    const [drillCell, setDrillCell] = useState(null);

    // Default workflow config (used when backend not yet deployed)
    const DEFAULT_WORKFLOW = {
        stages: [
            { id: 'identification', name: 'Identification', order: 1, stage_type: 'form', owner_role: 'risk_manager', next_stages: ['assessment'], sla_days: 3 },
            { id: 'assessment', name: 'Assessment', order: 2, stage_type: 'assessment', owner_role: 'risk_manager', next_stages: ['treatment'], sla_days: 5 },
            { id: 'treatment', name: 'Treatment Planning', order: 3, stage_type: 'treatment', owner_role: 'risk_owner', next_stages: ['review'], sla_days: 10 },
            { id: 'review', name: 'Review', order: 4, stage_type: 'review', owner_role: 'risk_reviewer', next_stages: ['acceptance'], sla_days: 5 },
            { id: 'acceptance', name: 'Acceptance', order: 5, stage_type: 'approval', owner_role: 'business_owner', next_stages: ['monitoring'], sla_days: 5 },
            { id: 'monitoring', name: 'Monitoring', order: 6, stage_type: 'monitoring', owner_role: 'risk_manager', next_stages: ['closed'], sla_days: null },
            { id: 'closed', name: 'Closed', order: 7, stage_type: 'closed', owner_role: null, next_stages: [], sla_days: null },
        ],
        role_config: DEFAULT_ROLE_CONFIGS,
    };

    // ──────── INIT ────────
    useEffect(() => {
        (async () => {
            // Auth guard — require portal-specific login (risk_portal_token) OR an active
            // main-dashboard session (access_token in sessionStorage). If a dashboard session
            // exists we sync it below, so don't redirect yet just because localStorage is empty.
            const portalToken = localStorage.getItem('risk_portal_token');
            const mainSessionToken = sessionStorage.getItem('access_token');
            if (!portalToken && !mainSessionToken) {
                navigate('/risk-portal/login');
                return;
            }
            // If main dashboard session exists, sync token.
            // Treat as a dashboard session (risk_admin) only when:
            //   a) explicitly flagged by RiskPortalAuth auto-redirect (rp_from_dashboard='1'), OR
            //   b) the dashboard user_profile email differs from the stored portal user email
            //      (stale session from a different user who used the portal previously), OR
            //   c) no portal user exists at all and it was not flagged as a portal login.
            // Never overwrite when rp_from_dashboard='0' (set by handleLogin in RiskPortalAuth)
            // because that means the user logged in via the portal form and has a valid role.
            if (mainSessionToken) {
                localStorage.setItem('risk_portal_token', mainSessionToken);
                const rpFlag = sessionStorage.getItem('rp_from_dashboard');
                const up0 = JSON.parse(localStorage.getItem('user_profile') || '{}');
                const pu0 = JSON.parse(localStorage.getItem('risk_portal_user') || '{}');
                const dashEmail = (up0.emailId || up0.email || '').toLowerCase();
                const portalEmail = (pu0.email || '').toLowerCase();
                const isDashboard = rpFlag === '1' ||
                    (rpFlag !== '0' && (!portalEmail || (dashEmail && dashEmail !== portalEmail)));
                if (isDashboard) {
                    sessionStorage.setItem('rp_from_dashboard', '1');
                    localStorage.setItem('risk_portal_user', JSON.stringify({
                        email: up0.emailId || up0.email || '',
                        first_name: up0.first_name || '',
                        last_name: up0.last_name || '',
                        organization_id: up0.organization_id || '',
                        // Preserve role set by dashboard before opening portal
                        ...(pu0.role ? { role: pu0.role } : {}),
                    }));
                }
            } else {
                sessionStorage.setItem('access_token', portalToken);
            }

            const up = JSON.parse(localStorage.getItem('user_profile') || '{}');

            // Resolve role from what was stored during portal login
            let gotRole = false;
            const portalUser = JSON.parse(localStorage.getItem('risk_portal_user') || '{}');
            if (portalUser?.role) {
                setRole(portalUser.role);
                setUserName(`${portalUser.first_name || ''} ${portalUser.last_name || ''}`.trim() || portalUser.email || 'User');
                gotRole = true;
            }

            if (!gotRole) {
                const fromDashboard = sessionStorage.getItem('rp_from_dashboard') === '1';
                setRole(fromDashboard ? 'risk_admin' : 'risk_manager');
                setUserName(up.name || up.email || up.emailId || 'User');
            }

            // Try portal workflow config
            try {
                const wfRes = await risk_portal_workflow_config();
                const wfData = extract(wfRes);
                if (wfData?.stages) { setWfConfig(wfData); }
                else { setWfConfig(DEFAULT_WORKFLOW); }
            } catch (e) {
                setWfConfig(DEFAULT_WORKFLOW);
            }

            // Portal backend is always deployed in this build
            setPortalBackendAvailable(true);

            setLoading(false);
        })();
    }, []);

    // Load data when view changes
    useEffect(() => {
        if (!role) return;
        const rc = getRoleConfig(wfConfig, role);
        if (view === 'dashboard' || view === 'pm_home' || view === 'executive_dashboard') { loadDashboard(); loadHeatmap(); if (role === 'ciso') loadAppetiteBreaches(); }
        if (view === 'heatmap') loadHeatmap();
        if (view === 'risks' || view === 'my_risks' || view === 'bu_risks') { loadRisks(); if (businessUnits.length === 0) loadBusinessUnits(); if (!categories || categories.length === 0) loadCategories(); if (projects.length === 0) loadProjects(); }
        if (view === 'dashboard' || view === 'pm_home' || view === 'executive_dashboard') {
            loadProjectBreakdown(); loadPortfolioTrend();
            if (projects.length === 0) loadProjects();
            // Need BUs loaded too so the scope-filter dropdown has options
            if (businessUnits.length === 0) loadBusinessUnits();
        }
        if (view === 'kris') loadKRIs();
        if (view === 'categories') loadCategories();
        if (view === 'appetite') { loadAppetite(); loadAppetiteBreaches(); }
        if (view === 'users') loadUsers();
        if (view === 'audit_log') loadRoleAuditLog();
        if (view === 'business_units') { loadBusinessUnits(); loadBuBreakdown(); }
        if (view === 'projects') { loadProjects(); loadProjectBreakdown(); if (businessUnits.length === 0) loadBusinessUnits(); }
        if (view === 'settings' || view === 'workflow' || view === 'pm_home') { loadOrgSettings(); }
        if (view === 'dashboard' || view === 'pm_home' || view === 'executive_dashboard') { loadBuBreakdown(); }
        if (view === 'treatments' || view === 'my_treatments') loadOverdueTreatments();
        if (view === 'pending_reviews') loadPendingTreatments();
        if (view === 'scenarios') loadScenarios();
        if (view === 'review_schedules') loadReviewSchedules();
        if (view === 'compliance') loadComplianceCoverage();
    }, [view, role, riskPage, heatmapType, viewAllOrgs, buFilter, projectFilter, dashScope]);

    // ──────── HELPERS ────────
    // Extract data from portal API responses (AIGApi / SbomApiIntercepter)
    // Extract data from portal API responses (handles both AIGApi/SbomApiIntercepter and direct axios)
    // SbomApiIntercepter wraps: { statusCode: { response_data: { data: ... } } }
    // Direct axios: { data: { response_data: { data: ... } } }
    const extract = (res) => res?.data?.response_data?.data || res?.data?.data || res?.statusCode?.response_data?.data || res?.statusCode?.data || null;

    // Extract risk list from dashboard API responses (ApiIntercepter)
    // Dashboard response shape: res.statusCode.results.response_data.data (array)
    const extractDashboardList = (res) => {
        // Path 1: paginated list -> res.statusCode.results.response_data.data
        const d1 = res?.statusCode?.results?.response_data?.data;
        if (Array.isArray(d1)) return d1;
        // Path 2: direct response_data.data
        const d2 = res?.statusCode?.response_data?.data;
        if (Array.isArray(d2)) return d2;
        // Path 3: response_data itself is array
        const d3 = res?.statusCode?.response_data;
        if (Array.isArray(d3)) return d3;
        // Path 4: portal API shape
        const d4 = res?.data?.response_data?.data;
        if (Array.isArray(d4)) return d4;
        if (d4?.results && Array.isArray(d4.results)) return d4.results;
        return [];
    };

    // Extract single risk from dashboard API: res.statusCode.response_data.data
    const extractDashboardItem = (res) => {
        return res?.statusCode?.response_data?.data || res?.data?.response_data?.data || res?.data?.data || null;
    };

    // Build dashboard stats from raw risk list
    const buildDashFromRisks = (allRisks) => {
        const by_state = {}, by_severity = {}, by_type_map = {};
        allRisks.forEach(r => {
            by_state[r.state] = (by_state[r.state] || 0) + 1;
            by_severity[r.severity] = (by_severity[r.severity] || 0) + 1;
            const t = r.risk_type || 'unknown';
            by_type_map[t] = (by_type_map[t] || 0) + 1;
        });
        const by_type = Object.entries(by_type_map).map(([risk_type, count]) => ({ risk_type, count })).sort((a, b) => b.count - a.count);

        // Map dashboard 1-4 scores to 5x5 inherent ratings
        const ratingOf = ratingOf4x4; // Dashboard data uses 4x4 scale
        const by_inherent_rating = {};
        allRisks.forEach(r => {
            const rating = ratingOf(r.risk_score || (r.likelihood * r.impact));
            by_inherent_rating[rating] = (by_inherent_rating[rating] || 0) + 1;
        });

        // Top 10 by score
        const sorted = [...allRisks].sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0));
        const top_risks = sorted.slice(0, 10).map(r => ({
            risk_id: r.risk_id, id: r.id, risk: (r.risk || '').substring(0, 80),
            inherent_score: r.risk_score || (r.likelihood * r.impact),
            inherent_rating: ratingOf(r.risk_score || (r.likelihood * r.impact)),
            residual_score: null, residual_rating: null,
            state: r.state, risk_owner: r.risk_owner || '',
        }));

        return {
            total_risks: allRisks.length, by_state, by_severity, by_type,
            by_inherent_rating, by_residual_rating: {},
            treatment_by_status: {}, overdue_actions: 0,
            top_risks, kri_breaches: 0,
        };
    };

    // Build heatmap from dashboard risks
    // type='inherent': uses dashboard likelihood × impact (the only scores available)
    // type='residual': ONLY shows risks that have portal RiskAssessment with explicit residual scores
    //                  Dashboard-only risks are NOT shown on residual (they haven't been assessed)
    const buildHeatmapFromRisks = (allRisks, type = 'inherent') => {
        const matrix = Array.from({ length: 5 }, () => Array(5).fill(0));
        const risks_by_cell = {};
        let assessedCount = 0;

        allRisks.forEach(r => {
            let li, im;

            if (type === 'residual') {
                // Only include risks that have explicit residual scores from portal assessment
                // Dashboard risks don't have residual - they show "Not Assessed" on residual tab
                if (!r.residual_likelihood && !r.residual_score) return;
                li = Math.min((r.residual_likelihood || r.likelihood || 1), 5);
                im = Math.min((r.residual_impact || r.impact || 1), 5);
            } else {
                li = Math.min((r.likelihood || 1), 5);
                im = Math.min((r.impact || 1), 5);
            }

            const idx_li = li - 1;
            const idx_im = im - 1;
            matrix[idx_li][idx_im] += 1;
            assessedCount++;
            const key = `${li},${im}`;
            if (!risks_by_cell[key]) risks_by_cell[key] = [];
            risks_by_cell[key].push({
                risk_id: r.risk_id, risk: (r.risk || '').substring(0, 80),
                inherent_score: r.risk_score || (r.likelihood * r.impact),
                residual_score: li * im,
                rating: type === 'residual' ? ratingOf5x5(li * im) : (r.severity || 'Low'),
            });
        });
        return { type, matrix, risks_by_cell, total_assessed: assessedCount };
    };

    // ──────── DATA LOADERS ────────

    // Master loader: fetch all risks from dashboard API (always available)
    const loadAllRisksFromDashboard = async () => {
        try {
            const res = await list_all_risk('?limit=100&offset=0&ordering=-created_at');
            const list = extractDashboardList(res);
            return list.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        } catch (e) {
            console.error('Dashboard risk fetch error', e);
            return [];
        }
    };

    // Load dashboard stats from the stats endpoint (accurate totals, no pagination limit)
    const loadDashboardStatsFromApi = async () => {
        try {
            const res = await total_risk_created({});
            const raw = res?.statusCode?.response_data?.data || res?.statusCode?.response_data;
            if (raw && raw.total !== undefined) {
                const ratingOf = ratingOf4x4; // Dashboard data uses 4x4 scale
                // Build by_inherent_rating from by_severity (dashboard uses 1-4 scale)
                const by_inherent_rating = {};
                if (raw.by_severity) {
                    Object.entries(raw.by_severity).forEach(([sev, count]) => {
                        const rating = sev === 'critical' ? 'Critical' : sev === 'high' ? 'High' : sev === 'medium' ? 'Medium' : 'Low';
                        by_inherent_rating[rating] = (by_inherent_rating[rating] || 0) + count;
                    });
                }
                // Build by_type from the risk list (need a separate call)
                const allRisks = await loadAllRisksFromDashboard();
                const by_type_map = {};
                allRisks.forEach(r => {
                    const t = r.risk_type || 'unknown';
                    by_type_map[t] = (by_type_map[t] || 0) + 1;
                });
                const by_type = Object.entries(by_type_map).map(([risk_type, count]) => ({ risk_type, count })).sort((a, b) => b.count - a.count);

                // Top 10 by score
                const sorted = [...allRisks].sort((a, b) => (b.risk_score || 0) - (a.risk_score || 0));
                const top_risks = sorted.slice(0, 10).map(r => ({
                    risk_id: r.risk_id, id: r.id, risk: (r.risk || '').substring(0, 80),
                    inherent_score: r.risk_score || (r.likelihood * r.impact),
                    inherent_rating: ratingOf(r.risk_score || (r.likelihood * r.impact)),
                    residual_score: null, residual_rating: null,
                    state: r.state, risk_owner: r.risk_owner || '',
                }));

                return {
                    total_risks: raw.total,
                    by_state: raw.by_state || {},
                    by_severity: raw.by_severity || {},
                    by_type,
                    by_inherent_rating,
                    by_residual_rating: {},
                    treatment_by_status: {},
                    overdue_actions: 0,
                    top_risks,
                    kri_breaches: 0,
                };
            }
        } catch (e) {
            console.error('Stats API error', e);
        }
        return null;
    };

    // Fetch top risks straight from the risks list endpoint (/risk-portal/risks/?page=1).
    // Used to fill the dashboard "Top Risks by Score" table when the dashboard
    // endpoint returns an empty top_risks array even though risks exist.
    const fetchTopRisksFromList = async (limit = 10) => {
        try {
            const params = { page: 1 };
            if (dashScope.bu_id) params.business_unit_id = dashScope.bu_id;
            if (dashScope.project_id) params.project_id = dashScope.project_id;
            const res = await risk_portal_risks(params);
            const d = extract(res);
            const list = (d && Array.isArray(d.results)) ? d.results : [];
            return [...list]
                .sort((a, b) => (b.inherent_score || 0) - (a.inherent_score || 0))
                .slice(0, limit)
                .map(r => ({
                    risk_id: r.risk_id, id: r.id, risk: (r.risk || '').substring(0, 80),
                    inherent_score: r.inherent_score,
                    inherent_rating: r.inherent_rating,
                    residual_score: r.residual_score ?? null,
                    residual_rating: r.residual_rating ?? null,
                    state: r.state, state_label: r.state_label,
                    risk_owner: r.risk_owner || '',
                }));
        } catch (e) {
            console.error('Top risks fetch error', e);
            return [];
        }
    };

    const loadDashboard = async () => {
        // Build scope params from the dashboard filter bar (skip when blank).
        const params = {};
        if (dashScope.bu_id) params.bu_id = dashScope.bu_id;
        if (dashScope.project_id) params.project_id = dashScope.project_id;
        // Try portal API first
        try {
            const res = await risk_portal_dashboard(Object.keys(params).length ? params : undefined);
            const d = extract(res);
            if (d && d.total_risks !== undefined) {
                // The dashboard endpoint sometimes returns an empty top_risks even when
                // risks exist — backfill it from the risks list endpoint so the
                // "Top Risks by Score" table is populated.
                if (!Array.isArray(d.top_risks) || d.top_risks.length === 0) {
                    d.top_risks = await fetchTopRisksFromList(3);
                } else {
                    d.top_risks = d.top_risks.slice(0, 3);
                }
                setDashData(d);
                return;
            }
        } catch (e) { /* fallback below */ }

        // Fallback: use dashboard stats endpoint (accurate totals)
        const statsData = await loadDashboardStatsFromApi();
        if (statsData) { setDashData(statsData); return; }

        // Last resort: build from list (may be capped at 100)
        const allRisks = await loadAllRisksFromDashboard();
        if (allRisks.length > 0) {
            setDashData(buildDashFromRisks(allRisks));
        }
    };

    const loadHeatmap = async () => {
        // Try portal API first
        try {
            const res = await risk_portal_heatmap({ type: heatmapType });
            const d = extract(res);
            if (d && d.matrix) { setHeatmapData(d); return; }
        } catch (e) { /* fallback below */ }

        // Fallback: build from dashboard stats matrix_data
        try {
            const res = await total_risk_created({});
            const raw = res?.statusCode?.response_data?.data || res?.statusCode?.response_data;
            if (raw?.matrix_data) {
                const matrix = Array.from({ length: 5 }, () => Array(5).fill(0));
                const risks_by_cell = {};
                (raw.matrix_data || []).forEach(cell => {
                    const li = (cell.likelihood || cell.x_axis?.value || 1) - 1;
                    const im = (cell.impact || cell.y_axis?.value || 1) - 1;
                    if (li >= 0 && li < 5 && im >= 0 && im < 5) {
                        matrix[li][im] = cell.count || 0;
                        const key = `${li + 1},${im + 1}`;
                        risks_by_cell[key] = (cell.risks || []).map(r => ({
                            risk_id: r.risk_id, risk: (r.risk || '').substring(0, 80),
                            inherent_score: (li + 1) * (im + 1),
                            residual_score: null, rating: r.severity || 'Low',
                        }));
                    }
                });
                if (heatmapType === 'inherent') {
                    setHeatmapData({ type: 'inherent', matrix, risks_by_cell, total_assessed: raw.total || 0 });
                    return;
                }
                // For residual, fall through to risk list method which can estimate control reduction
            }
        } catch (e) { /* last resort below */ }

        // Last resort: build from risk list
        const allRisks = await loadAllRisksFromDashboard();
        if (allRisks.length > 0) {
            setHeatmapData(buildHeatmapFromRisks(allRisks, heatmapType));
        }
    };

    const loadRisks = async () => {
        // Try portal API first
        try {
            const params = { ...riskFilters, page: riskPage };
            if (viewAllOrgs) params.all_orgs = 'true';
            if (view === 'my_risks') {
                const up = JSON.parse(localStorage.getItem('user_profile') || '{}');
                params.owner = up.email || '';
            }
            if (buFilter) params.business_unit_id = buFilter;
            if (projectFilter) params.project_id = projectFilter;
            const res = await risk_portal_risks(params);
            const d = extract(res);
            if (d && d.results) { setRisks(d); return; }
        } catch (e) { /* fallback below */ }

        // Fallback: load from dashboard API
        const allRisks = await loadAllRisksFromDashboard();
        let filtered = allRisks;

        // Apply filters
        if (riskFilters.state) filtered = filtered.filter(r => r.state === riskFilters.state);
        if (riskFilters.risk_type) filtered = filtered.filter(r => r.risk_type === riskFilters.risk_type);
        if (riskFilters.search) {
            const s = riskFilters.search.toLowerCase();
            filtered = filtered.filter(r => (r.risk || '').toLowerCase().includes(s) || (r.risk_id || '').toLowerCase().includes(s) || (r.threat || '').toLowerCase().includes(s));
        }
        if (view === 'my_risks') {
            const up = JSON.parse(localStorage.getItem('user_profile') || '{}');
            const email = (up.email || '').toLowerCase();
            if (email) filtered = filtered.filter(r => (r.risk_owner || '').toLowerCase() === email);
        }

        // Paginate
        const pageSize = 25;
        const start = (riskPage - 1) * pageSize;
        const paged = filtered.slice(start, start + pageSize);

        // Map to portal format
        const ratingOf = ratingOf4x4; // Dashboard data uses 4x4 scale
        const results = paged.map(r => ({
            id: r.id, risk_id: r.risk_id, risk: r.risk, threat: r.threat || '', vulnerability: r.vulnerability || '',
            risk_type: r.risk_type, state: r.state, severity: r.severity,
            risk_owner: r.risk_owner || '', treatment_option: r.treatment_option,
            target_date: r.target_date || null, created_at: r.created_at || null,
            inherent_score: r.risk_score || (r.likelihood * r.impact),
            inherent_rating: ratingOf(r.risk_score || (r.likelihood * r.impact)),
            residual_score: null, residual_rating: null, risk_velocity: null,
            assessed: false,
        }));

        setRisks({ results, count: filtered.length, page: riskPage, pages: Math.ceil(filtered.length / pageSize) || 1 });
    };

    const loadRiskDetail = async (idOrRiskId) => {
        // Resolve string risk_id (RSK-0318) to numeric id if needed
        let numericId = idOrRiskId;
        if (typeof idOrRiskId === 'string' && idOrRiskId.startsWith('RSK')) {
            // Look up in loaded risks list
            const found = (risks.results || []).find(r => r.risk_id === idOrRiskId);
            if (found?.id) {
                numericId = found.id;
            } else {
                // Fetch full list to find the ID
                const allRisks = await loadAllRisksFromDashboard();
                const match = allRisks.find(r => r.risk_id === idOrRiskId);
                if (match?.id) numericId = match.id;
            }
        }
        const id = numericId;

        // Try portal API first
        try {
            const res = await risk_portal_risk_detail(id);
            const d = extract(res);
            if (d && d.risk_id) { setRiskDetail(d); setSelectedRisk(d); setView('risk_detail'); loadWorkflowState(d.id || id); loadAckStatus(d.id || id); loadQuantification(d.id || id); loadBowtie(d.id || id); loadComplianceMappings(d.id || id); return; }
        } catch (e) { /* fallback below */ }

        // Fallback: load from dashboard API
        try {
            const res = await get_by_id_risk({ id });
            const raw = extractDashboardItem(res);
            if (raw) {
                const ratingOf = ratingOf4x4; // Dashboard data uses 4x4 scale
                const score = raw.risk_score || (raw.likelihood * raw.impact);
                const d = {
                    id: raw.id, risk_id: raw.risk_id, risk: raw.risk,
                    threat: raw.threat || '', vulnerability: raw.vulnerability || '',
                    risk_type: raw.risk_type, state: raw.state, severity: raw.severity,
                    risk_score: score, risk_owner: raw.risk_owner || '',
                    treatment_option: raw.treatment_option, treatment_plan: raw.treatment_plan || '',
                    target_date: raw.target_date || null, created_at: raw.created_at || null,
                    controls_applied: raw.controls_applied || '',
                    residual_risk: raw.residual_risk || '',
                    justification: raw.justification || '',
                    assessment: {
                        id: null, version: 0, source: 'dashboard',
                        inherent_likelihood: raw.likelihood || 1, inherent_impact: raw.impact || 1,
                        inherent_score: score, inherent_rating: ratingOf(score),
                        impact_dimensions: {}, control_effectiveness: 0,
                        residual_likelihood: null, residual_impact: null,
                        residual_score: null, residual_rating: null,
                        risk_velocity: null, assessed_at: raw.created_at, notes: 'Scores from dashboard (inherent only). Use "Assess Risk" to set residual scores.',
                    },
                    assessment_history: [],
                    treatments: raw.treatment_plan ? [{
                        id: null, strategy: raw.treatment_option || 'mitigate',
                        description: raw.treatment_plan, status: raw.state === 'closed' ? 'completed' : 'in_progress',
                        expected_residual_score: null, treatment_cost: null, actions: [], created_at: raw.created_at,
                    }] : [],
                    controls: raw.controls_applied ? [{ id: null, control_name: raw.controls_applied, control_type: 'preventive', effectiveness: 50, last_tested: null }] : [],
                    kris: [], comments: [], activity: [],
                };
                setRiskDetail(d);
                setSelectedRisk(d);
                setView('risk_detail');
                loadWorkflowState(d.id || id);
            }
        } catch (e) { console.error('Risk detail fallback error', e); }
    };

    const loadKRIs = async () => {
        try {
            const res = await risk_portal_kri_list();
            const d = extract(res);
            if (d) setKriList(d);
        } catch (e) { console.error('KRI load error', e); }
    };

    const loadCategories = async () => {
        try {
            const res = await risk_portal_categories();
            const d = extract(res);
            if (d) setCategories(d);
        } catch (e) { console.error('Categories load error', e); }
    };

    const loadAppetite = async () => {
        try {
            const res = await risk_portal_appetite();
            const d = extract(res);
            if (d) setAppetites(d);
        } catch (e) { console.error('Appetite load error', e); }
    };

    const loadUsers = async () => {
        try {
            const res = await risk_portal_roles_list();
            // DEBUG: log the raw response so we can see what shape arrives
            // eslint-disable-next-line no-console
            console.log('[loadUsers] raw response:', res);
            const d = extract(res);
            // eslint-disable-next-line no-console
            console.log('[loadUsers] extracted data:', d);
            if (Array.isArray(d?.roles)) {
                setPortalUsers(d.roles);
                // eslint-disable-next-line no-console
                console.log(`[loadUsers] set ${d.roles.length} users`);
            } else if (Array.isArray(d?.users)) {
                // backward compat with legacy shape
                setPortalUsers(d.users);
            } else {
                setPortalUsers([]);
                // eslint-disable-next-line no-console
                console.warn('[loadUsers] no roles array found in response');
            }
        } catch (e) {
            console.error('[loadUsers] error', e?.response || e);
            setPortalUsers([]);
        }
    };

    const loadRoleAuditLog = async () => {
        try {
            const res = await risk_portal_roles_audit_log();
            const d = extract(res);
            if (Array.isArray(d?.entries)) setRoleAuditLog(d.entries);
        } catch (e) { console.error('Audit log load error', e); }
    };

    const loadBusinessUnits = async () => {
        try {
            const res = await risk_portal_bus_list({ include_inactive: 'true' });
            const d = extract(res);
            if (Array.isArray(d?.business_units)) setBusinessUnits(d.business_units);
        } catch (e) { console.error('BU list error', e); }
    };

    const loadBuBreakdown = async () => {
        try {
            const res = await risk_portal_bu_breakdown({ rollup: 'true' });
            const d = extract(res);
            if (d) setBuBreakdown(d);
        } catch (e) { console.error('BU breakdown error', e); }
    };

    const loadProjects = async () => {
        try {
            const res = await risk_portal_projects_list();
            const d = extract(res);
            if (Array.isArray(d?.projects)) setProjects(d.projects);
        } catch (e) { console.error('Projects load error', e); }
    };
    const loadProjectBreakdown = async () => {
        try {
            const res = await risk_portal_project_breakdown();
            const d = extract(res);
            if (d) setProjectBreakdown(d);
        } catch (e) { console.error('Project breakdown error', e); }
    };
    const loadPortfolioTrend = async () => {
        try {
            const res = await risk_portal_portfolio_trend({ months: 6 });
            const d = extract(res);
            if (d) setPortfolioTrend(d);
        } catch (e) { console.error('Trend load error', e); }
    };

    const loadOrgSettings = async () => {
        try {
            const res = await risk_portal_get_settings();
            const d = extract(res);
            if (d) setOrgSettings(d);
        } catch (e) { console.error('Settings load error', e); }
    };

    const saveOrgSettings = async (partial) => {
        try {
            const res = await risk_portal_update_settings(partial);
            const body = res?.statusCode || res?.data || {};
            if (body?.status === 0) {
                flash(body?.error?.message || 'Failed to save settings', 'error');
                return;
            }
            flash('Settings saved', 'success');
            loadOrgSettings();
        } catch (e) {
            flash(e?.response?.data?.error?.message || 'Failed to save settings', 'error');
        }
    };

    // ──────── PHASE 2 LOADERS ────────
    const loadOverdueTreatments = async () => {
        try {
            const res = await risk_portal_overdue_treatments();
            const d = extract(res);
            if (Array.isArray(d)) setOverdueTreatments(d);
        } catch (e) { /* not available yet */ }
    };

    const loadFrameworkControls = async () => {
        try {
            const params = {};
            if (controlSearch) params.search = controlSearch;
            if (controlFrameworkId) params.framework_id = controlFrameworkId;
            const res = await risk_portal_framework_controls(params);
            const d = extract(res);
            if (Array.isArray(d)) setFrameworkControls(d);
        } catch (e) { /* not available yet */ }
    };

    const loadFrameworks = async () => {
        try {
            const res = await risk_portal_framework_list();
            const d = extract(res);
            if (Array.isArray(d)) setFrameworks(d);
        } catch (e) { /* not available yet */ }
    };

    // Portal-only operation wrapper - shows message if backend not deployed
    const [portalError, setPortalError] = useState(null);

    // Extract the deepest meaningful message from any of the error shapes our
    // backend produces (DRF default, custom response_generator, axios fallback).
    const extractErrorMessage = (e) => {
        const r = e?.response?.data;
        if (!r) return e?.message || 'Network error - could not reach the server';
        // Walk common error shapes deepest-first
        const candidates = [
            r?.error?.message,                  // {error: {message}}
            r?.response_data?.message,          // {response_data: {message}}
            r?.error_msg,                       // {error_msg}
            r?.detail,                          // DRF default
            r?.message,                         // generic
        ];
        return candidates.find(m => typeof m === 'string' && m.trim()) || e?.message || 'Unknown error';
    };

    // Heuristic - given an error message, infer (a) a friendly headline,
    // (b) variant ('warning' for fixable, 'error' for hard failures, 'info' for
    // permission), and (c) a CTA action the user can take to fix it.
    const classifyError = (status, msg) => {
        const m = (msg || '').toLowerCase();
        // Backend not reachable
        if (!status || status === 502 || status === 503 || status === 504) {
            return {
                headline: 'Connection problem', variant: 'error',
                hint: "We couldn't reach the server. Please check your connection and try again.", action: null
            };
        }
        if (status === 401 || status === 403 || /permission|cannot.*access|not authorized|sod|segregation/i.test(msg)) {
            return { headline: 'Permission denied', variant: 'info', hint: msg, action: null };
        }
        // Stage-advance hints with concrete next steps
        if (/treatment plan is required|move to .*review/i.test(msg)) {
            return {
                headline: 'Action needed', variant: 'warning', hint: msg,
                action: { label: 'Add Treatment Plan', onClick: () => setShowTreatmentModal(true) }
            };
        }
        if (/risk must be assessed|move to .*treatment/i.test(msg)) {
            return {
                headline: 'Action needed', variant: 'warning', hint: msg,
                action: { label: 'Run Assessment', onClick: () => setShowAssessModal(true) }
            };
        }
        if (/treatment plan.*not yet verified|move to .*approval|move to .*acceptance/i.test(msg)) {
            return {
                headline: 'Action needed', variant: 'warning', hint: msg,
                action: { label: 'Open Treatment Plans', onClick: () => setView('treatments') }
            };
        }
        if (/already in use|duplicate|unique/i.test(msg)) {
            return { headline: 'Already exists', variant: 'warning', hint: msg, action: null };
        }
        if (/required|mandatory|must be|invalid/i.test(msg) && status === 400) {
            return { headline: 'Check your input', variant: 'warning', hint: msg, action: null };
        }
        if (status === 404) {
            return { headline: 'Not found', variant: 'info', hint: msg || 'The item you requested no longer exists.', action: null };
        }
        if (status >= 500) {
            return {
                headline: 'Server error', variant: 'error',
                hint: 'Something went wrong on our side. The team has been notified - please try again in a moment.',
                action: null
            };
        }
        return { headline: 'Could not complete the action', variant: 'warning', hint: msg, action: null };
    };

    const portalOp = async (operation, fallbackMsg) => {
        try {
            return await operation();
        } catch (e) {
            const status = e?.response?.status;
            const isBackendDown = !e?.response || status === 502 || status === 503 || status === 504;
            const message = isBackendDown
                ? (fallbackMsg ? `${fallbackMsg} (server unreachable)` : "We couldn't reach the server.")
                : extractErrorMessage(e);
            const meta = classifyError(status, message);
            setPortalError({
                status: status || 'offline',
                headline: meta.headline,
                hint: meta.hint || message,
                variant: meta.variant,
                action: meta.action,
                url: e?.config?.url || '',
                data: JSON.stringify(e?.response?.data || {}, null, 2),
            });
            return null;
        }
    };

    const loadAckStatus = async (riskId) => {
        try {
            const res = await risk_portal_get_ack(riskId);
            const d = extract(res);
            if (d) setAckStatus(d);
        } catch (e) { setAckStatus(null); }
    };

    const handleRequestAck = async () => {
        if (!riskDetail) return;
        await portalOp(async () => {
            await risk_portal_request_ack(riskDetail.id);
            flash(`Acknowledgement request sent to ${riskDetail.risk_owner}`);
            loadAckStatus(riskDetail.id);
        }, 'Acknowledgement requires portal backend. Share the risk manually using the Share button.');
    };

    const loadWorkflowState = async (riskId) => {
        try {
            const res = await risk_portal_workflow_state(riskId);
            const d = extract(res);
            if (d) setWorkflowState(d);
        } catch (e) {
            // Fallback: derive from risk state
            setWorkflowState(null);
        }
    };

    const handleAdvanceStage = async (nextStage) => {
        if (!riskDetail) return;
        await portalOp(async () => {
            await risk_portal_advance_stage(riskDetail.id, { next_stage: nextStage, acting_role: role });
            flash(`Stage advanced to ${nextStage}`);
            loadRiskDetail(riskDetail.id);
            loadWorkflowState(riskDetail.id);
        }, 'Workflow advancement requires portal backend deployment.');
    };

    const handleAcceptRisk = async () => {
        if (!riskDetail) return;
        const justification = prompt('Justification for accepting residual risk:');
        if (justification === null) return;
        await portalOp(async () => {
            const res = await risk_portal_accept_risk(riskDetail.id, { justification });
            const d = extract(res);
            if (d?.escalated) {
                flash(`Risk exceeds appetite (score ${d.residual_score} > threshold ${d.max_residual}). Escalated to CISO.`);
            } else {
                flash('Risk accepted, moved to monitoring');
            }
            loadRiskDetail(riskDetail.id);
            loadWorkflowState(riskDetail.id);
        }, 'Risk acceptance requires portal backend deployment.');
    };

    const handleCloseRisk = async (force = false) => {
        if (!riskDetail) return;
        await portalOp(async () => {
            const res = await risk_portal_close_risk(riskDetail.id, { force });
            const data = extract(res);
            if (data?.blockers?.length > 0 && !force) {
                setCloseBlockers(data);
            } else {
                flash('Risk closed');
                setCloseBlockers(null);
                loadRiskDetail(riskDetail.id);
                loadWorkflowState(riskDetail.id);
            }
        }, 'Risk closure requires portal backend deployment.');
    };

    const loadQuantification = async (riskId) => {
        try {
            const res = await risk_portal_get_quantification(riskId);
            const d = extract(res);
            setQuantification(d);
            if (d?.monte_carlo_results?.iterations) setMonteCarloResults(d.monte_carlo_results);
        } catch (e) { setQuantification(null); }
    };

    const loadBowtie = async (riskId) => {
        try {
            const res = await risk_portal_get_bowtie(riskId);
            setBowtieData(extract(res));
        } catch (e) { setBowtieData(null); }
    };

    const loadReviewSchedules = async () => {
        try {
            const res = await risk_portal_list_review_schedules();
            const d = extract(res);
            if (Array.isArray(d)) setReviewSchedules(d);
        } catch (e) { /* not available */ }
    };

    const loadComplianceCoverage = async () => {
        try {
            const res = await risk_portal_compliance_coverage();
            const d = extract(res);
            if (Array.isArray(d)) setComplianceCoverage(d);
        } catch (e) { /* not available */ }
    };

    const loadComplianceMappings = async (riskId) => {
        try {
            const res = await risk_portal_compliance_mappings(riskId);
            const d = extract(res);
            if (Array.isArray(d)) setComplianceMappings(d);
        } catch (e) { setComplianceMappings([]); }
    };

    const handleMapCompliance = async (riskId, data) => {
        await portalOp(async () => {
            await risk_portal_map_compliance(riskId, data);
            flash('Compliance mapping added');
            loadComplianceMappings(riskId);
        }, 'Compliance mapping requires portal backend.');
    };

    const handleDeleteComplianceMapping = async (riskId, mapId) => {
        if (!window.confirm('Remove this compliance mapping?')) return;
        await portalOp(async () => {
            await risk_portal_delete_compliance_mapping(riskId, mapId);
            flash('Mapping removed');
            loadComplianceMappings(riskId);
        }, 'Compliance mapping requires portal backend.');
    };

    const loadBoardReport = async () => {
        try {
            const res = await risk_portal_board_report();
            const d = extract(res);
            if (d) setBoardReport(d);
        } catch (e) { /* not available */ }
    };

    const loadScenarios = async () => {
        try {
            const res = await risk_portal_list_scenarios();
            const d = extract(res);
            if (Array.isArray(d)) setScenarios(d);
        } catch (e) { /* not available */ }
    };

    const handleRunMonteCarlo = async () => {
        if (!riskDetail) return;
        try {
            setAiLoading('montecarlo');
            const res = await risk_portal_monte_carlo(riskDetail.id);
            const d = extract(res);
            if (d) { setMonteCarloResults(d); flash(`Simulation complete: ${d.iterations} iterations`); }
        } catch (e) { flash('Simulation failed. Set best/most likely/worst case losses first.', 'error'); }
        setAiLoading(null);
    };

    // Generic AI runner - keeps loading/error/result state in sync inline so the
    // user always sees feedback even if the API throws or returns empty.
    const _runAi = async (kind, callable, label) => {
        if (!callable) return;
        setAiLoading(kind);
        setAiError(null);
        setAiResult(null);
        try {
            const res = await callable();
            const d = extract(res);
            // Some interceptor responses surface server errors as {statusCode: {status: 0, error: {...}}}
            const innerErr = res?.statusCode?.error?.message || res?.errors?.message;
            if (innerErr) {
                setAiError(innerErr);
            } else if (d) {
                setAiResult({ type: kind, ...d });
            } else {
                setAiError(`${label} returned an empty response. The AI service may be temporarily unavailable - please try again in a moment.`);
            }
        } catch (e) {
            const msg = e?.response?.data?.error?.message
                || e?.response?.data?.error_msg
                || e?.message
                || 'Service unavailable';
            setAiError(`${label} failed: ${msg}`);
        }
        setAiLoading(null);
    };

    const handleAiCategorize = (desc) =>
        _runAi('categorize', () => risk_portal_ai_categorize({ description: desc }), 'Auto-Categorize');
    const handleAiDuplicates = (desc) =>
        _runAi('duplicates', () => risk_portal_ai_duplicates({ description: desc }), 'Detect Duplicates');
    const handleAiStatement = (desc) =>
        _runAi('statement', () => risk_portal_ai_risk_statement({ description: desc }), 'Generate ISO Statement');
    const handleAiTreatment = (desc, riskType, severity) =>
        _runAi('treatment', () => risk_portal_ai_suggest_treatment({ description: desc, risk_type: riskType, severity }), 'Suggest Treatment');

    const loadPendingTreatments = async () => {
        try {
            const res = await risk_portal_pending_treatments();
            const d = extract(res);
            if (Array.isArray(d)) setPendingTreatments(d);
        } catch (e) { /* not available yet */ }
    };

    const loadAppetiteBreaches = async () => {
        try {
            const res = await risk_portal_appetite_breaches();
            const d = extract(res);
            if (d) setAppetiteBreaches(d);
        } catch (e) { /* not available yet */ }
    };

    // ──────── ACTIONS ────────
    const handleApproveTreatment = async (tpId) => {
        try {
            const res = await risk_portal_approve_treatment(tpId);
            const body = res?.data || res?.statusCode || {};
            if (body?.status === 0) {
                flash(body?.error?.message || 'Approve failed', 'error');
                return;
            }
            flash(body?.response_data?.message || 'Treatment approved (owner notified)', 'success');
            if (riskDetail) loadRiskDetail(riskDetail.id);
        } catch (e) {
            const msg = e?.response?.data?.error?.message || e?.response?.data?.response_data?.message || 'Approve failed';
            flash(msg, 'error');
        }
    };

    const handleRejectTreatment = async (tpId) => {
        const reason = prompt('Reason for sending the plan back for revision:');
        if (reason === null) return;
        if (!reason.trim()) { flash('A reason is required to reject', 'error'); return; }
        try {
            const res = await risk_portal_reject_treatment(tpId, { reason });
            const body = res?.data || res?.statusCode || {};
            if (body?.status === 0) {
                flash(body?.error?.message || 'Reject failed', 'error');
                return;
            }
            flash(body?.response_data?.message || 'Treatment rejected (owner notified)', 'success');
            if (riskDetail) loadRiskDetail(riskDetail.id);
        } catch (e) {
            const msg = e?.response?.data?.error?.message || e?.response?.data?.response_data?.message || 'Reject failed';
            flash(msg, 'error');
        }
    };

    const handleUpdateKri = async () => {
        if (!editingKri) return;
        try {
            await risk_portal_update_kri(editingKri.id, kriForm);
            setShowKriEditModal(false);
            flash('KRI updated');
            loadKRIs();
        } catch (e) { flash('Update failed'); }
    };

    const handleCreateKriForRisk = async () => {
        if (!riskDetail) return;
        try {
            await risk_portal_create_kri(riskDetail.id, kriForm);
            setShowKriCreateModal(false);
            flash('KRI created');
            loadRiskDetail(riskDetail.id);
            loadKRIs();
        } catch (e) { flash('Create failed'); }
    };

    const handleDeleteKri = async (kriId) => {
        if (!window.confirm('Delete this KRI?')) return;
        try {
            await risk_portal_delete_kri(kriId);
            flash('KRI deleted');
            loadKRIs();
        } catch (e) { flash('Delete failed'); }
    };

    const handleDeleteTreatment = async (tpId) => {
        if (!window.confirm('Delete this treatment plan?')) return;
        try {
            await risk_portal_delete_treatment(tpId);
            flash('Treatment deleted');
            if (riskDetail) loadRiskDetail(riskDetail.id);
        } catch (e) { flash('Delete failed'); }
    };

    const handleDeleteControl = async (ctrlId) => {
        if (!window.confirm('Remove this control mapping?')) return;
        try {
            await risk_portal_delete_control(ctrlId);
            flash('Control removed');
            if (riskDetail) loadRiskDetail(riskDetail.id);
        } catch (e) { flash('Delete failed'); }
    };

    const handleDeleteComment = async (comId) => {
        if (!window.confirm('Delete this comment?')) return;
        try {
            await risk_portal_delete_comment(comId);
            flash('Comment deleted');
            if (riskDetail) loadRiskDetail(riskDetail.id);
        } catch (e) { flash('Delete failed'); }
    };

    const handleDeleteEvidence = async (evId) => {
        if (!window.confirm('Delete this evidence?')) return;
        try {
            await risk_portal_delete_evidence(evId);
            flash('Evidence deleted');
            if (riskDetail) loadRiskDetail(riskDetail.id);
        } catch (e) { flash('Delete failed'); }
    };

    const handleDeleteCategory = async (catId) => {
        if (!window.confirm('Remove this category?')) return;
        try {
            await risk_portal_delete_category(catId);
            flash('Category removed');
            loadCategories();
        } catch (e) { flash('Delete failed'); }
    };

    // ── Treatment Update ──
    const handleUpdateTreatment = async (tpId, updates) => {
        await portalOp(async () => {
            await risk_portal_update_treatment(tpId, updates);
            flash('Treatment plan updated');
            if (riskDetail) loadRiskDetail(riskDetail.id);
        }, 'Treatment update requires portal backend.');
    };

    // ── Control Update ──
    const handleUpdateControl = async (ctrlId, updates) => {
        await portalOp(async () => {
            await risk_portal_update_control(ctrlId, updates);
            flash('Control updated');
            if (riskDetail) loadRiskDetail(riskDetail.id);
        }, 'Control update requires portal backend.');
    };

    // ── Appetite Save ──
    const handleSaveAppetite = async (appetiteData) => {
        await portalOp(async () => {
            await risk_portal_save_appetite(appetiteData);
            flash('Appetite threshold saved');
            loadAppetite();
            loadAppetiteBreaches();
        }, 'Appetite save requires portal backend.');
    };

    // ── Appetite Delete ──
    const handleDeleteAppetite = async (appId) => {
        if (!window.confirm('Delete this appetite threshold?')) return;
        await portalOp(async () => {
            await risk_portal_delete_appetite(appId);
            flash('Appetite deleted');
            loadAppetite();
            loadAppetiteBreaches();
        }, 'Appetite delete requires portal backend.');
    };

    // ── Workflow Config Save with validation feedback ──
    const [workflowValidation, setWorkflowValidation] = useState(null); // {errors, warnings, payload}

    const reloadWorkflowConfig = async () => {
        try {
            const res = await risk_portal_workflow_config();
            const d = extract(res);
            if (d) setWfConfig(d);
        } catch (e) { /* ignore */ }
    };

    const handleSaveWorkflow = async (wfData, force = false) => {
        try {
            const payload = force ? { ...wfData, force: true } : wfData;
            const res = await risk_portal_update_workflow(payload);
            // Backend may return success with embedded warnings (when force=true)
            const data = extract(res) || {};
            if (Array.isArray(data.warnings) && data.warnings.length) {
                flash(`Workflow saved with ${data.warnings.length} warning(s)`, 'warning');
            } else {
                flash('Workflow configuration saved', 'success');
            }
            setWorkflowValidation(null);
            await reloadWorkflowConfig();
        } catch (e) {
            const status = e?.response?.status;
            const body = e?.response?.data || {};
            // Extract errors / warnings from response - supports both wrapped + flat shapes
            const dataNode = body?.response_data?.data || body?.data || body || {};
            const errors = dataNode.errors || [];
            const warnings = dataNode.warnings || [];
            const requiresConfirm = !!dataNode.requires_confirmation;
            if (status === 400 && (errors.length || warnings.length)) {
                setWorkflowValidation({ errors, warnings, requiresConfirm, payload: wfData });
            } else {
                // Unknown error - fall through to the generic notification modal
                portalOp(async () => { throw e; }, 'Workflow update requires portal backend.');
            }
        }
    };

    // ── Activity Log ──
    const loadActivityLog = async (riskId) => {
        try {
            const res = await risk_portal_activity(riskId);
            const d = extract(res);
            return d || [];
        } catch { return []; }
    };

    const handleCreateRisk = async () => {
        if (!riskForm.risk.trim()) { flash('Risk description is required', 'error'); return; }
        if (!riskForm.risk_owner.trim()) { flash('Risk owner email is required', 'error'); return; }
        try {
            setLoader(true);
            const res = await add_new_risk(riskForm);
            // Detect 200-with-status:0 error envelope (works for both ApiIntercepter and AIGApi shapes)
            const body = res?.data || res?.statusCode || {};
            if (body?.status === 0) {
                const errMsg = body?.error?.message || 'Failed to create risk';
                flash(errMsg, 'error');
                return;
            }
            const d = extract(res);
            setShowCreateRiskModal(false);
            setRiskForm({ risk: '', threat: '', vulnerability: '', likelihood: 1, impact: 1, state: 'open', risk_type: '', risk_owner: '', target_date: '' });
            flash(d?.risk_id ? `Risk ${d.risk_id} created` : 'Risk created', 'success');
            setLoader(false);
            loadDashboard();
            loadRisks();
        } catch (e) {
            const msg = e?.response?.data?.response_data?.message
                || e?.response?.data?.error?.message
                || e?.response?.data?.error_msg
                || 'Failed to create risk';
            flash(msg, 'error');
            setLoader(false);
        }
    };

    // Open the Edit Risk modal: fetch the current record and pre-fill the form.
    const openEditRisk = async (idOrRiskId) => {
        // Resolve a RSK-#### risk_id string to its numeric id when needed.
        let numericId = idOrRiskId;
        if (typeof idOrRiskId === 'string' && idOrRiskId.startsWith('RSK')) {
            const found = (risks.results || []).find(r => r.risk_id === idOrRiskId)
                || (dashData?.top_risks || []).find(r => r.risk_id === idOrRiskId);
            if (found?.id) numericId = found.id;
        }
        try {
            setLoader(true);
            const res = await get_by_id_risk({ id: numericId });
            const raw = extractDashboardItem(res);
            setLoader(false);
            if (!raw) { flash('Could not load risk details', 'error'); return; }
            setEditRiskForm({
                id: raw.id,
                risk_id: raw.risk_id,
                risk: raw.risk || '',
                threat: raw.threat || '',
                vulnerability: raw.vulnerability || '',
                risk_type: raw.risk_type || '',
                risk_owner: raw.risk_owner || '',
                state: raw.state || 'open',
                likelihood: raw.likelihood || 1,
                impact: raw.impact || 1,
                target_date: raw.target_date ? String(raw.target_date).split('T')[0] : '',
                treatment_option: raw.treatment_option || '',
                treatment_plan: raw.treatment_plan || '',
            });
            if (!categories || categories.length === 0) loadCategories();
            setShowEditRiskModal(true);
        } catch (e) {
            setLoader(false);
            flash('Could not load risk details: ' + (e?.response?.data?.error?.message || e.message), 'error');
        }
    };

    // Submit the Edit Risk form via PUT /scanner/risk-registry/<id>/
    const handleUpdateRisk = async () => {
        const f = editRiskForm;
        if (!f) return;
        if (!f.risk.trim()) { flash('Risk description is required', 'error'); return; }
        if (!f.risk_owner.trim()) { flash('Risk owner email is required', 'error'); return; }
        try {
            setLoader(true);
            const params = {
                risk: f.risk,
                threat: f.threat,
                vulnerability: f.vulnerability,
                risk_type: f.risk_type,
                risk_owner: f.risk_owner,
                state: f.state,
                likelihood: +f.likelihood,
                impact: +f.impact,
                target_date: f.target_date || null,
                treatment_option: f.treatment_option || '',
                treatment_plan: f.treatment_plan || '',
            };
            const res = await update_risk_registry({ id: f.id, params });
            const body = res?.data || res?.statusCode || {};
            if (body?.status === 0) {
                flash(body?.error?.message || 'Failed to update risk', 'error');
                setLoader(false);
                return;
            }
            setShowEditRiskModal(false);
            setEditRiskForm(null);
            flash(`Risk ${f.risk_id || ''} updated`.trim(), 'success');
            setLoader(false);
            loadDashboard();
            loadRisks();
        } catch (e) {
            const msg = e?.response?.data?.response_data?.message
                || e?.response?.data?.error?.message
                || e?.response?.data?.error_msg
                || 'Failed to update risk';
            flash(msg, 'error');
            setLoader(false);
        }
    };

    const handleShareRisk = async () => {
        if (!riskDetail || !shareForm.recipient_email) { flash('Recipient email required'); return; }
        if (!shareForm.recipient_name.trim()) { flash('Recipient name is required'); return; }
        const res = await portalOp(async () => {
            return await risk_portal_share_risk(riskDetail.id, shareForm);
        }, 'Sharing requires portal backend deployment.');
        if (res) {
            const d = extract(res);
            if (d?.share_url) {
                setShareUrl(d.share_url);
                flash(`Risk shared with ${shareForm.recipient_email}. Email notification sent.`);
                loadRiskShares();
            } else { flash('Risk shared'); }
        }
    };

    const loadRiskShares = async () => {
        if (!riskDetail) return;
        try {
            const res = await risk_portal_list_shares(riskDetail.id);
            const d = extract(res);
            if (Array.isArray(d)) setRiskShares(d);
        } catch (e) { /* not available */ }
    };

    const handleRevokeShare = async (shareId) => {
        if (!window.confirm('Revoke this share link?')) return;
        try {
            await risk_portal_revoke_share(shareId);
            flash('Share link revoked');
            loadRiskShares();
        } catch (e) { flash('Revoke failed'); }
    };

    const handleMapControl = async (frameworkCtrl) => {
        if (!riskDetail) return;
        try {
            await risk_portal_add_control(riskDetail.id, {
                control_name: `${frameworkCtrl.code} - ${frameworkCtrl.name}`,
                control_description: frameworkCtrl.description,
                control_type: 'preventive',
                effectiveness: 50,
                framework_control_id: frameworkCtrl.id,
            });
            setShowControlMapModal(false);
            flash('Control mapped');
            loadRiskDetail(riskDetail.id);
        } catch (e) { flash('Mapping failed'); }
    };

    const handleAssess = async () => {
        if (!riskDetail) return;
        try {
            // Try portal API first
            await risk_portal_assess(riskDetail.id, assessForm);
            setShowAssessModal(false);
            flash('Risk assessed successfully');
            loadRiskDetail(riskDetail.id);
            loadHeatmap();
        } catch (e) {
            // Fallback: update via dashboard PATCH API (works when portal backend not deployed)
            try {
                const { edit_by_id_risk_registry } = await import('../services');
                await edit_by_id_risk_registry({
                    id: riskDetail.id,
                    params: {
                        likelihood: assessForm.inherent_likelihood,
                        impact: assessForm.inherent_impact,
                    }
                });
                setShowAssessModal(false);
                flash('Assessment saved (inherent scores updated via dashboard)');
                loadRiskDetail(riskDetail.id);
                loadHeatmap();
                loadDashboard();
            } catch (e2) {
                flash('Assessment failed: ' + (e2?.response?.data?.error_msg || e?.response?.data?.error_msg || e.message));
            }
        }
    };

    const handleCreateTreatment = async () => {
        if (!riskDetail) return;
        try {
            await risk_portal_create_treatment(riskDetail.id, treatmentForm);
            setShowTreatmentModal(false);
            flash('Treatment plan created');
            loadRiskDetail(riskDetail.id);
        } catch (e) {
            // Fallback: save treatment via dashboard PATCH
            try {
                const { edit_by_id_risk_registry } = await import('../services');
                await edit_by_id_risk_registry({
                    id: riskDetail.id,
                    params: {
                        treatment_option: treatmentForm.strategy || 'mitigate',
                        treatment_plan: treatmentForm.description || '',
                    }
                });
                setShowTreatmentModal(false);
                flash('Treatment saved via dashboard (basic fields only)');
                loadRiskDetail(riskDetail.id);
            } catch (e2) {
                flash('Failed: ' + (e?.response?.data?.error_msg || e.message));
            }
        }
    };

    const handleInvite = async () => {
        try {
            // Use the new RBAC role-create path (writes audit log + sends invite email)
            const res = await risk_portal_role_create({ ...inviteForm, status: 'invited' });
            const d = res?.statusCode?.response_data || res?.data?.response_data || res?.statusCode || res?.data || {};
            const sent = d?.data?.invite_email_sent;
            setShowInviteModal(false);
            flash(d?.message || (sent ? `Invite emailed to ${inviteForm.email}` : 'User invited'));
            loadUsers();
        } catch (e) {
            const msg = e?.response?.data?.error?.message || e?.response?.data?.response_data?.message || 'Invite failed';
            flash(msg);
        }
    };

    const handleCreateCategory = async () => {
        try {
            await risk_portal_create_category(categoryForm);
            setShowCategoryModal(false);
            flash('Category created');
            loadCategories();
        } catch (e) { flash('Failed'); }
    };

    const handleComment = async () => {
        if (!riskDetail || !commentText.trim()) return;
        try {
            await risk_portal_add_comment(riskDetail.id, { text: commentText });
            setCommentText('');
            flash('Comment added');
            loadRiskDetail(riskDetail.id);
        } catch (e) { flash('Comment failed'); }
    };

    // Centered notification toast - type can be passed explicitly or auto-detected from msg keywords.
    const flash = (msg, type) => {
        if (!msg) return;
        const text = String(msg);
        if (!type) {
            const lc = text.toLowerCase();
            if (/(failed|error|invalid|unable|cannot|denied|exceeded|exceeds|blocked|reject|escalated|expired)/.test(lc)) type = 'error';
            else if (/(warning|caution|insufficient|breach|overdue|pending|escalat)/.test(lc)) type = 'warning';
            else type = 'success';
        }
        setMessage({ text, type, id: Date.now() });
        // Errors stay longer so the user can read; success auto-clears faster
        const ttl = type === 'error' ? 6000 : 3500;
        setTimeout(() => setMessage((m) => (m && m.text === text ? null : m)), ttl);
    };

    // ──────── ROLE CONFIG ────────
    const rc = getRoleConfig(wfConfig, role);

    if (loading) return <div className="rp-loading"><div className="rp-spinner" /><p>Loading Risk Portal...</p></div>;

    // ═══════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════
    return (
        <div className="rp">
            {/* ── HEADER ── */}
            <div className="rp-header">
                <div className="rp-header-left">
                    <span className="rp-logo">NMT SECURITY</span>
                    <span className="rp-header-title">Risk Registry Portal</span>
                </div>
                <div className="rp-header-right">
                    <span className="rp-header-user">{userName}</span>
                    <span className="rp-role-badge">{{ risk_admin: 'Risk Admin', risk_manager: 'Risk Manager', risk_owner: 'Risk Owner', risk_reviewer: 'Risk Reviewer', business_owner: 'Business Owner', risk_committee: 'Risk Committee', ciso: 'CISO (Read-Only)' }[role] || role}</span>
                    <button className="rp-header-link" onClick={() => navigate('/dashboard')} title="Leave Risk Portal and return to NMT Security main app">← Main App</button>
                </div>
            </div>

            <div className="rp-body">
                {/* ── SIDEBAR ── */}
                <div className="rp-sidebar">
                    <div className="rp-sidebar-label">Navigation</div>
                    <nav className="rp-nav">
                        {(rc.sidebar || []).map(item => (
                            <button key={item} className={`rp-nav-item ${view === item ? 'active' : ''}`}
                                onClick={() => { setView(item); setSelectedRisk(null); setDrillCell(null); }}>
                                {SIDEBAR_ICONS[item] || null}
                                {SIDEBAR_LABELS[item] || item}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* ── MAIN CONTENT ── */}
                <div className="rp-content">
                    {message && <CenteredToast notification={message} onClose={() => setMessage(null)} />}

                    {/* DASHBOARD */}
                    {(view === 'dashboard' || view === 'pm_home' || view === 'executive_dashboard') && renderDashboard()}

                    {/* HEAT MAP */}
                    {view === 'heatmap' && renderHeatmap()}

                    {/* RISK LIST */}
                    {(view === 'risks' || view === 'my_risks' || view === 'bu_risks') && renderRiskList()}

                    {/* RISK DETAIL */}
                    {view === 'risk_detail' && riskDetail && renderRiskDetail()}

                    {/* KRI */}
                    {view === 'kris' && renderKRIs()}

                    {/* TREATMENTS */}
                    {(view === 'treatments' || view === 'my_treatments') && renderTreatments()}

                    {/* CATEGORIES */}
                    {view === 'categories' && renderCategories()}

                    {/* APPETITE */}
                    {view === 'appetite' && renderAppetite()}

                    {/* USERS */}
                    {view === 'users' && renderUsers()}
                    {view === 'guide' && renderGuide()}
                    {view === 'audit_log' && renderAuditLog()}
                    {view === 'business_units' && renderBusinessUnits()}
                    {view === 'projects' && renderProjects()}

                    {/* PENDING REVIEWS */}
                    {view === 'pending_reviews' && renderPendingReviews()}
                    {view === 'pending_acceptance' && renderPendingAcceptance()}

                    {/* SETTINGS / WORKFLOW */}
                    {(view === 'settings' || view === 'workflow') && renderSettings()}

                    {/* REPORTS */}
                    {view === 'scenarios' && renderScenarios()}
                    {view === 'review_schedules' && renderReviewSchedules()}
                    {view === 'compliance' && renderComplianceCoverage()}
                    {view === 'nova_ai' && renderNovaAI()}
                    {view === 'reports' && renderReports()}
                </div>

                {/* ── WORKFLOW VALIDATION MODAL ── */}
                {workflowValidation && (
                    <div className="rp-modal-overlay" onClick={() => setWorkflowValidation(null)}>
                        <div className="rp-modal rp-modal-lg" onClick={e => e.stopPropagation()}>
                            <div className="rp-modal-header">
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>
                                    {workflowValidation.errors.length
                                        ? 'Workflow has errors - please fix before saving'
                                        : `${workflowValidation.warnings.length} warning(s) - review before saving`}
                                </h3>
                                <button className="rp-modal-close" onClick={() => setWorkflowValidation(null)}>×</button>
                            </div>
                            <div className="rp-modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                                {workflowValidation.errors.length > 0 && (
                                    <div style={{ marginBottom: 16 }}>
                                        <div style={{ fontWeight: 600, color: 'var(--rp-danger)', marginBottom: 8, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                            Errors ({workflowValidation.errors.length}) - must fix
                                        </div>
                                        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--rp-text)' }}>
                                            {workflowValidation.errors.map((e, i) => (
                                                <li key={i} style={{ marginBottom: 8, lineHeight: 1.5 }}>{e}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                                {workflowValidation.warnings.length > 0 && (
                                    <div>
                                        <div style={{ fontWeight: 600, color: 'var(--rp-warning)', marginBottom: 8, fontSize: 13, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                                            Warnings ({workflowValidation.warnings.length}) - recommended to fix
                                        </div>
                                        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--rp-text-2)' }}>
                                            {workflowValidation.warnings.map((w, i) => (
                                                <li key={i} style={{ marginBottom: 8, lineHeight: 1.5 }}>{w}</li>
                                            ))}
                                        </ul>
                                        {workflowValidation.requiresConfirm && (
                                            <p style={{ marginTop: 12, fontSize: 12, color: 'var(--rp-text-3)', fontStyle: 'italic' }}>
                                                None of these block saving, but each represents a deviation from common
                                                governance patterns. Consider fixing them, or click "Save Anyway" to proceed.
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="rp-modal-footer">
                                <button className="rp-btn" onClick={() => setWorkflowValidation(null)}>
                                    {workflowValidation.errors.length ? 'Close & Fix' : 'Close'}
                                </button>
                                {workflowValidation.errors.length === 0 && workflowValidation.requiresConfirm && (
                                    <button className="rp-btn rp-btn-primary" onClick={() => {
                                        const p = workflowValidation.payload;
                                        setWorkflowValidation(null);
                                        handleSaveWorkflow(p, true);
                                    }}>Save Anyway</button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* ── NOTIFICATION MODAL - clear, action-oriented ── */}
                {portalError && (() => {
                    const ICON = {
                        warning: {
                            color: '#ff8b00', bg: '#fffae6',
                            svg: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>
                        },
                        error: {
                            color: '#de350b', bg: '#ffebe6',
                            svg: <><circle cx="12" cy="12" r="10" /><line x1="15" y1="9" x2="9" y2="15" /><line x1="9" y1="9" x2="15" y2="15" /></>
                        },
                        info: {
                            color: '#0052cc', bg: '#deebff',
                            svg: <><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></>
                        },
                    }[portalError.variant] || { color: '#ff8b00', bg: '#fffae6', svg: <><circle cx="12" cy="12" r="10" /></> };
                    return (
                        <div className="rp-modal-overlay" onClick={() => setPortalError(null)}>
                            <div className="rp-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
                                <div className="rp-modal-body" style={{ paddingTop: 28, paddingBottom: 20 }}>
                                    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                                        <div style={{ flexShrink: 0, width: 36, height: 36, borderRadius: '50%', background: ICON.bg, color: ICON.color, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{ICON.svg}</svg>
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--rp-text)' }}>{portalError.headline}</h3>
                                            <p style={{ margin: '8px 0 0', fontSize: 14, color: 'var(--rp-text-2)', lineHeight: 1.5 }}>{portalError.hint}</p>
                                        </div>
                                    </div>
                                    <details style={{ marginTop: 16, fontSize: 11, color: 'var(--rp-text-3)' }}>
                                        <summary style={{ cursor: 'pointer', userSelect: 'none' }}>Technical details</summary>
                                        <div style={{ marginTop: 8, padding: 10, background: 'var(--rp-surface-2)', border: '1px solid var(--rp-border)', borderRadius: 'var(--rp-r)', fontFamily: 'var(--rp-font-mono)' }}>
                                            <div>Status: <b>{portalError.status}</b></div>
                                            {portalError.url && <div style={{ wordBreak: 'break-all' }}>Endpoint: {portalError.url}</div>}
                                            <pre style={{ margin: '6px 0 0', maxHeight: 150, overflow: 'auto', fontSize: 10 }}>{portalError.data}</pre>
                                        </div>
                                    </details>
                                </div>
                                <div className="rp-modal-footer">
                                    <button className="rp-btn" onClick={() => setPortalError(null)}>Dismiss</button>
                                    {portalError.action && (
                                        <button className="rp-btn rp-btn-primary" onClick={() => {
                                            const fn = portalError.action.onClick;
                                            setPortalError(null);
                                            if (fn) fn();
                                        }}>{portalError.action.label}</button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })()}

                {/* ── MODALS ── */}
                {showAssessModal && renderAssessModal()}
                {showTreatmentModal && renderTreatmentModal()}
                {showInviteModal && renderInviteModal()}
                {showCategoryModal && renderCategoryModal()}
                {showControlMapModal && renderControlMapModal()}
                {showKriCreateModal && renderKriCreateModal()}
                {showCreateRiskModal && renderCreateRiskModal()}
                {showEditRiskModal && renderEditRiskModal()}
                {showShareModal && renderShareModal()}
            </div>
        </div>
    );

    // ═══════════════════════════════════════════════
    // DASHBOARD VIEW
    // ═══════════════════════════════════════════════
    // Reusable scope filter for dashboards. Hidden for risk_owner (their dashboard
    // is already personal). Renders a compact horizontal bar above the KPIs:
    //    [Business Unit ▼]  [Project ▼]  [Clear]   Showing 14 of 22 risks
    function renderDashboardScopeBar() {
        if (role === 'risk_owner') return null;
        const total = dashData?.total_risks ?? 0;
        const active = dashScope.bu_id || dashScope.project_id;
        const buName = dashScope.bu_id
            ? (businessUnits.find(b => String(b.id) === String(dashScope.bu_id))?.name || `BU #${dashScope.bu_id}`)
            : '';
        const projName = dashScope.project_id
            ? (projects.find(p => String(p.id) === String(dashScope.project_id))?.name || `Project #${dashScope.project_id}`)
            : '';
        return (
            <div style={{
                display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
                padding: '10px 14px', marginBottom: 16,
                background: 'var(--rp-surface)', border: '1px solid var(--rp-border)',
                borderRadius: 'var(--rp-r-lg)',
            }}>
                <span style={{ fontSize: 11, color: 'var(--rp-text-3)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Scope
                </span>
                <select className="rp-select" value={dashScope.bu_id}
                    onChange={e => setDashScope(s => ({ ...s, bu_id: e.target.value }))}
                    style={{ minWidth: 200, width: 'auto' }}>
                    <option value="">All Business Units</option>
                    {businessUnits.filter(b => b.is_active !== false).map(b => (
                        <option key={b.id} value={b.id}>
                            {b.parent_id ? '↳ ' : ''}{b.name}{b.code ? ` (${b.code})` : ''}
                        </option>
                    ))}
                </select>
                <select className="rp-select" value={dashScope.project_id}
                    onChange={e => setDashScope(s => ({ ...s, project_id: e.target.value }))}
                    style={{ minWidth: 200, width: 'auto' }}>
                    <option value="">All Projects</option>
                    {projects.filter(p => p.is_active !== false).map(p => (
                        <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ''}</option>
                    ))}
                </select>
                {active && (
                    <>
                        <button className="rp-btn rp-btn-sm" onClick={() => setDashScope({ bu_id: '', project_id: '' })}>
                            Clear filters
                        </button>
                        <span style={{ fontSize: 12, color: 'var(--rp-text-2)', marginLeft: 'auto' }}>
                            Showing <b>{total}</b> risk{total === 1 ? '' : 's'} for
                            {buName && <> BU <b>{buName}</b></>}
                            {buName && projName && ' ·'}
                            {projName && <> Project <b>{projName}</b></>}
                        </span>
                    </>
                )}
            </div>
        );
    }

    function renderDashboard() {
        const d = dashData;
        const totalTreatments = Object.values(d.treatment_by_status || {}).reduce((a, b) => a + b, 0);

        // ── RISK MANAGER DASHBOARD ──
        if (role === 'risk_manager') return (
            <div className="rp-view">
                <div className="rp-section-header" style={{ marginBottom: 16 }}>
                    <h2 className="rp-view-title" style={{ marginBottom: 0 }}>Risk Manager Dashboard</h2>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button className="rp-btn" onClick={() => setView('risks')}>View Register</button>
                        <button className="rp-btn rp-btn-primary" onClick={() => setShowCreateRiskModal(true)}>+ New Risk</button>
                    </div>
                </div>
                {renderDashboardScopeBar()}
                <div className="rp-kpi-row">
                    <KpiCard label="Total Risks" value={d.total_risks} color={KPI_COLOR.total} />
                    <KpiCard label="Critical" value={d.by_inherent_rating?.Critical || 0} color={KPI_COLOR.critical} />
                    <KpiCard label="High" value={d.by_inherent_rating?.High || 0} color={KPI_COLOR.high} />
                    <KpiCard label="Medium" value={d.by_inherent_rating?.Medium || 0} color={KPI_COLOR.medium} />
                    <KpiCard label="Low" value={d.by_inherent_rating?.Low || 0} color={KPI_COLOR.low} />
                    <KpiCard label="Overdue Actions" value={d.overdue_actions || 0} color={KPI_COLOR.overdue} />
                </div>
                <div className="rp-charts-row">
                    <div className="rp-card">
                        <h3>RISKS BY STATE</h3>
                        {renderBarChart(d.by_state || {}, d.total_risks, '#1b2a4a')}
                    </div>
                    <div className="rp-card">
                        <h3>RISKS BY CATEGORY</h3>
                        {renderDonutList(d.by_type || [])}
                    </div>
                    <div className="rp-card">
                        <h3>TREATMENT PROGRESS</h3>
                        {renderBarChart(d.treatment_by_status || {}, totalTreatments, '#0f7b4f')}
                    </div>
                </div>
                <div className="rp-card rp-card-wide">
                    <h3>RISK HEAT MAP <button className="rp-link-btn" onClick={() => setView('heatmap')}>View Full Map -></button></h3>
                    {renderMiniHeatmap()}
                </div>
                {renderBuBreakdownCard()}
                {renderProjectBreakdownCard()}
                {renderPortfolioTrendCard()}
                {renderTopRisksTable(d)}
            </div>
        );

        // ── CISO / EXECUTIVE DASHBOARD ──
        if (role === 'ciso') {
            const totalAssessed = (d.by_inherent_rating?.Critical || 0) + (d.by_inherent_rating?.High || 0) + (d.by_inherent_rating?.Medium || 0) + (d.by_inherent_rating?.Low || 0);
            const critHigh = (d.by_inherent_rating?.Critical || 0) + (d.by_inherent_rating?.High || 0);
            const posture = totalAssessed > 0 ? Math.round(((totalAssessed - critHigh) / totalAssessed) * 100) : 100;
            return (
                <div className="rp-view">
                    <h2 className="rp-view-title">Executive Risk Dashboard</h2>
                    {renderDashboardScopeBar()}
                    <div className="rp-kpi-row">
                        <div className="rp-kpi-card">
                            <div className="rp-kpi-value" style={{ color: posture >= 70 ? '#19A07E' : posture >= 40 ? '#D9BF21' : '#D24646', fontSize: '32px' }}>{posture}%</div>
                            <div className="rp-kpi-label">Risk Posture Score</div>
                            <div className="rp-kpi-bar" style={{ background: posture >= 70 ? '#19A07E' : posture >= 40 ? '#D9BF21' : '#D24646' }} />
                        </div>
                        <KpiCard label="Total Risks" value={d.total_risks} color="#1b2a4a" />
                        <KpiCard label="Critical / High" value={critHigh} color="#D24646" />
                        <KpiCard label="KRI Breaches" value={d.kri_breaches || 0} color="#991b1b" />
                        <KpiCard label="Overdue Actions" value={d.overdue_actions || 0} color="#D24646" />
                    </div>
                    {/* Risk posture bar */}
                    <div className="rp-card">
                        <h3>RISK POSTURE</h3>
                        <div className="rp-posture-bar">
                            <div className="rp-posture-segment" style={{ flex: d.by_inherent_rating?.Critical || 0, background: ratingColor('critical') }} title={`Critical: ${d.by_inherent_rating?.Critical || 0}`} />
                            <div className="rp-posture-segment" style={{ flex: d.by_inherent_rating?.High || 0, background: ratingColor('high') }} title={`High: ${d.by_inherent_rating?.High || 0}`} />
                            <div className="rp-posture-segment" style={{ flex: d.by_inherent_rating?.Medium || 0, background: ratingColor('medium') }} title={`Medium: ${d.by_inherent_rating?.Medium || 0}`} />
                            <div className="rp-posture-segment" style={{ flex: d.by_inherent_rating?.Low || 0, background: ratingColor('low') }} title={`Low: ${d.by_inherent_rating?.Low || 0}`} />
                        </div>
                        <div className="rp-posture-legend">
                            <span><i style={{ background: ratingColor('critical') }} />Critical ({d.by_inherent_rating?.Critical || 0})</span>
                            <span><i style={{ background: ratingColor('high') }} />High ({d.by_inherent_rating?.High || 0})</span>
                            <span><i style={{ background: ratingColor('medium') }} />Medium ({d.by_inherent_rating?.Medium || 0})</span>
                            <span><i style={{ background: ratingColor('low') }} />Low ({d.by_inherent_rating?.Low || 0})</span>
                        </div>
                    </div>
                    <div className="rp-charts-row">
                        <div className="rp-card">
                            <h3>RISK HEAT MAP <button className="rp-link-btn" onClick={() => setView('heatmap')}>Full Map -></button></h3>
                            {renderMiniHeatmap()}
                        </div>
                        <div className="rp-card">
                            <h3>RISKS BY CATEGORY</h3>
                            {renderDonutList(d.by_type || [])}
                        </div>
                    </div>

                    {/* Escalation Panel */}
                    {appetiteBreaches.total_breaches > 0 && (
                        <div className="rp-card">
                            <h3>RISKS REQUIRING CISO REVIEW <span className="rp-badge-danger">{appetiteBreaches.total_breaches}</span></h3>
                            <table className="rp-table">
                                <thead><tr><th>Risk</th><th>Description</th><th>Type</th><th>Score</th><th>Threshold</th><th>Action</th></tr></thead>
                                <tbody>
                                    {(appetiteBreaches.breaches || []).map((b, i) => (
                                        <tr key={i}>
                                            <td><span className="rp-risk-id rp-clickable" onClick={() => loadRiskDetail(b.risk_id)}>{b.risk_id}</span></td>
                                            <td>{b.risk}</td>
                                            <td><span className="rp-type-badge">{b.type}</span></td>
                                            <td><span className="rp-score-badge" style={{ background: '#D24646' }}>{b.score}</span></td>
                                            <td>{b.threshold}</td>
                                            <td><button className="rp-btn-approve" onClick={() => loadRiskDetail(b.risk_id)}>Review</button></td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {renderPortfolioTrendCard()}
                    {renderBuBreakdownCard()}
                    {renderProjectBreakdownCard()}
                    {renderTopRisksTable(d)}
                </div>
            );
        }

        // ── RISK OWNER DASHBOARD ──
        // Uses the backend's `my_risks` list - every active risk where the caller
        // is the registered risk_owner (not capped, not pre-filtered by score).
        if (role === 'risk_owner') {
            const myRisks = d.my_risks || [];
            const myCount = d.my_risks_count ?? myRisks.length;
            return (
                <div className="rp-view">
                    <h2 className="rp-view-title">My Risk Tasks</h2>
                    <div className="rp-kpi-row">
                        <KpiCard label="My Risks" value={myCount} color={KPI_COLOR.high} />
                        <KpiCard label="Pending Actions" value={d.overdue_actions || 0} color={KPI_COLOR.overdue} />
                        <KpiCard label="Total Org Risks" value={d.total_risks} color={KPI_COLOR.total} />
                    </div>
                    <div className="rp-card rp-card-wide">
                        <h3>MY ASSIGNED RISKS {myCount > 0 && <span style={{ fontSize: 11, color: 'var(--rp-text-3)', fontWeight: 400 }}>({myCount})</span>}</h3>
                        {myRisks.length === 0 ? (
                            <EmptyState
                                title="No risks assigned to you"
                                hint="When a Risk Manager assigns you as the owner of a risk, it'll show up here for treatment planning."
                            />
                        ) : (
                            <table className="rp-table">
                                <thead><tr><th>Risk ID</th><th>Description</th><th>Score</th><th>Rating</th><th>State</th><th>Target</th></tr></thead>
                                <tbody>
                                    {myRisks.map(r => {
                                        const rating = normalizeRating(r.inherent_rating);
                                        return (
                                            <tr key={r.risk_id} className="rp-clickable" onClick={() => loadRiskDetail(r.id || r.risk_id)}>
                                                <td><span className="rp-risk-id">{r.risk_id}</span></td>
                                                <td>{r.risk}</td>
                                                <td><span className="rp-score-badge" style={{ background: RATING_COLORS[rating] || '#6366f1' }}>{r.inherent_score}</span></td>
                                                <td><span className="rp-rating" style={{ color: RATING_COLORS[rating] }}>{rating}</span></td>
                                                <td><span className="rp-state-badge">{r.state_label || (r.state || '').replace(/_/g, ' ')}</span></td>
                                                <td>{r.target_date ? fmtDate(r.target_date) : '-'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            );
        }

        // ── RISK REVIEWER DASHBOARD ──
        // Reviewer's primary metric is "treatment plans waiting for me", not
        // org-wide totals. Reviewer's actionable queue lives on Pending Reviews.
        if (role === 'risk_reviewer') return (
            <div className="rp-view">
                <div className="rp-section-header" style={{ marginBottom: 16 }}>
                    <h2 className="rp-view-title" style={{ marginBottom: 0 }}>Review Dashboard</h2>
                    <button className="rp-btn rp-btn-primary" onClick={() => setView('pending_reviews')}>Open Review Queue</button>
                </div>
                {renderDashboardScopeBar()}
                <div className="rp-kpi-row">
                    <KpiCard label="Pending Reviews" value={d.pending_reviews_count || 0} color={KPI_COLOR.pending} />
                    <KpiCard label="Critical / High" value={(d.by_inherent_rating?.Critical || 0) + (d.by_inherent_rating?.High || 0)} color={KPI_COLOR.critical} />
                    <KpiCard label="Overdue Actions" value={d.overdue_actions || 0} color={KPI_COLOR.overdue} />
                    <KpiCard label="Total Org Risks" value={d.total_risks} color={KPI_COLOR.total} />
                </div>
                <div className="rp-card rp-card-wide">
                    <h3>RISK HEAT MAP <button className="rp-link-btn" onClick={() => setView('heatmap')}>Full Map -></button></h3>
                    {renderMiniHeatmap()}
                </div>
                {renderTopRisksTable(d)}
            </div>
        );

        // ── RISK COMMITTEE DASHBOARD ──
        // High/critical risks awaiting acceptance (severity-tiered routing).
        // Pending count comes from the backend (driven by stage='acceptance' +
        // severity tier), not from a fragile filter over top_risks.
        if (role === 'risk_committee') {
            const critHigh = (d.by_inherent_rating?.Critical || 0) + (d.by_inherent_rating?.High || 0);
            const pendingAck = (d.top_risks || []).filter(r => (r.inherent_rating === 'Critical' || r.inherent_rating === 'High') && (r.state === 'open' || r.state === 'in_progress'));
            return (
                <div className="rp-view">
                    <h2 className="rp-view-title">Risk Committee: High & Critical Acceptance</h2>
                    {renderDashboardScopeBar()}
                    <div className="rp-kpi-row">
                        <KpiCard label="Pending Acceptance" value={d.pending_committee_count || 0} color={KPI_COLOR.pending} />
                        <KpiCard label="Critical / High" value={critHigh} color={KPI_COLOR.critical} />
                        <KpiCard label="Risks Exceeding Appetite" value={appetiteBreaches?.total_breaches || 0} color={KPI_COLOR.high} />
                        <KpiCard label="KRI Breaches" value={d.kri_breaches || 0} color={KPI_COLOR.overdue} />
                    </div>
                    <div className="rp-card rp-card-wide">
                        <h3>RISKS AWAITING COMMITTEE ACCEPTANCE <span className="rp-badge-danger">{pendingAck.length}</span></h3>
                        <table className="rp-table">
                            <thead><tr><th>Risk ID</th><th>Description</th><th>Type</th><th>Severity</th><th>Inherent Score</th><th>Owner</th></tr></thead>
                            <tbody>
                                {pendingAck.map(r => (
                                    <tr key={r.risk_id} className="rp-clickable" onClick={() => loadRiskDetail(r.id || r.risk_id)}>
                                        <td><span className="rp-risk-id">{r.risk_id}</span></td>
                                        <td>{r.risk}</td>
                                        <td><span className="rp-type-badge">{(r.risk_type || '').replace(/_/g, ' ')}</span></td>
                                        <td><span className="rp-rating" style={{ color: RATING_COLORS[r.inherent_rating] }}>{r.inherent_rating}</span></td>
                                        <td><span className="rp-score-badge" style={{ background: RATING_COLORS[r.inherent_rating] }}>{r.inherent_score}</span></td>
                                        <td>{r.risk_owner || '-'}</td>
                                    </tr>
                                ))}
                                {pendingAck.length === 0 && <tr><td colSpan={6} className="rp-empty-cell">No high/critical risks pending acceptance</td></tr>}
                            </tbody>
                        </table>
                    </div>
                    <div className="rp-card rp-card-wide">
                        <h3>RISK HEAT MAP <button className="rp-link-btn" onClick={() => setView('heatmap')}>Full Map -></button></h3>
                        {renderMiniHeatmap()}
                    </div>
                    {renderPortfolioTrendCard()}
                    {renderProjectBreakdownCard()}
                </div>
            );
        }

        // ── BUSINESS OWNER DASHBOARD ──
        // BO's job: accept LOW/MEDIUM residual risk. Primary metric = items
        // waiting for their sign-off (pending_bo_count from backend), not the
        // org-wide open total.
        if (role === 'business_owner') return (
            <div className="rp-view">
                <div className="rp-section-header" style={{ marginBottom: 16 }}>
                    <h2 className="rp-view-title" style={{ marginBottom: 0 }}>Business Unit Risk Posture</h2>
                    <button className="rp-btn rp-btn-primary" onClick={() => setView('pending_acceptance')}>Open Acceptance Queue</button>
                </div>
                {renderDashboardScopeBar()}
                <div className="rp-kpi-row">
                    <KpiCard label="Pending Acceptance" value={d.pending_bo_count || 0} color={KPI_COLOR.pending} />
                    <KpiCard label="Critical" value={d.by_inherent_rating?.Critical || 0} color={KPI_COLOR.critical} />
                    <KpiCard label="High" value={d.by_inherent_rating?.High || 0} color={KPI_COLOR.high} />
                    <KpiCard label="Total Risks" value={d.total_risks} color={KPI_COLOR.total} />
                </div>
                <div className="rp-charts-row">
                    <div className="rp-card">
                        <h3>RISK HEAT MAP <button className="rp-link-btn" onClick={() => setView('heatmap')}>Full Map -></button></h3>
                        {renderMiniHeatmap()}
                    </div>
                    <div className="rp-card">
                        <h3>RISKS BY STATE</h3>
                        {renderBarChart(d.by_state || {}, d.total_risks, KPI_COLOR.total)}
                    </div>
                </div>
                {renderTopRisksTable(d)}
            </div>
        );

        // ── RISK ADMIN DASHBOARD ──
        if (role === 'risk_admin') return (
            <div className="rp-view">
                <div className="rp-section-header" style={{ marginBottom: 16 }}>
                    <h2 className="rp-view-title" style={{ marginBottom: 0 }}>Risk Platform Administration</h2>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button className="rp-btn" onClick={() => setView('settings')}>Settings</button>
                        <button className="rp-btn rp-btn-primary" onClick={() => setShowCreateRiskModal(true)}>+ New Risk</button>
                    </div>
                </div>
                {renderDashboardScopeBar()}
                <div className="rp-kpi-row">
                    <KpiCard label="Total Risks" value={d.total_risks} color={KPI_COLOR.total} />
                    <KpiCard label="Critical" value={d.by_inherent_rating?.Critical || 0} color={KPI_COLOR.critical} />
                    <KpiCard label="High" value={d.by_inherent_rating?.High || 0} color={KPI_COLOR.high} />
                    <KpiCard label="KRI Breaches" value={d.kri_breaches || 0} color={KPI_COLOR.overdue} />
                    <KpiCard label="Overdue Actions" value={d.overdue_actions || 0} color={KPI_COLOR.overdue} />
                </div>
                <div className="rp-charts-row">
                    <div className="rp-card">
                        <h3>RISKS BY STATE</h3>
                        {renderBarChart(d.by_state || {}, d.total_risks, KPI_COLOR.total)}
                    </div>
                    <div className="rp-card">
                        <h3>RISKS BY CATEGORY</h3>
                        {renderDonutList(d.by_type || [])}
                    </div>
                    <div className="rp-card">
                        <h3>TREATMENT PROGRESS</h3>
                        {renderBarChart(d.treatment_by_status || {}, totalTreatments, KPI_COLOR.low)}
                    </div>
                </div>
                {renderPortfolioTrendCard()}
                {renderBuBreakdownCard()}
                {renderProjectBreakdownCard()}
                {renderTopRisksTable(d)}
            </div>
        );

        // ── FALLBACK (generic) ──
        return (
            <div className="rp-view">
                <h2 className="rp-view-title">{rc.dashboard_title || 'Dashboard'}</h2>
                <div className="rp-kpi-row">
                    <KpiCard label="Total Risks" value={d.total_risks} color="#6366f1" />
                    <KpiCard label="Critical" value={d.by_inherent_rating?.Critical || 0} color="#D24646" />
                    <KpiCard label="High" value={d.by_inherent_rating?.High || 0} color="#D24646" />
                    <KpiCard label="Overdue" value={d.overdue_actions || 0} color="#D24646" />
                </div>
                <div className="rp-card rp-card-wide">
                    <h3>RISK HEAT MAP <button className="rp-link-btn" onClick={() => setView('heatmap')}>Full Map -></button></h3>
                    {renderMiniHeatmap()}
                </div>
                {renderTopRisksTable(d)}
            </div>
        );
    }

    // ── Shared dashboard sub-components ──
    // Severity-aware bar chart: when the row key matches a severity (low/medium/
    // high/critical), each bar is filled with its semantic colour. Otherwise it
    // falls back to the categorical palette so every series stays distinguishable.
    function renderBarChart(data, total, color) {
        const entries = Object.entries(data);
        return (
            <div className="rp-bar-chart">
                {entries.map(([key, count], i) => {
                    const sev = SEVERITY_COLORS[(key || '').toLowerCase()];
                    const fill = sev || color || RP_CATEGORICAL[i % RP_CATEGORICAL.length];
                    return (
                        <div key={key} className="rp-bar-item">
                            <span className="rp-bar-label">{key.replace(/_/g, ' ')}</span>
                            <div className="rp-bar-track">
                                <div className="rp-bar-fill" style={{ width: `${Math.min(100, (count / Math.max(total, 1)) * 100)}%`, background: fill }} />
                            </div>
                            <span className="rp-bar-value">{count}</span>
                        </div>
                    );
                })}
                {entries.length === 0 && <EmptyState title="No data yet" hint="This chart populates once risks are created." />}
            </div>
        );
    }

    // SVG donut chart for categorical breakdowns (e.g. risks by type). Replaces
    // the legacy text-only list - gives the dashboard a proper at-a-glance read.
    function renderDonutList(byType) {
        const slices = (byType || []).filter(t => (t.count || 0) > 0).slice(0, 10);
        if (slices.length === 0) return <EmptyState title="No data yet" hint="Categorise risks by type and they'll appear here." />;
        const total = slices.reduce((acc, s) => acc + (s.count || 0), 0);
        // Build SVG arc segments
        const size = 140, r = 60, inner = 38, cx = size / 2, cy = size / 2;
        let acc = 0;
        const arcs = slices.map((s, i) => {
            const v = s.count || 0;
            const a0 = (acc / total) * 2 * Math.PI - Math.PI / 2;
            acc += v;
            const a1 = (acc / total) * 2 * Math.PI - Math.PI / 2;
            const large = (a1 - a0) > Math.PI ? 1 : 0;
            const x0 = cx + r * Math.cos(a0), y0 = cy + r * Math.sin(a0);
            const x1 = cx + r * Math.cos(a1), y1 = cy + r * Math.sin(a1);
            const xi0 = cx + inner * Math.cos(a0), yi0 = cy + inner * Math.sin(a0);
            const xi1 = cx + inner * Math.cos(a1), yi1 = cy + inner * Math.sin(a1);
            const d = `M ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} L ${xi1} ${yi1} A ${inner} ${inner} 0 ${large} 0 ${xi0} ${yi0} Z`;
            return { d, color: RP_CATEGORICAL[i % RP_CATEGORICAL.length], label: (s.risk_type || 'Unknown').replace(/_/g, ' '), count: v };
        });
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-label="Risks by type donut">
                    {arcs.map((a, i) => <path key={i} d={a.d} fill={a.color} stroke="#fff" strokeWidth="1.5" />)}
                    <text x={cx} y={cy - 4} textAnchor="middle" fontSize="20" fontWeight="600" fill="var(--rp-text)">{total}</text>
                    <text x={cx} y={cy + 12} textAnchor="middle" fontSize="10" fill="var(--rp-text-3)">RISKS</text>
                </svg>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {arcs.map((a, i) => {
                        const pct = Math.round((a.count / total) * 100);
                        return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                                <span style={{ width: 10, height: 10, background: a.color, borderRadius: 2, flexShrink: 0 }} />
                                <span style={{ flex: 1, color: 'var(--rp-text)', textTransform: 'capitalize' }}>{a.label}</span>
                                <span style={{ color: 'var(--rp-text-3)', fontVariantNumeric: 'tabular-nums', minWidth: 30, textAlign: 'right' }}>{a.count}</span>
                                <span style={{ color: 'var(--rp-text-3)', fontVariantNumeric: 'tabular-nums', minWidth: 36, textAlign: 'right' }}>{pct}%</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    function renderProjectBreakdownCard() {
        const projs = projectBreakdown.projects || [];
        const unassigned = projectBreakdown.unassigned?.counts || {};
        if (projs.length === 0 && (unassigned.total || 0) === 0) return null;
        const top = [...projs]
            .map(p => ({ ...p, total: p.counts?.total || 0 }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 8);
        const unassignedRow = (unassigned.total || 0) > 0 ? [{ id: '__none__', name: 'Not project-bound', counts: unassigned, total: unassigned.total || 0 }] : [];
        const rows = [...unassignedRow, ...top];
        return (
            <div className="rp-card rp-card-wide" style={{ marginTop: 12 }}>
                <h3>RISK DISTRIBUTION BY PROJECT</h3>
                <table className="rp-table">
                    <thead>
                        <tr>
                            <th>Project</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th style={{ textAlign: 'right' }}>Critical</th>
                            <th style={{ textAlign: 'right' }}>High</th>
                            <th style={{ textAlign: 'right' }}>Medium</th>
                            <th style={{ textAlign: 'right' }}>Low</th>
                            <th style={{ textAlign: 'right' }}>Open</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => {
                            const c = r.counts || {};
                            const isUn = r.id === '__none__';
                            return (
                                <tr key={r.id} className={isUn ? '' : 'rp-clickable'} onClick={isUn ? undefined : () => { setProjectFilter(String(r.id)); setView('risks'); }}
                                    style={isUn ? { background: '#f8fafc', fontStyle: 'italic' } : {}}>
                                    <td>{r.name}{r.code ? <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 4 }}>({r.code})</span> : ''}</td>
                                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{c.total || 0}</td>
                                    <td style={{ textAlign: 'right', color: '#7f1d1d' }}>{c.critical || 0}</td>
                                    <td style={{ textAlign: 'right', color: '#dc2626' }}>{c.high || 0}</td>
                                    <td style={{ textAlign: 'right', color: '#ca8a04' }}>{c.medium || 0}</td>
                                    <td style={{ textAlign: 'right', color: '#16a34a' }}>{c.low || 0}</td>
                                    <td style={{ textAlign: 'right' }}>{c.open || 0}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    function renderPortfolioTrendCard() {
        const series = portfolioTrend.series || [];
        if (series.length === 0) return null;
        // Combined chart: stacked inflow/outflow bars + open-at-end line.
        // Layout in SVG so gridlines + ticks land cleanly.
        const margin = { top: 16, right: 24, bottom: 36, left: 36 };
        const barCount = series.length;
        const innerW = Math.max(barCount * 92, 480);
        const innerH = 200;
        const W = innerW + margin.left + margin.right;
        const H = innerH + margin.top + margin.bottom;
        const maxFlow = Math.max(1, ...series.map(s => Math.max(s.inflow || 0, s.outflow || 0)));
        const maxOpen = Math.max(1, ...series.map(s => s.open_at_end || 0));
        const yScaleFlow = (v) => innerH - (v / maxFlow) * (innerH - 30);
        const yScaleOpen = (v) => innerH - (v / maxOpen) * (innerH - 30);
        const stepX = innerW / Math.max(barCount - 1, 1);
        const ticks = [0, 0.25, 0.5, 0.75, 1].map(t => t * (innerH - 30));

        const linePath = series.map((s, i) => {
            const x = (barCount === 1 ? innerW / 2 : i * stepX);
            const y = yScaleOpen(s.open_at_end || 0);
            return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
        }).join(' ');

        return (
            <div className="rp-card rp-card-wide" style={{ marginTop: 12 }}>
                <h3>PORTFOLIO TREND - last {portfolioTrend.months || 6} months</h3>
                <div style={{ overflowX: 'auto' }}>
                    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Portfolio trend over time">
                        <g transform={`translate(${margin.left}, ${margin.top})`}>
                            {/* Y-axis grid lines */}
                            {ticks.map((t, i) => (
                                <line key={i} x1={0} y1={innerH - t} x2={innerW} y2={innerH - t}
                                    stroke="var(--rp-border)" strokeDasharray="3 3" strokeWidth={1} />
                            ))}
                            {/* Y-axis labels (flow scale on the left) */}
                            {[0, 0.5, 1].map((t, i) => {
                                const v = Math.round(t * maxFlow);
                                const y = innerH - t * (innerH - 30);
                                return <text key={i} x={-8} y={y + 4} textAnchor="end" fontSize="10" fill="var(--rp-text-3)">{v}</text>;
                            })}
                            {/* Bars: paired inflow/outflow */}
                            {series.map((s, i) => {
                                const cx = barCount === 1 ? innerW / 2 : i * stepX;
                                const inflow = s.inflow || 0;
                                const outflow = s.outflow || 0;
                                const inH = (inflow / maxFlow) * (innerH - 30);
                                const outH = (outflow / maxFlow) * (innerH - 30);
                                const barW = 18;
                                return (
                                    <g key={s.period}>
                                        <rect x={cx - barW - 2} y={innerH - inH} width={barW} height={inH}
                                            fill={RP_TREND.inflow} rx={2}>
                                            <title>{`${s.period_label}: ${inflow} new`}</title>
                                        </rect>
                                        <rect x={cx + 2} y={innerH - outH} width={barW} height={outH}
                                            fill={RP_TREND.outflow} rx={2}>
                                            <title>{`${s.period_label}: ${outflow} closed`}</title>
                                        </rect>
                                    </g>
                                );
                            })}
                            {/* Open-at-end line + dots */}
                            <path d={linePath} fill="none" stroke={RP_TREND.open} strokeWidth={2} />
                            {series.map((s, i) => {
                                const x = barCount === 1 ? innerW / 2 : i * stepX;
                                const y = yScaleOpen(s.open_at_end || 0);
                                return (
                                    <g key={s.period}>
                                        <circle cx={x} cy={y} r={4} fill="#fff" stroke={RP_TREND.open} strokeWidth={2}>
                                            <title>{`${s.period_label}: ${s.open_at_end} open at end`}</title>
                                        </circle>
                                        <text x={x} y={y - 9} textAnchor="middle" fontSize="10" fontWeight={600} fill={RP_TREND.open}>{s.open_at_end}</text>
                                    </g>
                                );
                            })}
                            {/* X-axis labels */}
                            {series.map((s, i) => {
                                const x = barCount === 1 ? innerW / 2 : i * stepX;
                                return (
                                    <g key={s.period}>
                                        <text x={x} y={innerH + 16} textAnchor="middle" fontSize="11" fill="var(--rp-text-2)">{s.period_label}</text>
                                        <text x={x} y={innerH + 30} textAnchor="middle" fontSize="10"
                                            fill={s.net >= 0 ? RP_TREND.net_pos : RP_TREND.net_neg}>
                                            net {s.net >= 0 ? '+' : ''}{s.net}
                                        </text>
                                    </g>
                                );
                            })}
                            {/* X-axis baseline */}
                            <line x1={0} y1={innerH} x2={innerW} y2={innerH} stroke="var(--rp-border-2)" strokeWidth={1} />
                        </g>
                    </svg>
                </div>
                <div style={{ display: 'flex', gap: 18, fontSize: 12, color: 'var(--rp-text-2)', marginTop: 4, paddingLeft: margin.left }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ display: 'inline-block', width: 12, height: 12, background: RP_TREND.inflow, borderRadius: 2 }} />New (inflow)</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ display: 'inline-block', width: 12, height: 12, background: RP_TREND.outflow, borderRadius: 2 }} />Closed (outflow)</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><i style={{ display: 'inline-block', width: 12, height: 12, background: RP_TREND.open, borderRadius: 2 }} />Open at month-end</span>
                </div>
            </div>
        );
    }

    function renderBuBreakdownCard() {
        const bus = (buBreakdown.business_units || []);
        const orgCounts = buBreakdown.organization_level?.counts || {};
        if (bus.length === 0 && (orgCounts.total || 0) === 0) return null;
        // Top 8 BUs by total risks (rolled-up)
        const top = [...bus]
            .map(b => ({ ...b, total: b.counts_with_descendants?.total || 0 }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 8);
        const orgRow = (orgCounts.total || 0) > 0 ? [{
            id: '__org__', name: 'Organization-level (no BU)', counts_with_descendants: orgCounts, counts_direct: orgCounts, total: orgCounts.total || 0,
        }] : [];
        const rows = [...orgRow, ...top];
        return (
            <div className="rp-card rp-card-wide" style={{ marginTop: 12 }}>
                <h3>RISK DISTRIBUTION BY BUSINESS UNIT
                    <button className="rp-link-btn" onClick={() => setView('business_units')} style={{ marginLeft: 12 }}>Manage BUs -></button>
                </h3>
                <table className="rp-table">
                    <thead>
                        <tr>
                            <th>Business Unit</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th style={{ textAlign: 'right' }}>Critical</th>
                            <th style={{ textAlign: 'right' }}>High</th>
                            <th style={{ textAlign: 'right' }}>Medium</th>
                            <th style={{ textAlign: 'right' }}>Low</th>
                            <th style={{ textAlign: 'right' }}>Open</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(r => {
                            const c = r.counts_with_descendants || {};
                            const isOrg = r.id === '__org__';
                            return (
                                <tr key={r.id} className={isOrg ? '' : 'rp-clickable'} onClick={isOrg ? undefined : () => { setBuFilter(String(r.id)); setView('risks'); }}
                                    style={isOrg ? { background: '#f8fafc', fontStyle: 'italic' } : {}}>
                                    <td>{r.name}{r.code ? <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 4 }}>({r.code})</span> : ''}</td>
                                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{c.total || 0}</td>
                                    <td style={{ textAlign: 'right', color: '#7f1d1d' }}>{c.critical || 0}</td>
                                    <td style={{ textAlign: 'right', color: '#dc2626' }}>{c.high || 0}</td>
                                    <td style={{ textAlign: 'right', color: '#ca8a04' }}>{c.medium || 0}</td>
                                    <td style={{ textAlign: 'right', color: '#16a34a' }}>{c.low || 0}</td>
                                    <td style={{ textAlign: 'right' }}>{c.open || 0}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    function renderTopRisksTable(d) {
        const rows = d.top_risks || [];
        const canCreate = role === 'risk_admin' || role === 'risk_manager';
        return (
            <div className="rp-card rp-card-wide">
                <h3>TOP RISKS BY SCORE
                    {rows.length > 0 && <button className="rp-link-btn" onClick={() => setView('risks')}>View all -></button>}
                </h3>
                {rows.length === 0 ? (
                    <EmptyState
                        title="No risks captured yet"
                        hint={canCreate
                            ? 'Add your first risk to populate the dashboard, heat map, and top-risks table.'
                            : 'Risks will appear here as the team identifies and assesses them.'}
                        action={canCreate
                            ? <button className="rp-btn rp-btn-primary" onClick={() => setShowCreateRiskModal(true)}>+ Add First Risk</button>
                            : null}
                    />
                ) : (
                    <table className="rp-table">
                        <thead><tr><th>Risk ID</th><th>Description</th><th>Score</th><th>Rating</th><th>State</th><th>Owner</th>{rc.permissions?.can_configure && <th>Action</th>}</tr></thead>
                        <tbody>
                            {rows.map(r => {
                                const rating = normalizeRating(r.inherent_rating);
                                return (
                                    <tr key={r.risk_id} className="rp-clickable" onClick={() => loadRiskDetail(r.id || r.risk_id)}>
                                        <td><span className="rp-risk-id">{r.risk_id}</span></td>
                                        <td>{r.risk}</td>
                                        <td><span className="rp-score-badge" style={{ background: RATING_COLORS[rating] || '#6366f1' }}>{r.inherent_score}</span></td>
                                        <td><span className="rp-rating" style={{ color: RATING_COLORS[rating] }}>{rating}</span></td>
                                        <td><span className="rp-state-badge">{r.state_label || (r.state || '').replace(/_/g, ' ')}</span></td>
                                        <td>{r.risk_owner}</td>
                                        {rc.permissions?.can_configure && (
                                            <td onClick={e => e.stopPropagation()}>
                                                <button
                                                    className="rp-btn"
                                                    style={{ marginRight: 6 }}
                                                    onClick={() => openEditRisk(r.id || r.risk_id)}
                                                >Edit</button>
                                                <button
                                                    className="rp-btn rp-btn-danger"
                                                    onClick={async () => {
                                                        if (!window.confirm(`Delete ${r.risk_id}? This cannot be undone.`)) return;
                                                        try {
                                                            await delete_risk({ id: r.id });
                                                            flash(`${r.risk_id} deleted`, 'success');
                                                            loadRisks(); loadDashboard();
                                                        } catch (err) {
                                                            flash('Delete failed: ' + (err?.response?.data?.error?.message || err.message || 'Unknown error'));
                                                        }
                                                    }}
                                                >Delete</button>
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // MINI HEAT MAP (for dashboard)
    // ═══════════════════════════════════════════════
    function renderMiniHeatmap() {
        if (!dashData) return null;
        // Use heatmap data if loaded, otherwise show empty 5x5
        return (
            <div className="rp-heatmap-mini" onClick={() => setView('heatmap')} style={{ cursor: 'pointer' }}>
                <div className="rp-heatmap-grid-mini">
                    {[4, 3, 2, 1, 0].map(li => (
                        <div key={li} className="rp-heatmap-row-mini">
                            <span className="rp-heatmap-ylabel-mini">{li + 1}</span>
                            {[0, 1, 2, 3, 4].map(im => {
                                const count = heatmapData?.matrix?.[li]?.[im] || 0;
                                return (
                                    <div key={im} className="rp-heatmap-cell-mini" style={{ background: count > 0 ? HEATMAP_COLORS[li][im] : `${HEATMAP_COLORS[li][im]}30` }}>
                                        {count > 0 && <span>{count}</span>}
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // FULL 5x5 HEAT MAP
    // ═══════════════════════════════════════════════
    function renderHeatmap() {
        return (
            <div className="rp-view">
                <h2 className="rp-view-title">5x5 Risk Heat Map</h2>
                <p className="rp-subtitle">
                    {heatmapType === 'inherent'
                        ? 'Inherent Risk: Raw exposure before any controls or treatments are applied. Score = Likelihood × Impact.'
                        : 'Residual Risk: Remaining exposure after controls reduce likelihood and impact. Only risks with a portal assessment (dual scoring) appear here. Use "Assess Risk" on each risk to set residual scores.'}
                </p>
                <div className="rp-heatmap-controls">
                    <button className={`rp-toggle-btn ${heatmapType === 'inherent' ? 'active' : ''}`} onClick={() => setHeatmapType('inherent')}>Inherent Risk</button>
                    <button className={`rp-toggle-btn ${heatmapType === 'residual' ? 'active' : ''}`} onClick={() => setHeatmapType('residual')}>Residual Risk</button>
                    <span className="rp-heatmap-total">Total Assessed: {heatmapData?.total_assessed || 0}</span>
                </div>

                <div className="rp-heatmap-container">
                    <div className="rp-heatmap-ylabel-col">
                        <span className="rp-heatmap-axis-title">Likelihood ↑</span>
                        {[4, 3, 2, 1, 0].map(i => <span key={i} className="rp-heatmap-ylabel">{LIKELIHOOD_LABELS[i]}<br /><small>({i + 1})</small></span>)}
                    </div>
                    <div>
                        <div className="rp-heatmap-grid">
                            {[4, 3, 2, 1, 0].map(li => (
                                <div key={li} className="rp-heatmap-row">
                                    {[0, 1, 2, 3, 4].map(im => {
                                        const count = heatmapData?.matrix?.[li]?.[im] || 0;
                                        const cellKey = `${li + 1},${im + 1}`;
                                        const isActive = drillCell === cellKey;
                                        return (
                                            <div key={im}
                                                className={`rp-heatmap-cell ${count > 0 ? 'rp-heatmap-clickable' : ''} ${isActive ? 'rp-heatmap-active' : ''}`}
                                                style={{ background: count > 0 ? HEATMAP_COLORS[li][im] : `${HEATMAP_COLORS[li][im]}20` }}
                                                onClick={() => count > 0 && setDrillCell(isActive ? null : cellKey)}>
                                                <span className="rp-heatmap-score">{(li + 1) * (im + 1)}</span>
                                                {count > 0 && <span className="rp-heatmap-count">{count} risk{count > 1 ? 's' : ''}</span>}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                        <div className="rp-heatmap-xlabel-row">
                            {IMPACT_LABELS.map((l, i) => <span key={i} className="rp-heatmap-xlabel">{l}<br /><small>({i + 1})</small></span>)}
                        </div>
                        <div className="rp-heatmap-axis-title" style={{ textAlign: 'center' }}>Impact -></div>
                    </div>
                </div>

                {/* Legend */}
                <div className="rp-heatmap-legend">
                    <span style={{ background: '#19A07E' }}>Low (1-4)</span>
                    <span style={{ background: '#D9BF21', color: '#1b2a4a' }}>Medium (5-9)</span>
                    <span style={{ background: '#DE9E30' }}>High (10-15)</span>
                    <span style={{ background: '#D24646' }}>Critical (16-25)</span>
                </div>

                {/* No residual data banner */}
                {heatmapType === 'residual' && (heatmapData?.total_assessed || 0) === 0 && (
                    <div className="rp-card" style={{ textAlign: 'center', padding: 24, marginTop: 16 }}>
                        <h3 style={{ textTransform: 'none', letterSpacing: 0 }}>No Residual Risk Data</h3>
                        <p className="rp-empty-msg" style={{ maxWidth: 500, margin: '0 auto' }}>
                            Residual risk requires a formal assessment. Open any risk and click "Assess Risk" to score both inherent (before controls) and residual (after controls) separately.
                        </p>
                        <button className="rp-btn rp-btn-primary" style={{ marginTop: 12 }} onClick={() => setView('risks')}>Go to Risk Register</button>
                    </div>
                )}

                {/* Drill-down */}
                {drillCell && heatmapData?.risks_by_cell?.[drillCell] && (
                    <div className="rp-card rp-card-wide rp-drill-down">
                        <h3>Risks at Likelihood {drillCell.split(',')[0]}, Impact {drillCell.split(',')[1]}</h3>
                        <table className="rp-table">
                            <thead><tr><th>Risk ID</th><th>Description</th><th>Inherent</th><th>Residual</th><th>Rating</th></tr></thead>
                            <tbody>
                                {heatmapData.risks_by_cell[drillCell].map(r => (
                                    <tr key={r.risk_id} className="rp-clickable" onClick={() => loadRiskDetail(r.id || r.risk_id)}>
                                        <td><span className="rp-risk-id">{r.risk_id}</span></td>
                                        <td>{r.risk}</td>
                                        <td>{r.inherent_score}</td>
                                        <td>{r.residual_score}</td>
                                        <td><span className="rp-rating" style={{ color: RATING_COLORS[r.rating] }}>{r.rating}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // RISK LIST VIEW
    // ═══════════════════════════════════════════════
    function renderRiskList() {
        return (
            <div className="rp-view">
                <div className="rp-section-header" style={{ marginBottom: 16 }}>
                    <h2 className="rp-view-title" style={{ marginBottom: 0 }}>
                        {view === 'my_risks' ? 'My Risks' : view === 'bu_risks' ? 'Business Unit Risks' : 'Risk Register'}
                    </h2>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                        {(role === 'risk_admin' || role === 'ciso') && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--rp-text-2)', cursor: 'pointer' }}>
                                <input type="checkbox" checked={viewAllOrgs} onChange={(e) => { setViewAllOrgs(e.target.checked); setRiskPage(1); }} />
                                All Organizations
                            </label>
                        )}
                        {(rc.permissions?.can_assess || rc.permissions?.can_configure) && (
                            <button className="rp-btn rp-btn-primary" onClick={() => {
                                // Pre-fill risk owner ONLY for self-assigning roles (Risk Owner).
                                // Risk Manager / Admin / Reviewer should explicitly pick an owner -
                                // RACI separates the creator from the accountable owner.
                                const isSelfAssignRole = role === 'risk_owner';
                                if (isSelfAssignRole && !riskForm.risk_owner) {
                                    const me = localStorage.getItem('email') || '';
                                    if (me) setRiskForm(p => ({ ...p, risk_owner: me }));
                                } else if (!isSelfAssignRole) {
                                    // Always clear when a non-self-assigning role opens the modal,
                                    // so a stale value from a previous session doesn't carry over.
                                    setRiskForm(p => ({ ...p, risk_owner: '' }));
                                }
                                // Make sure custom categories appear in the dropdown
                                if (!categories || categories.length === 0) loadCategories();
                                setShowCreateRiskModal(true);
                            }}>+ Create Risk</button>
                        )}
                    </div>
                    {(rc.permissions?.can_assess || rc.permissions?.can_configure) && (<>
                        <button className="rp-btn" onClick={async () => {
                            try {
                                const response = await download_risk_registry_template({ responseType: 'blob' });
                                const blobData = response?.statusCode;
                                if (blobData?.type === 'application/json') {
                                    const text = await blobData.text();
                                    const json = JSON.parse(text);
                                    alert(json?.error?.message || json?.message || 'Template download failed.');
                                    return;
                                }
                                const blob = new Blob([blobData], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                                const url = window.URL.createObjectURL(blob);
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', 'risk-registry-template.xlsx');
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                window.URL.revokeObjectURL(url);
                            } catch (error) {
                                console.error('Template download failed:', error);
                                alert('Template download failed.');
                            }
                        }}>Download Template</button>
                        <label className="rp-btn" style={{ cursor: 'pointer' }}>
                            Import Excel/CSV
                            <input type="file" style={{ display: 'none' }} accept=".csv,.xlsx,.xls"
                                onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    try {
                                        const fd = new FormData();
                                        fd.append('file', file);
                                        const res = await risk_portal_bulk_import(fd);
                                        const d = extract(res);
                                        // Show detailed results
                                        const errs = (d?.results || []).filter(r => r.status === 'error');
                                        if (errs.length > 0) {
                                            const errMsg = errs.slice(0, 5).map(r => `Row ${r.row}: ${r.errors.join(', ')}`).join('\n');
                                            alert(`Imported: ${d?.created || 0} risks\nSkipped: ${d?.skipped || 0} rows\n\nErrors:\n${errMsg}${errs.length > 5 ? `\n...and ${errs.length - 5} more` : ''}`);
                                        }
                                        flash(`${d?.created || 0} risks imported, ${d?.skipped || 0} skipped`);
                                        loadRisks(); loadDashboard();
                                    } catch (err) {
                                        flash('Import failed: ' + (err?.response?.data?.error_msg || err?.response?.data?.response_data?.message || err.message));
                                    }
                                    e.target.value = '';
                                }} />
                        </label>
                    </>)}
                    <button className="rp-btn" onClick={() => {
                        const rows = (risks.results || []).map(r => [r.risk_id, `"${(r.risk || '').replace(/"/g, '""')}"`, r.risk_type, r.state, r.severity, r.inherent_score || '', r.residual_score || '', r.risk_owner, r.target_date || ''].join(','));
                        const csv = 'Risk ID,Description,Type,State,Severity,Inherent Score,Residual Score,Owner,Target Date\n' + rows.join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' });
                        const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'risk_registry_export.csv'; a.click();
                    }}>Export CSV</button>
                </div>

                {/* Filters */}
                <div className="rp-filters">
                    <input className="rp-input" placeholder="Search risks..." value={riskFilters.search || ''}
                        onChange={e => setRiskFilters(f => ({ ...f, search: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && loadRisks()} />
                    <select className="rp-select" value={riskFilters.state || ''} onChange={e => { setRiskFilters(f => ({ ...f, state: e.target.value })); setRiskPage(1); }}>
                        <option value="">All States</option>
                        <option value="open">Open</option><option value="in_progress">In Progress</option>
                        <option value="accepted">Accepted</option><option value="closed">Closed</option>
                        <option value="transferred">Transferred</option>
                    </select>
                    <select className="rp-select" value={riskFilters.risk_type || ''} onChange={e => { setRiskFilters(f => ({ ...f, risk_type: e.target.value })); setRiskPage(1); }}>
                        <option value="">All Categories</option>
                        {categories && categories.length > 0 ? (
                            categories.filter(c => c.is_active !== false).map(c => (
                                <option key={c.id || c.name} value={c.name || c.id}>{c.name}</option>
                            ))
                        ) : (
                            <>
                                <option value="financial">Financial</option><option value="operational">Operational</option>
                                <option value="technical">Technical</option><option value="compliance_legal">Compliance/Legal</option>
                                <option value="reputational">Reputational</option><option value="strategic">Strategic</option>
                            </>
                        )}
                    </select>
                    <select className="rp-select" value={buFilter} onChange={(e) => { setBuFilter(e.target.value); setRiskPage(1); }} style={{ minWidth: 200 }}>
                        <option value="">All BUs (organization-wide)</option>
                        {businessUnits.filter(b => b.is_active).map(b => (
                            <option key={b.id} value={b.id}>{b.parent_id ? '↳ ' : ''}{b.name}{b.code ? ` (${b.code})` : ''}</option>
                        ))}
                    </select>
                    <select className="rp-select" value={projectFilter} onChange={(e) => { setProjectFilter(e.target.value); setRiskPage(1); }} style={{ minWidth: 200 }}>
                        <option value="">All Projects</option>
                        {projects.filter(p => p.is_active !== false).map(p => (
                            <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ''}</option>
                        ))}
                    </select>
                    <button className="rp-btn rp-btn-primary" onClick={loadRisks}>Search</button>
                </div>
                {buFilter && (
                    <div style={{ fontSize: 12, color: '#475569', marginBottom: 8 }}>
                        Showing risks for <b>{businessUnits.find(b => b.id === parseInt(buFilter))?.name || ''}</b> and all sub-units.
                        <button className="rp-link-btn" onClick={() => setBuFilter('')} style={{ marginLeft: 8 }}>Clear</button>
                    </div>
                )}

                <div className="rp-table-scroll">
                <table className="rp-table">
                    <thead>
                        <tr>
                            <th>Risk ID</th><th>Description</th><th>Business Unit</th><th>Project</th><th>Type</th><th>State</th>
                            <th>Inherent</th><th>Residual</th><th>Velocity</th><th>Owner</th><th>Target</th>
                            {rc.permissions?.can_configure && <th>Action</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {(risks.results || []).map(r => (
                            <tr key={r.id} className="rp-clickable" onClick={() => loadRiskDetail(r.id)}>
                                <td><span className="rp-risk-id">{r.risk_id}</span></td>
                                <td className="rp-cell-desc">{r.risk}</td>
                                <td>{r.business_unit_name ? <span className="rp-role-badge">{r.business_unit_name}</span> : <span style={{ color: '#94a3b8', fontSize: 11 }}>org-level</span>}</td>
                                <td>{r.project_name ? <span className="rp-role-badge" style={{ background: '#ecfdf5', color: '#065f46' }}>{r.project_name}</span> : <span style={{ color: '#94a3b8', fontSize: 11 }}>-</span>}</td>
                                <td><span className="rp-type-badge">{(r.risk_type || '').replace(/_/g, ' ')}</span></td>
                                <td><span className="rp-state-badge">{r.state_label || (r.state || '').replace(/_/g, ' ')}</span></td>
                                <td>{r.assessed ? <span className="rp-score-badge" style={{ background: RATING_COLORS[r.inherent_rating] }}>{r.inherent_score}</span> : <span className="rp-not-assessed">-</span>}</td>
                                <td>{r.assessed ? <span className="rp-score-badge" style={{ background: RATING_COLORS[r.residual_rating], opacity: 0.8 }}>{r.residual_score}</span> : '-'}</td>
                                <td>{r.risk_velocity || '-'}</td>
                                <td>{r.risk_owner || '-'}</td>
                                <td>{fmtDate(r.target_date) || '-'}</td>
                                {rc.permissions?.can_configure && (
                                    <td onClick={e => e.stopPropagation()}>
                                        <button
                                            className="rp-btn"
                                            style={{ marginRight: 6 }}
                                            onClick={() => openEditRisk(r.id)}
                                        >Edit</button>
                                        <button
                                            className="rp-btn rp-btn-danger"
                                            onClick={async () => {
                                                if (!window.confirm(`Delete ${r.risk_id}? This cannot be undone.`)) return;
                                                try {
                                                    await delete_risk({ id: r.id });
                                                    flash(`${r.risk_id} deleted`, 'success');
                                                    loadRisks();
                                                } catch (err) {
                                                    flash('Delete failed: ' + (err?.response?.data?.error?.message || err.message || 'Unknown error'));
                                                }
                                            }}
                                        >Delete</button>
                                    </td>
                                )}
                            </tr>
                        ))}
                        {risks.results?.length === 0 && <tr><td colSpan={rc.permissions?.can_configure ? 12 : 11} className="rp-empty-cell">No risks found</td></tr>}
                    </tbody>
                </table>
                </div>

                {/* Pagination */}
                {risks.pages > 1 && (
                    <div className="rp-pagination">
                        <button disabled={riskPage <= 1} onClick={() => setRiskPage(p => p - 1)}>Previous</button>
                        <span>Page {risks.page} of {risks.pages} ({risks.count} total)</span>
                        <button disabled={riskPage >= risks.pages} onClick={() => setRiskPage(p => p + 1)}>Next</button>
                    </div>
                )}
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // RISK DETAIL VIEW
    // ═══════════════════════════════════════════════
    function renderRiskDetail() {
        const r = riskDetail;
        const a = r.assessment;
        const perms = rc.permissions || {};
        return (
            <div className="rp-view">
                <button className="rp-back-link" onClick={() => { setView('risks'); setRiskDetail(null); }}>← Back to Risk List</button>

                <div className="rp-detail-header">
                    <h2><span className="rp-risk-id-lg">{r.risk_id}</span> {r.risk}</h2>
                    <div className="rp-detail-meta">
                        <span className="rp-state-badge rp-state-badge-lg">{r.state_label || (r.state || '').replace(/_/g, ' ')}</span>
                        <span className="rp-type-badge">{(r.risk_type || '').replace(/_/g, ' ')}</span>
                        {r.risk_owner && <span className="rp-owner-badge">Owner: {r.risk_owner}</span>}
                        {r.target_date && <span className="rp-date-badge">Target: {fmtDate(r.target_date)}</span>}
                        <button className="rp-btn rp-btn-share" onClick={() => { setShareForm({ recipient_email: r.risk_owner || '', recipient_name: '', permission: 'update', message: '', expires_days: 30 }); setShareUrl(''); loadRiskShares(); setShowShareModal(true); }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.59" y1="13.51" x2="15.42" y2="17.49" /><line x1="15.41" y1="6.51" x2="8.59" y2="10.49" /></svg>
                            Share Risk
                        </button>
                        {/* Acknowledgement status + request button */}
                        {ackStatus?.status === 'acknowledged' && (
                            <span className="rp-ack-badge rp-ack-done">Ownership Acknowledged</span>
                        )}
                        {ackStatus?.status === 'declined' && (
                            <span className="rp-ack-badge rp-ack-declined">Declined: {ackStatus.declined_reason || 'No reason'}</span>
                        )}
                        {ackStatus?.status === 'pending' && (
                            <span className="rp-ack-badge rp-ack-pending">Pending Acknowledgement</span>
                        )}
                        {(rc.permissions?.can_assess || rc.permissions?.can_configure) && r.risk_owner && ackStatus?.status !== 'acknowledged' && (
                            <button className="rp-btn" style={{ fontSize: 11 }} onClick={handleRequestAck}>
                                {ackStatus?.status === 'pending' ? 'Send Reminder' : 'Request Acknowledgement'}
                            </button>
                        )}
                    </div>
                </div>

                {/* Workflow Stage Progress Bar */}
                {renderStageProgressBar(r)}

                {/* Assessment Section */}
                <div className="rp-detail-section">
                    <div className="rp-section-header">
                        <h3>Risk Assessment</h3>
                        {perms.can_assess && <button className="rp-btn rp-btn-primary" onClick={() => { setAssessForm(a ? { inherent_likelihood: a.inherent_likelihood, inherent_impact: a.inherent_impact, residual_likelihood: a.residual_likelihood, residual_impact: a.residual_impact, control_effectiveness: a.control_effectiveness, risk_velocity: a.risk_velocity, notes: '' } : { inherent_likelihood: 1, inherent_impact: 1, residual_likelihood: 1, residual_impact: 1, control_effectiveness: 0, risk_velocity: 'months', notes: '' }); setShowAssessModal(true); }}>
                            {a ? 'Reassess' : 'Assess Risk'}
                        </button>}
                    </div>
                    {a ? (
                        <div>
                            {a.source === 'dashboard' && (
                                <p className="rp-not-assessed-msg" style={{ marginBottom: 12 }}>
                                    Showing dashboard scores (inherent only). Click "Assess Risk" to add residual scoring with control effectiveness.
                                </p>
                            )}
                            <div className="rp-assessment-grid">
                                <div className="rp-assess-card">
                                    <h4>Inherent Risk</h4>
                                    <div className="rp-assess-score" style={{ background: RATING_COLORS[a.inherent_rating] || '#8492a6' }}>{a.inherent_score}</div>
                                    <div className="rp-assess-rating" style={{ color: RATING_COLORS[a.inherent_rating] }}>{a.inherent_rating}</div>
                                    <div className="rp-assess-detail">Likelihood: {a.inherent_likelihood} | Impact: {a.inherent_impact}</div>
                                </div>
                                <div className="rp-assess-card">
                                    <h4>Residual Risk</h4>
                                    {a.residual_score != null ? (<>
                                        <div className="rp-assess-score" style={{ background: RATING_COLORS[a.residual_rating] || '#8492a6' }}>{a.residual_score}</div>
                                        <div className="rp-assess-rating" style={{ color: RATING_COLORS[a.residual_rating] }}>{a.residual_rating}</div>
                                        <div className="rp-assess-detail">Likelihood: {a.residual_likelihood} | Impact: {a.residual_impact}</div>
                                    </>) : (
                                        <div className="rp-assess-not-set">
                                            <div style={{ fontSize: 20, color: 'var(--rp-text-3)', marginBottom: 4 }}>-</div>
                                            <div className="rp-assess-detail">Not assessed yet</div>
                                        </div>
                                    )}
                                </div>
                                <div className="rp-assess-card">
                                    <h4>Control Effectiveness</h4>
                                    {a.control_effectiveness > 0 ? (
                                        <div className="rp-assess-score" style={{ background: '#6366f1' }}>{a.control_effectiveness}%</div>
                                    ) : (
                                        <div style={{ fontSize: 20, color: 'var(--rp-text-3)' }}>-</div>
                                    )}
                                    <div className="rp-assess-detail">Velocity: {a.risk_velocity || '-'}</div>
                                </div>
                                {a.version > 0 && (
                                    <div className="rp-assess-card">
                                        <h4>Version</h4>
                                        <div className="rp-assess-score" style={{ background: '#8492a6' }}>v{a.version}</div>
                                        <div className="rp-assess-detail">{fmtTime(a.assessed_at)}</div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div>
                            <p className="rp-not-assessed-msg" style={{ marginBottom: 12 }}>
                                No formal assessment yet. Showing inherent (captured at creation) and current residual values.
                            </p>
                            <div className="rp-assessment-grid">
                                <div className="rp-assess-card">
                                    <h4>Inherent Risk</h4>
                                    <div className="rp-assess-score" style={{ background: RATING_COLORS[r.inherent_severity] || '#8492a6' }}>{r.inherent_score ?? (r.inherent_likelihood && r.inherent_impact ? r.inherent_likelihood * r.inherent_impact : '-')}</div>
                                    <div className="rp-assess-rating" style={{ color: RATING_COLORS[r.inherent_severity] }}>{r.inherent_severity || '-'}</div>
                                    <div className="rp-assess-detail">Likelihood: {r.inherent_likelihood ?? '-'} | Impact: {r.inherent_impact ?? '-'}</div>
                                </div>
                                <div className="rp-assess-card">
                                    <h4>Residual Risk (current)</h4>
                                    <div className="rp-assess-score" style={{ background: RATING_COLORS[r.residual_severity || r.severity] || '#8492a6' }}>{r.residual_score ?? r.risk_score ?? '-'}</div>
                                    <div className="rp-assess-rating" style={{ color: RATING_COLORS[r.residual_severity || r.severity] }}>{r.residual_severity || r.severity || '-'}</div>
                                    <div className="rp-assess-detail">Likelihood: {r.residual_likelihood ?? r.likelihood ?? '-'} | Impact: {r.residual_impact ?? r.impact ?? '-'}</div>
                                </div>
                                {(r.target_likelihood || r.target_impact) && (
                                    <div className="rp-assess-card">
                                        <h4>Target Risk</h4>
                                        <div className="rp-assess-score" style={{ background: '#0ea5e9' }}>{(r.target_likelihood || 0) * (r.target_impact || 0) || '-'}</div>
                                        <div className="rp-assess-detail">Likelihood: {r.target_likelihood ?? '-'} | Impact: {r.target_impact ?? '-'}</div>
                                    </div>
                                )}
                            </div>
                            <div style={{ marginTop: 8, fontSize: 12, color: '#64748b' }}>
                                Inherent rating is captured at creation and never changes. Residual reflects the current likelihood and impact.
                            </div>
                        </div>
                    )}

                    {/* Field Change History */}
                    {(role === 'risk_admin' || role === 'risk_manager' || role === 'ciso') && (
                        <details className="rp-history" style={{ marginTop: 12 }}>
                            <summary>Field Change History (audit log)</summary>
                            <FieldChangeHistory riskId={r.id} riskKey={r.risk_id} />
                        </details>
                    )}

                    {/* Assessment History */}
                    {r.assessment_history?.length > 1 && (
                        <details className="rp-history">
                            <summary>Assessment History ({r.assessment_history.length} versions)</summary>
                            <table className="rp-table rp-table-sm">
                                <thead><tr><th>Version</th><th>Inherent</th><th>Rating</th><th>Residual</th><th>Date</th></tr></thead>
                                <tbody>
                                    {r.assessment_history.map(h => (
                                        <tr key={h.id}><td>v{h.version}</td><td>{h.inherent_score}</td><td>{h.inherent_rating}</td><td>{h.residual_score}</td><td>{fmtTime(h.assessed_at)}</td></tr>
                                    ))}
                                </tbody>
                            </table>
                        </details>
                    )}
                </div>

                {/* Treatment Plans */}
                <div className="rp-detail-section">
                    <div className="rp-section-header">
                        <h3>Treatment Plans ({r.treatments?.length || 0})</h3>
                        {perms.can_treat && <button className="rp-btn rp-btn-primary" onClick={() => { setTreatmentForm({ strategy: 'mitigate', description: '', actions: [{ title: '', owner_email: '', priority: 'medium', due_date: '' }] }); setShowTreatmentModal(true); }}>Add Treatment</button>}
                    </div>
                    {(r.treatments || []).map(tp => (
                        <div key={tp.id} className="rp-treatment-card">
                            <div className="rp-treatment-header">
                                <span className="rp-treatment-strategy">{tp.strategy}</span>
                                <span className={`rp-treatment-status rp-treatment-status-${tp.status}`}>{(tp.status || '').replace(/_/g, ' ')}</span>
                                {/* Approve/Reject buttons for reviewers - visible while plan isn't yet verified */}
                                {perms.can_approve && tp.id && tp.status !== 'verified' && (
                                    <div className="rp-treatment-actions">
                                        <button className="rp-btn-approve" onClick={() => handleApproveTreatment(tp.id)} title="Mark plan as verified. Owner will be notified.">Approve</button>
                                        <button className="rp-btn-reject" onClick={() => handleRejectTreatment(tp.id)} title="Send back for revision. Owner will be notified with the reason.">Reject</button>
                                    </div>
                                )}
                                {perms.can_approve && tp.status === 'verified' && (
                                    <span className="rp-treatment-status rp-treatment-status-verified" style={{ marginLeft: 6 }}>✓ verified</span>
                                )}
                                {/* Edit button */}
                                {(perms.can_treat || perms.can_configure) && tp.id && tp.status !== 'verified' && (
                                    <button className="rp-btn-sm-ghost" onClick={() => {
                                        const newStatus = tp.status === 'not_started' ? 'in_progress' : tp.status === 'in_progress' ? 'completed' : tp.status;
                                        handleUpdateTreatment(tp.id, { status: newStatus });
                                    }} title={`Mark as ${tp.status === 'not_started' ? 'In Progress' : 'Completed'}`}>
                                        {tp.status === 'not_started' ? 'Start' : tp.status === 'in_progress' ? 'Complete' : 'Edit'}
                                    </button>
                                )}
                                {/* Delete button */}
                                {(perms.can_treat || perms.can_configure) && tp.id && (
                                    <button className="rp-btn-sm-danger" onClick={() => handleDeleteTreatment(tp.id)} title="Delete treatment">×</button>
                                )}
                            </div>
                            <p>{tp.description}</p>
                            {tp.actions?.length > 0 && (
                                <table className="rp-table rp-table-sm">
                                    <thead><tr><th>Action</th><th>Owner</th><th>Priority</th><th>Due</th><th>Status</th>{(perms.can_treat || perms.can_configure) && <th></th>}</tr></thead>
                                    <tbody>
                                        {tp.actions.map(act => (
                                            <tr key={act.id}>
                                                <td>{act.title}</td><td>{act.owner_email}</td>
                                                <td><span className={`rp-priority-badge rp-priority-${act.priority}`}>{act.priority}</span></td>
                                                <td>{fmtDate(act.due_date)}</td>
                                                <td><span className={`rp-treatment-status rp-treatment-status-${act.status}`}>{(act.status || '').replace(/_/g, ' ')}</span></td>
                                                {(perms.can_treat || perms.can_configure) && <td>
                                                    {act.status !== 'completed' && (
                                                        <button className="rp-btn-sm-ghost" onClick={() => {
                                                            const newStatus = act.status === 'not_started' ? 'in_progress' : 'completed';
                                                            handleUpdateTreatment(tp.id, { actions: [{ id: act.id, status: newStatus }] });
                                                        }}>{act.status === 'not_started' ? 'Start' : 'Done'}</button>
                                                    )}
                                                </td>}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    ))}
                    {(!r.treatments || r.treatments.length === 0) && <p className="rp-empty-msg">No treatment plans defined.</p>}
                </div>

                {/* Controls */}
                <div className="rp-detail-section">
                    <div className="rp-section-header">
                        <h3>Mapped Controls ({r.controls?.length || 0})</h3>
                        {(perms.can_assess || perms.can_treat) && <button className="rp-btn rp-btn-primary" onClick={() => { loadFrameworks(); loadFrameworkControls(); setShowControlMapModal(true); }}>Map Control</button>}
                    </div>
                    {r.controls?.length > 0 && (
                        <table className="rp-table rp-table-sm">
                            <thead><tr><th>Control</th><th>Type</th><th>Effectiveness</th><th>Last Tested</th><th></th></tr></thead>
                            <tbody>
                                {r.controls.map(c => (
                                    <tr key={c.id}>
                                        <td>{c.control_name}</td><td>{c.control_type}</td>
                                        <td>
                                            {(perms.can_assess || perms.can_configure) ? (
                                                <select className="rp-select" defaultValue={c.effectiveness} style={{ width: 80 }}
                                                    onChange={(e) => handleUpdateControl(c.id, { effectiveness: parseInt(e.target.value), last_tested: new Date().toISOString() })}>
                                                    <option value="0">0%</option><option value="25">25%</option><option value="50">50%</option><option value="75">75%</option><option value="100">100%</option>
                                                </select>
                                            ) : `${c.effectiveness}%`}
                                        </td>
                                        <td>{fmtDate(c.last_tested) || '-'}</td>
                                        <td>{c.id && <button className="rp-btn-sm-danger" onClick={() => handleDeleteControl(c.id)}>×</button>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {(!r.controls || r.controls.length === 0) && <p className="rp-empty-msg">No controls mapped. Use "Map Control" to link framework controls to this risk.</p>}
                </div>

                {/* KRIs */}
                {r.kris?.length > 0 && (
                    <div className="rp-detail-section">
                        <div className="rp-section-header">
                            <h3>Key Risk Indicators ({r.kris?.length || 0})</h3>
                            {(perms.can_assess || perms.can_treat) && <button className="rp-btn rp-btn-primary" onClick={() => { setKriForm({ name: '', description: '', unit: '', current_value: 0, threshold_warning: '', threshold_breach: '' }); setShowKriCreateModal(true); }}>Add KRI</button>}
                        </div>
                        <div className="rp-kri-cards">
                            {r.kris.map(k => (
                                <div key={k.id} className={`rp-kri-card rp-kri-${k.status}`}>
                                    <div className="rp-kri-name">{k.name}</div>
                                    <div className="rp-kri-value">{k.current_value} {k.unit}</div>
                                    <div className="rp-kri-thresholds">
                                        <span>Warning: {k.threshold_warning}</span> | <span>Breach: {k.threshold_breach}</span>
                                    </div>
                                    <div className={`rp-kri-status rp-kri-status-${k.status}`}>{k.status}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Evidence */}
                <div className="rp-detail-section">
                    <div className="rp-section-header">
                        <h3>Evidence ({r.evidence?.length || 0})</h3>
                        <label className="rp-btn rp-btn-primary" style={{ cursor: 'pointer' }}>
                            Upload
                            <input type="file" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.zip"
                                onChange={async (e) => {
                                    const file = e.target.files[0];
                                    if (!file) return;
                                    try {
                                        const fd = new FormData();
                                        fd.append('file', file);
                                        fd.append('risk_id', r.id);
                                        fd.append('description', '');
                                        await risk_portal_upload_evidence(fd);
                                        flash('Evidence uploaded');
                                        loadRiskDetail(r.id);
                                    } catch (err) { flash('Upload failed'); }
                                    e.target.value = '';
                                }} />
                        </label>
                    </div>
                    {r.evidence?.length > 0 ? (
                        <table className="rp-table rp-table-sm">
                            <thead><tr><th>File</th><th>Description</th><th>Uploaded</th><th></th></tr></thead>
                            <tbody>
                                {r.evidence.map(ev => (
                                    <tr key={ev.id}>
                                        <td>{ev.file_name}</td>
                                        <td>{ev.description || '-'}</td>
                                        <td>{fmtTime(ev.uploaded_at)}</td>
                                        <td>{ev.id && <button className="rp-btn-sm-danger" onClick={() => handleDeleteEvidence(ev.id)}>×</button>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <p className="rp-empty-msg">No evidence files attached.</p>}
                </div>

                {/* Compliance Mapping */}
                <div className="rp-detail-section">
                    <div className="rp-section-header">
                        <h3>Compliance Mapping ({complianceMappings.length})</h3>
                        {(perms.can_assess || perms.can_configure) && (
                            <button className="rp-btn rp-btn-primary" onClick={() => { loadFrameworks(); loadFrameworkControls(); setShowControlMapModal('compliance'); }}>Map to Requirement</button>
                        )}
                    </div>
                    {complianceMappings.length > 0 ? (
                        <table className="rp-table rp-table-sm">
                            <thead><tr><th>Framework</th><th>Ref</th><th>Requirement</th><th>Type</th><th></th></tr></thead>
                            <tbody>
                                {complianceMappings.map(m => (
                                    <tr key={m.id}>
                                        <td><strong>{m.framework_name}</strong></td>
                                        <td>{m.control_ref}</td>
                                        <td>{m.control_title}</td>
                                        <td><span className="rp-type-badge">{m.mapping_type}</span></td>
                                        <td><button className="rp-btn-sm-danger" onClick={() => handleDeleteComplianceMapping(r.id, m.id)}>x</button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : <p className="rp-empty-msg">No compliance requirements mapped. Map this risk to framework controls for regulatory traceability.</p>}
                </div>

                {/* Comments */}
                <div className="rp-detail-section">
                    <h3>Comments ({r.comments?.length || 0})</h3>
                    <div className="rp-comment-form">
                        <textarea className="rp-textarea" rows={2} value={commentText} onChange={e => setCommentText(e.target.value)} placeholder="Add a comment..." />
                        <button className="rp-btn rp-btn-sm" onClick={handleComment}>Post</button>
                    </div>
                    {(r.comments || []).map(c => (
                        <div key={c.id} className="rp-comment">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span className="rp-comment-time">{fmtTime(c.created_at)}</span>
                                {c.id && <button className="rp-btn-sm-danger" onClick={() => handleDeleteComment(c.id)} title="Delete">×</button>}
                            </div>
                            <p>{c.text}</p>
                        </div>
                    ))}
                </div>

                {/* NOVA AI Assistant */}
                <div className="rp-detail-section">
                    <div className="rp-section-header">
                        <h3>NOVA AI Assistant</h3>
                    </div>
                    <div className="rp-ai-actions">
                        <button className="rp-btn" onClick={() => handleAiCategorize(r.risk)} disabled={!!aiLoading}>
                            {aiLoading === 'categorize' ? 'Analyzing...' : 'Auto-Categorize'}
                        </button>
                        <button className="rp-btn" onClick={() => handleAiDuplicates(r.risk)} disabled={!!aiLoading}>
                            {aiLoading === 'duplicates' ? 'Analyzing...' : 'Detect Duplicates'}
                        </button>
                        <button className="rp-btn" onClick={() => handleAiStatement(r.risk)} disabled={!!aiLoading}>
                            {aiLoading === 'statement' ? 'Analyzing...' : 'Generate ISO Statement'}
                        </button>
                        <button className="rp-btn" onClick={() => handleAiTreatment(r.risk, r.risk_type, r.severity)} disabled={!!aiLoading}>
                            {aiLoading === 'treatment' ? 'Analyzing...' : 'Suggest Treatment'}
                        </button>
                    </div>
                    {aiResult && (
                        <div className="rp-ai-result">
                            {aiResult.type === 'categorize' && (<>
                                <div className="rp-ai-label">Suggested Category: <strong>{aiResult.category}</strong> ({Math.round((aiResult.confidence || 0) * 100)}% confidence)</div>
                                {aiResult.reasoning && <p>{aiResult.reasoning}</p>}
                                <span className="rp-ai-method">via {aiResult.method}</span>
                            </>)}
                            {aiResult.type === 'duplicates' && (<>
                                <div className="rp-ai-label">{aiResult.count || 0} potential duplicate(s) found</div>
                                {(aiResult.duplicates || []).map((d, i) => (
                                    <div key={i} className="rp-ai-dup-item">
                                        <span className="rp-risk-id rp-clickable" onClick={() => loadRiskDetail(d.id || d.risk_id)}>{d.risk_id}</span>
                                        <span>{d.risk}</span>
                                        <span className="rp-score-badge" style={{ background: '#6366f1' }}>{d.similarity}%</span>
                                        {d.reason && <span style={{ fontSize: 10, color: 'var(--rp-text-3)' }}>{d.reason}</span>}
                                    </div>
                                ))}
                            </>)}
                            {aiResult.type === 'statement' && (<>
                                <div className="rp-ai-label">ISO 31000 Risk Statement</div>
                                <div className="rp-ai-statement">
                                    <p><strong>Risk:</strong> {aiResult.risk_statement}</p>
                                    <p><strong>Threat:</strong> {aiResult.threat}</p>
                                    <p><strong>Vulnerability:</strong> {aiResult.vulnerability}</p>
                                    <p><strong>Impact:</strong> {aiResult.impact}</p>
                                    <p><strong>Category:</strong> {aiResult.suggested_category} | <strong>L:</strong> {aiResult.suggested_likelihood} | <strong>I:</strong> {aiResult.suggested_impact}</p>
                                </div>
                            </>)}
                            {aiResult.type === 'treatment' && (<>
                                <div className="rp-ai-label">AI Treatment Recommendation</div>
                                <div className="rp-ai-statement">
                                    <p><strong>Strategy:</strong> {aiResult.recommended_strategy}</p>
                                    <p><strong>Plan:</strong> {aiResult.treatment_plan}</p>
                                    {aiResult.controls?.length > 0 && <p><strong>Controls:</strong> {aiResult.controls.join(', ')}</p>}
                                    <p><strong>Effectiveness:</strong> {aiResult.estimated_effectiveness}% | <strong>Cost:</strong> {aiResult.cost_estimate} | <strong>Timeline:</strong> {aiResult.timeline}</p>
                                </div>
                            </>)}
                            <button className="rp-btn-sm-ghost" onClick={() => setAiResult(null)} style={{ marginTop: 8 }}>Dismiss</button>
                        </div>
                    )}
                </div>

                {/* FAIR Quantification */}
                <div className="rp-detail-section">
                    <div className="rp-section-header">
                        <h3>Financial Quantification (FAIR)</h3>
                    </div>
                    {quantification ? (
                        <div>
                            <div className="rp-fair-grid">
                                <div className="rp-fair-card"><h4>Asset Value</h4><div className="rp-fair-value">${Number(quantification.asset_value || 0).toLocaleString()}</div></div>
                                <div className="rp-fair-card"><h4>Exposure Factor</h4><div className="rp-fair-value">{((quantification.exposure_factor || 0) * 100).toFixed(0)}%</div></div>
                                <div className="rp-fair-card"><h4>SLE</h4><div className="rp-fair-value">${Number(quantification.sle || 0).toLocaleString()}</div><div className="rp-assess-detail">Single Loss Expectancy</div></div>
                                <div className="rp-fair-card"><h4>ARO</h4><div className="rp-fair-value">{quantification.annual_rate_of_occurrence}x/yr</div></div>
                                <div className="rp-fair-card" style={{ borderLeft: '3px solid #D24646' }}><h4>ALE</h4><div className="rp-fair-value" style={{ color: '#D24646' }}>${Number(quantification.ale || 0).toLocaleString()}</div><div className="rp-assess-detail">Annual Loss Expectancy</div></div>
                                {quantification.roi != null && <div className="rp-fair-card"><h4>Treatment ROI</h4><div className="rp-fair-value" style={{ color: quantification.roi > 0 ? '#19A07E' : '#D24646' }}>{(quantification.roi * 100).toFixed(0)}%</div></div>}
                            </div>
                        </div>
                    ) : null}
                    {/* FAIR Edit Form */}
                    {(perms.can_assess || perms.can_configure) && (
                        <details className="rp-history" style={{ marginTop: 12 }}>
                            <summary>{quantification ? 'Edit Quantification' : 'Add Financial Quantification'}</summary>
                            <div className="rp-form-row" style={{ marginTop: 10 }}>
                                <div className="rp-form-group"><label>Asset Value ($)</label><input className="rp-input" type="number" defaultValue={quantification?.asset_value || ''} id="fair-av" /></div>
                                <div className="rp-form-group"><label>Exposure Factor (0-1)</label><input className="rp-input" type="number" step="0.01" min="0" max="1" defaultValue={quantification?.exposure_factor || 0.5} id="fair-ef" /></div>
                            </div>
                            <div className="rp-form-row">
                                <div className="rp-form-group"><label>Annual Rate of Occurrence</label><input className="rp-input" type="number" step="0.1" defaultValue={quantification?.annual_rate_of_occurrence || 1} id="fair-aro" /></div>
                                <div className="rp-form-group"><label>Treatment Cost ($)</label><input className="rp-input" type="number" defaultValue={quantification?.treatment_cost || ''} id="fair-tc" /></div>
                            </div>
                            <div className="rp-form-row">
                                <div className="rp-form-group"><label>Best Case Loss ($)</label><input className="rp-input" type="number" defaultValue={quantification?.best_case_loss || ''} id="fair-best" /></div>
                                <div className="rp-form-group"><label>Most Likely Loss ($)</label><input className="rp-input" type="number" defaultValue={quantification?.most_likely_loss || ''} id="fair-likely" /></div>
                                <div className="rp-form-group"><label>Worst Case Loss ($)</label><input className="rp-input" type="number" defaultValue={quantification?.worst_case_loss || ''} id="fair-worst" /></div>
                            </div>
                            <button className="rp-btn rp-btn-primary" style={{ marginTop: 8 }} onClick={async () => {
                                try {
                                    await risk_portal_save_quantification(r.id, {
                                        asset_value: document.getElementById('fair-av').value || 0,
                                        exposure_factor: document.getElementById('fair-ef').value || 0.5,
                                        annual_rate_of_occurrence: document.getElementById('fair-aro').value || 1,
                                        treatment_cost: document.getElementById('fair-tc').value || null,
                                        best_case_loss: document.getElementById('fair-best').value || null,
                                        most_likely_loss: document.getElementById('fair-likely').value || null,
                                        worst_case_loss: document.getElementById('fair-worst').value || null,
                                    });
                                    flash('Quantification saved');
                                    loadQuantification(r.id);
                                } catch (e) { flash('Save failed'); }
                            }}>Save Quantification</button>
                        </details>
                    )}

                    {/* Monte Carlo Results */}
                    {monteCarloResults && (
                        <div style={{ marginTop: 16 }}>
                            <h4 style={{ fontSize: 12, fontWeight: 600, marginBottom: 8 }}>Monte Carlo Simulation ({monteCarloResults.iterations?.toLocaleString()} iterations)</h4>
                            <div className="rp-fair-grid">
                                <div className="rp-fair-card"><h4>Mean Loss</h4><div className="rp-fair-value">${monteCarloResults.mean?.toLocaleString()}</div></div>
                                <div className="rp-fair-card"><h4>P90</h4><div className="rp-fair-value">${monteCarloResults.p90?.toLocaleString()}</div></div>
                                <div className="rp-fair-card"><h4>P95</h4><div className="rp-fair-value">${monteCarloResults.p95?.toLocaleString()}</div></div>
                                <div className="rp-fair-card"><h4>P99 (Worst)</h4><div className="rp-fair-value" style={{ color: '#D24646' }}>${monteCarloResults.p99?.toLocaleString()}</div></div>
                            </div>
                            {/* Histogram */}
                            {monteCarloResults.histogram && (
                                <div className="rp-mc-histogram">
                                    {monteCarloResults.histogram.map((count, i) => (
                                        <div key={i} className="rp-mc-bar-col">
                                            <div className="rp-mc-bar" style={{ height: `${Math.min(100, (count / Math.max(...monteCarloResults.histogram)) * 100)}%` }} title={`${count} events`} />
                                            <span className="rp-mc-bar-label">{monteCarloResults.histogram_labels?.[i]?.split('-')[0] || ''}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                    {quantification && !monteCarloResults && (
                        <button className="rp-btn rp-btn-primary" style={{ marginTop: 12 }} onClick={handleRunMonteCarlo} disabled={!!aiLoading}>
                            {aiLoading === 'montecarlo' ? 'Running...' : 'Run Monte Carlo (10K iterations)'}
                        </button>
                    )}
                </div>

                {/* Bow-Tie Analysis */}
                <div className="rp-detail-section">
                    <div className="rp-section-header">
                        <h3>Bow-Tie Analysis</h3>
                    </div>
                    {bowtieData ? (
                        <div className="rp-bowtie">
                            <div className="rp-bowtie-side">
                                <h4>Threats</h4>
                                {(bowtieData.threats || []).map((t, i) => <div key={i} className="rp-bowtie-item rp-bowtie-threat">{t.name}</div>)}
                                <h4 style={{ marginTop: 8 }}>Preventive Barriers</h4>
                                {(bowtieData.preventive_barriers || []).map((b, i) => (
                                    <div key={i} className="rp-bowtie-barrier"><span>{b.name}</span><span className="rp-score-badge" style={{ background: b.effectiveness >= 75 ? '#19A07E' : b.effectiveness >= 50 ? '#D9BF21' : '#D24646' }}>{b.effectiveness}%</span></div>
                                ))}
                            </div>
                            <div className="rp-bowtie-center">
                                <div className="rp-bowtie-event">{bowtieData.top_event}</div>
                            </div>
                            <div className="rp-bowtie-side">
                                <h4>Consequences</h4>
                                {(bowtieData.consequences || []).map((c, i) => <div key={i} className="rp-bowtie-item rp-bowtie-consequence">{c.name}</div>)}
                                <h4 style={{ marginTop: 8 }}>Mitigating Barriers</h4>
                                {(bowtieData.mitigating_barriers || []).map((b, i) => (
                                    <div key={i} className="rp-bowtie-barrier"><span>{b.name}</span><span className="rp-score-badge" style={{ background: b.effectiveness >= 75 ? '#19A07E' : b.effectiveness >= 50 ? '#D9BF21' : '#D24646' }}>{b.effectiveness}%</span></div>
                                ))}
                            </div>
                        </div>
                    ) : null}
                    {/* Bow-Tie Edit */}
                    {(perms.can_assess || perms.can_configure) && (
                        <details className="rp-history" style={{ marginTop: 12 }}>
                            <summary>{bowtieData ? 'Edit Bow-Tie' : 'Create Bow-Tie Analysis'}</summary>
                            <div style={{ marginTop: 10 }}>
                                <div className="rp-form-group"><label>Top Event</label><input className="rp-input" defaultValue={bowtieData?.top_event || r.risk?.substring(0, 100)} id="bt-event" /></div>
                                <div className="rp-form-group"><label>Threats (one per line)</label><textarea className="rp-textarea" rows={3} defaultValue={(bowtieData?.threats || []).map(t => t.name).join('\n')} id="bt-threats" placeholder="External attacker&#10;Insider threat&#10;System failure" /></div>
                                <div className="rp-form-group"><label>Preventive Barriers (one per line, format: name|effectiveness%)</label><textarea className="rp-textarea" rows={3} defaultValue={(bowtieData?.preventive_barriers || []).map(b => `${b.name}|${b.effectiveness}`).join('\n')} id="bt-prev" placeholder="Firewall|75&#10;Access Control|50" /></div>
                                <div className="rp-form-group"><label>Consequences (one per line)</label><textarea className="rp-textarea" rows={3} defaultValue={(bowtieData?.consequences || []).map(c => c.name).join('\n')} id="bt-consequences" placeholder="Data breach&#10;Financial loss&#10;Reputation damage" /></div>
                                <div className="rp-form-group"><label>Mitigating Barriers (one per line, format: name|effectiveness%)</label><textarea className="rp-textarea" rows={3} defaultValue={(bowtieData?.mitigating_barriers || []).map(b => `${b.name}|${b.effectiveness}`).join('\n')} id="bt-mit" placeholder="Incident Response|75&#10;Insurance|50" /></div>
                                <button className="rp-btn rp-btn-primary" onClick={async () => {
                                    const parseItems = (id) => document.getElementById(id).value.split('\n').filter(l => l.trim()).map((l, i) => ({ id: i + 1, name: l.trim() }));
                                    const parseBarriers = (id) => document.getElementById(id).value.split('\n').filter(l => l.trim()).map((l, i) => {
                                        const parts = l.split('|');
                                        return { id: i + 1, name: parts[0].trim(), effectiveness: parseInt(parts[1]) || 50, status: 'active' };
                                    });
                                    try {
                                        await risk_portal_save_bowtie(r.id, {
                                            top_event: document.getElementById('bt-event').value,
                                            threats: parseItems('bt-threats'),
                                            consequences: parseItems('bt-consequences'),
                                            preventive_barriers: parseBarriers('bt-prev'),
                                            mitigating_barriers: parseBarriers('bt-mit'),
                                        });
                                        flash('Bow-tie saved');
                                        loadBowtie(r.id);
                                    } catch (e) { flash('Save failed'); }
                                }}>Save Bow-Tie</button>
                            </div>
                        </details>
                    )}
                </div>

                {/* Activity Log */}
                {r.activity?.length > 0 && (
                    <div className="rp-detail-section">
                        <details className="rp-history">
                            <summary>Activity Log ({r.activity.length})</summary>
                            <div className="rp-activity-list">
                                {r.activity.map((a, i) => (
                                    <div key={i} className="rp-activity-item">
                                        <span className="rp-activity-action">{a.action}</span>
                                        <span className="rp-activity-detail">{a.detail}</span>
                                        <span className="rp-activity-time">{fmtTime(a.performed_at)}</span>
                                    </div>
                                ))}
                            </div>
                        </details>
                    </div>
                )}
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // KRI VIEW
    // ═══════════════════════════════════════════════
    function renderKRIs() {
        return (
            <div className="rp-view">
                <h2 className="rp-view-title">Key Risk Indicators</h2>
                <div className="rp-kpi-row">
                    <KpiCard label="Total KRIs" value={kriList.length} color="#6366f1" />
                    <KpiCard label="Normal" value={kriList.filter(k => k.status === 'normal').length} color="#19A07E" />
                    <KpiCard label="Warning" value={kriList.filter(k => k.status === 'warning').length} color="#D9BF21" />
                    <KpiCard label="Breach" value={kriList.filter(k => k.status === 'breach').length} color="#D24646" />
                </div>

                {/* KRI Cards with threshold bars */}
                <div className="rp-kri-grid-full">
                    {kriList.map(k => {
                        const maxVal = Math.max(k.current_value, k.threshold_breach || 0, k.threshold_warning || 0) * 1.2 || 100;
                        const valPct = Math.min(100, (k.current_value / maxVal) * 100);
                        const warnPct = k.threshold_warning ? Math.min(100, (k.threshold_warning / maxVal) * 100) : null;
                        const breachPct = k.threshold_breach ? Math.min(100, (k.threshold_breach / maxVal) * 100) : null;
                        const barColor = k.status === 'breach' ? '#C70017' : k.status === 'warning' ? '#c27803' : '#0f7b4f';
                        return (
                            <div key={k.id} className={`rp-kri-card-full rp-kri-${k.status}`}>
                                <div className="rp-kri-card-header">
                                    <div>
                                        <div className="rp-kri-name">{k.name}</div>
                                        <span className="rp-risk-id">{k.risk_id}</span>
                                    </div>
                                    <div className="rp-kri-card-actions">
                                        <button className="rp-btn-sm-ghost" onClick={() => { setEditingKri(k); setKriForm({ name: k.name, description: '', unit: k.unit, current_value: k.current_value, threshold_warning: k.threshold_warning ?? '', threshold_breach: k.threshold_breach ?? '' }); setShowKriEditModal(true); }}>Edit</button>
                                        <button className="rp-btn-sm-danger" onClick={() => handleDeleteKri(k.id)}>×</button>
                                    </div>
                                </div>
                                <div className="rp-kri-card-value-row">
                                    <span className="rp-kri-value">{k.current_value}</span>
                                    <span className="rp-kri-unit">{k.unit}</span>
                                    <span className={`rp-kri-status rp-kri-status-${k.status}`}>{k.status.toUpperCase()}</span>
                                </div>
                                {/* Threshold bar */}
                                <div className="rp-kri-bar-container">
                                    <div className="rp-kri-bar-track">
                                        <div className="rp-kri-bar-fill" style={{ width: `${valPct}%`, background: barColor }} />
                                        {warnPct && <div className="rp-kri-bar-marker rp-kri-bar-warn" style={{ left: `${warnPct}%` }} title={`Warning: ${k.threshold_warning}`} />}
                                        {breachPct && <div className="rp-kri-bar-marker rp-kri-bar-breach" style={{ left: `${breachPct}%` }} title={`Breach: ${k.threshold_breach}`} />}
                                    </div>
                                    <div className="rp-kri-bar-labels">
                                        <span>0</span>
                                        {k.threshold_warning && <span style={{ position: 'absolute', left: `${warnPct}%`, transform: 'translateX(-50%)', color: '#c27803', fontSize: '9px' }}>W:{k.threshold_warning}</span>}
                                        {k.threshold_breach && <span style={{ position: 'absolute', left: `${breachPct}%`, transform: 'translateX(-50%)', color: '#C70017', fontSize: '9px' }}>B:{k.threshold_breach}</span>}
                                        <span>{Math.round(maxVal)}</span>
                                    </div>
                                </div>
                                <div className="rp-kri-card-footer">{fmtTime(k.last_updated)}</div>
                            </div>
                        );
                    })}
                    {kriList.length === 0 && <p className="rp-empty-msg">No KRIs defined. Add KRIs from the Risk Detail view.</p>}
                </div>

                {/* KRI Edit Modal */}
                {showKriEditModal && editingKri && (
                    <div className="rp-modal-overlay" onClick={() => setShowKriEditModal(false)}>
                        <div className="rp-modal" onClick={e => e.stopPropagation()}>
                            <div className="rp-modal-header"><h3>Update KRI: {editingKri.name}</h3><button className="rp-modal-close" onClick={() => setShowKriEditModal(false)}>×</button></div>
                            <div className="rp-modal-body">
                                <div className="rp-form-group"><label>Current Value</label><input className="rp-input" type="number" step="any" value={kriForm.current_value} onChange={e => setKriForm(p => ({ ...p, current_value: e.target.value }))} /></div>
                                <div className="rp-form-row">
                                    <div className="rp-form-group"><label>Warning Threshold</label><input className="rp-input" type="number" step="any" value={kriForm.threshold_warning} onChange={e => setKriForm(p => ({ ...p, threshold_warning: e.target.value }))} /></div>
                                    <div className="rp-form-group"><label>Breach Threshold</label><input className="rp-input" type="number" step="any" value={kriForm.threshold_breach} onChange={e => setKriForm(p => ({ ...p, threshold_breach: e.target.value }))} /></div>
                                </div>
                                <div className="rp-form-group"><label>Unit</label><input className="rp-input" value={kriForm.unit} onChange={e => setKriForm(p => ({ ...p, unit: e.target.value }))} /></div>
                            </div>
                            <div className="rp-modal-footer">
                                <button className="rp-btn" onClick={() => setShowKriEditModal(false)}>Cancel</button>
                                <button className="rp-btn rp-btn-primary" onClick={handleUpdateKri}>Update KRI</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // TREATMENT LIST VIEW
    // ═══════════════════════════════════════════════
    function renderTreatments() {
        return (
            <div className="rp-view">
                <h2 className="rp-view-title">{view === 'my_treatments' ? 'My Treatment Plans' : 'Treatment Plans'}</h2>

                {/* Overdue Actions */}
                {overdueTreatments.length > 0 && (
                    <div className="rp-card">
                        <h3>OVERDUE ACTIONS <span className="rp-badge-danger">{overdueTreatments.length}</span></h3>
                        <table className="rp-table">
                            <thead><tr><th>Action</th><th>Risk</th><th>Strategy</th><th>Owner</th><th>Due</th><th>Days Overdue</th><th>Priority</th></tr></thead>
                            <tbody>
                                {overdueTreatments.map(a => (
                                    <tr key={a.id} className="rp-clickable" onClick={() => loadRiskDetail(a.risk_id)}>
                                        <td>{a.title}</td>
                                        <td><span className="rp-risk-id">{a.risk_id}</span></td>
                                        <td><span className="rp-type-badge">{a.strategy}</span></td>
                                        <td>{a.owner_email}</td>
                                        <td>{fmtDate(a.due_date)}</td>
                                        <td><span className="rp-overdue-days">{a.days_overdue}d</span></td>
                                        <td><span className={`rp-priority-badge rp-priority-${a.priority}`}>{a.priority}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {overdueTreatments.length === 0 && (
                    <div className="rp-card">
                        <h3>OVERDUE ACTIONS</h3>
                        <EmptyState
                            title="All caught up"
                            hint="No treatment actions are overdue. Navigate to any risk to manage its treatment plans."
                            action={<button className="rp-btn rp-btn-primary" onClick={() => setView('risks')}>View Risk Register</button>}
                        />
                    </div>
                )}
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // CATEGORIES VIEW
    // ═══════════════════════════════════════════════
    function renderCategories() {
        return (
            <div className="rp-view">
                <div className="rp-section-header">
                    <h2 className="rp-view-title">Risk Categories</h2>
                    <button className="rp-btn rp-btn-primary" onClick={() => { setCategoryForm({ name: '', description: '', color: '#6366f1' }); setShowCategoryModal(true); }}>Add Category</button>
                </div>
                <div className="rp-category-grid">
                    {categories.map(c => (
                        <div key={c.id} className="rp-info-tile">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                <div className="rp-info-tile-head">
                                    <span className="rp-info-tile-dot" style={{ background: c.color || 'var(--rp-text-3)' }} />
                                    {c.name}
                                </div>
                                {c.id && <button className="rp-btn-sm-danger" onClick={() => handleDeleteCategory(c.id)}>×</button>}
                            </div>
                            <div className="rp-info-tile-body">{c.description}</div>
                        </div>
                    ))}
                    {categories.length === 0 && (
                        <EmptyState
                            title="No categories yet"
                            hint="Add categories to organize your risk taxonomy (e.g. Cyber, Compliance, Operational)."
                        />
                    )}
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // APPETITE VIEW
    // ═══════════════════════════════════════════════
    function renderAppetite() {
        const perms = rc.permissions || {};
        return (
            <div className="rp-view">
                <h2 className="rp-view-title">Risk Appetite & Tolerance</h2>

                {/* Breach Alert Banner */}
                {appetiteBreaches.total_breaches > 0 && (
                    <div className="rp-breach-alert">
                        <span className="rp-breach-alert-icon">!</span>
                        <div>
                            <strong>{appetiteBreaches.total_breaches} risk{appetiteBreaches.total_breaches > 1 ? 's' : ''} exceeding appetite thresholds</strong>
                            <p>These risks require immediate attention or escalation.</p>
                        </div>
                    </div>
                )}

                {/* Appetite Thresholds */}
                <div className="rp-card">
                    <h3>TOLERANCE THRESHOLDS</h3>
                    <table className="rp-table">
                        <thead><tr><th>Category</th><th>Appetite Statement</th><th>Max Inherent</th><th>Max Residual</th><th>Max ALE</th>{perms.can_configure && <th></th>}</tr></thead>
                        <tbody>
                            {appetites.map(a => (
                                <tr key={a.id}>
                                    <td><strong>{a.category__name || 'Global'}</strong></td>
                                    <td>{a.appetite_statement || '-'}</td>
                                    <td><span className="rp-score-badge" style={{ background: '#f97316' }}>{a.max_inherent_score}</span></td>
                                    <td><span className="rp-score-badge" style={{ background: '#0f7b4f' }}>{a.max_residual_score}</span></td>
                                    <td>{a.max_aggregate_ale ? `$${Number(a.max_aggregate_ale).toLocaleString()}` : '-'}</td>
                                    {perms.can_configure && <td><button className="rp-btn-sm-danger" onClick={() => handleDeleteAppetite(a.id)}>x</button></td>}
                                </tr>
                            ))}
                            {appetites.length === 0 && <tr><td colSpan={perms.can_configure ? 6 : 5} className="rp-empty-cell">No appetite thresholds configured</td></tr>}
                        </tbody>
                    </table>
                    {perms.can_configure && (
                        <details className="rp-history" style={{ marginTop: 12 }}>
                            <summary>Add Appetite Threshold</summary>
                            <div className="rp-form-row" style={{ marginTop: 10 }}>
                                <div className="rp-form-group"><label>Category</label>
                                    <select className="rp-select" id="app-cat">{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}<option value="">Global</option></select>
                                </div>
                                <div className="rp-form-group"><label>Statement</label><input className="rp-input" id="app-stmt" placeholder="e.g. Low tolerance for financial risks" /></div>
                            </div>
                            <div className="rp-form-row">
                                <div className="rp-form-group"><label>Max Inherent Score</label><input className="rp-input" type="number" min="1" max="25" id="app-inh" defaultValue="15" /></div>
                                <div className="rp-form-group"><label>Max Residual Score</label><input className="rp-input" type="number" min="1" max="25" id="app-res" defaultValue="9" /></div>
                                <div className="rp-form-group"><label>Max ALE ($)</label><input className="rp-input" type="number" id="app-ale" /></div>
                            </div>
                            <button className="rp-btn rp-btn-primary" style={{ marginTop: 8 }} onClick={() => {
                                handleSaveAppetite({
                                    category_id: document.getElementById('app-cat').value || null,
                                    appetite_statement: document.getElementById('app-stmt').value,
                                    max_inherent_score: document.getElementById('app-inh').value || 15,
                                    max_residual_score: document.getElementById('app-res').value || 9,
                                    max_aggregate_ale: document.getElementById('app-ale').value || null,
                                });
                            }}>Save Threshold</button>
                        </details>
                    )}
                </div>

                {/* Breaches Table */}
                {appetiteBreaches.breaches?.length > 0 && (
                    <div className="rp-card">
                        <h3>RISKS EXCEEDING APPETITE</h3>
                        <table className="rp-table">
                            <thead><tr><th>Risk</th><th>Description</th><th>Type</th><th>Score</th><th>Threshold</th><th>Excess</th></tr></thead>
                            <tbody>
                                {appetiteBreaches.breaches.map((b, i) => (
                                    <tr key={i} className="rp-clickable" onClick={() => loadRiskDetail(b.risk_id)}>
                                        <td><span className="rp-risk-id">{b.risk_id}</span></td>
                                        <td>{b.risk}</td>
                                        <td><span className="rp-type-badge">{b.type}</span></td>
                                        <td><span className="rp-score-badge" style={{ background: '#C70017' }}>{b.score}</span></td>
                                        <td>{b.threshold}</td>
                                        <td><span className="rp-overdue-days">+{b.score - b.threshold}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // USERS VIEW
    // ═══════════════════════════════════════════════
    function renderUsers() {
        const canManage = role === 'risk_admin';

        const handleRoleChange = async (userId, newRole) => {
            await portalOp(async () => {
                await risk_portal_role_update(userId, { role: newRole });
                flash('Role updated');
                loadUsers();
            }, 'Role update requires portal backend.');
        };

        const handleStatusToggle = async (userId, isActive) => {
            await portalOp(async () => {
                await risk_portal_role_update(userId, { is_active: !isActive, status: !isActive ? 'active' : 'inactive' });
                flash(!isActive ? 'User activated' : 'User deactivated');
                loadUsers();
            }, 'Status update requires portal backend.');
        };

        const handleDelete = async (userId, email) => {
            if (!window.confirm(`Remove ${email} from Risk Registry roles?`)) return;
            await portalOp(async () => {
                await risk_portal_role_delete(userId);
                flash('Role removed');
                loadUsers();
            }, 'Role delete requires portal backend.');
        };

        const handleResendInvite = async (userId, email) => {
            await portalOp(async () => {
                const res = await risk_portal_role_resend_invite(userId);
                const d = res?.statusCode?.response_data || res?.data?.response_data || res?.statusCode || res?.data || {};
                flash(d?.message || `Invite re-sent to ${email}`);
            }, 'Invite resend requires portal backend.');
        };

        return (
            <div className="rp-view">
                <div className="rp-section-header">
                    <h2 className="rp-view-title">User Roles</h2>
                    {canManage && (
                        <button className="rp-btn rp-btn-primary" onClick={() => { setInviteForm({ email: '', first_name: '', last_name: '', role: 'risk_owner' }); setShowInviteModal(true); }}>Invite User</button>
                    )}
                </div>
                {!canManage && (
                    <Banner variant="warning">
                        Only <b>Risk Admin</b> can modify roles. You're viewing in read-only mode.
                    </Banner>
                )}
                <table className="rp-table">
                    <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Department</th><th>Status</th><th>Actions</th></tr></thead>
                    <tbody>
                        {portalUsers.map(u => (
                            <tr key={u.id}>
                                <td>{u.first_name} {u.last_name}</td>
                                <td>{u.email}</td>
                                <td>
                                    {canManage ? (
                                        <select className="rp-select" value={u.role} style={{ width: 160 }}
                                            onChange={(e) => handleRoleChange(u.id, e.target.value)}>
                                            <option value="risk_admin">Risk Admin</option>
                                            <option value="risk_manager">Risk Manager</option>
                                            <option value="risk_owner">Risk Owner</option>
                                            <option value="risk_reviewer">Risk Reviewer</option>
                                            <option value="business_owner">Business Owner</option>
                                            <option value="risk_committee">Risk Committee</option>
                                            <option value="ciso">CISO (Read-Only)</option>
                                        </select>
                                    ) : (
                                        <span className="rp-role-badge">{(u.role || '').replace(/_/g, ' ')}</span>
                                    )}
                                </td>
                                <td>{u.department || '-'}</td>
                                <td>
                                    <span className={`rp-status-badge rp-status-${u.status}`}>{u.status}</span>
                                </td>
                                <td>
                                    {canManage && (
                                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                            <button className="rp-btn rp-btn-sm" disabled={!u.is_active} title={u.is_active ? '' : 'Activate user first'} onClick={() => handleResendInvite(u.id, u.email)}>Resend Invite</button>
                                            <button className="rp-btn rp-btn-sm" onClick={() => handleStatusToggle(u.id, u.is_active !== false && u.status === 'active')}>
                                                {u.status === 'active' ? 'Deactivate' : 'Activate'}
                                            </button>
                                            <button className="rp-btn rp-btn-sm rp-btn-danger" onClick={() => handleDelete(u.id, u.email)}>Remove</button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {portalUsers.length === 0 && <tr><td colSpan={6} className="rp-empty-cell">No users assigned. Click <b>Invite User</b> to add one.</td></tr>}
                    </tbody>
                </table>
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // ROLE AUDIT LOG - immutable history of role grants/changes/revocations
    // ═══════════════════════════════════════════════
    function renderAuditLog() {
        const ACTION_COLORS = {
            grant: '#059669', modify: '#0ea5e9', revoke: '#dc2626',
            deactivate: '#a16207', reactivate: '#16a34a',
        };
        const fmt = (iso) => {
            try { return new Date(iso).toLocaleString(); } catch { return iso; }
        };
        const renderDiff = (before, after) => {
            const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
            const changed = [...keys].filter(k => JSON.stringify(before?.[k]) !== JSON.stringify(after?.[k]));
            if (changed.length === 0) return <span style={{ color: '#94a3b8', fontSize: 11 }}>-</span>;
            return (
                <div style={{ fontSize: 11, color: '#475569', lineHeight: 1.5 }}>
                    {changed.map(k => (
                        <div key={k}><b>{k}:</b> {String(before?.[k] ?? '∅')} -> {String(after?.[k] ?? '∅')}</div>
                    ))}
                </div>
            );
        };
        return (
            <div className="rp-view">
                <div className="rp-section-header">
                    <h2 className="rp-view-title">Role Audit Log</h2>
                    <button className="rp-btn" onClick={loadRoleAuditLog}>Refresh</button>
                </div>
                <p style={{ color: '#475569', marginTop: -8, marginBottom: 12, fontSize: 13 }}>
                    Immutable history of all role grants, changes, deactivations and revocations. Required by SOX and ISO 27001 A.9 controls.
                </p>
                <div className="rp-card">
                    <table className="rp-table">
                        <thead>
                            <tr>
                                <th style={{ width: 160 }}>When</th>
                                <th style={{ width: 100 }}>Action</th>
                                <th>Target</th>
                                <th>Role</th>
                                <th>Actor</th>
                                <th>Change</th>
                            </tr>
                        </thead>
                        <tbody>
                            {roleAuditLog.map(e => (
                                <tr key={e.id}>
                                    <td style={{ fontSize: 12 }}>{fmt(e.created_at)}</td>
                                    <td>
                                        <span style={{
                                            display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11,
                                            fontWeight: 600, color: '#fff', background: ACTION_COLORS[e.action] || '#64748b',
                                        }}>{e.action}</span>
                                    </td>
                                    <td>{e.target_email}</td>
                                    <td><span className="rp-role-badge">{(e.target_role || '').replace(/_/g, ' ')}</span></td>
                                    <td style={{ fontSize: 12 }}>{e.changed_by_email || '-'}</td>
                                    <td>{renderDiff(e.before_state, e.after_state)}</td>
                                </tr>
                            ))}
                            {roleAuditLog.length === 0 && (
                                <tr><td colSpan={6} className="rp-empty-cell">No role-change events recorded yet.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // BUSINESS UNITS - full CRUD + hierarchical tree
    // ═══════════════════════════════════════════════
    function renderBusinessUnits() {
        const canManage = role === 'risk_admin' || role === 'risk_manager';
        const TIER_COLORS = { low: '#16a34a', medium: '#d97706', high: '#dc2626', critical: '#7f1d1d' };

        // Build a map id -> BU and a tree by parent_id
        const byId = Object.fromEntries(businessUnits.map(bu => [bu.id, bu]));
        const childrenOf = {};
        businessUnits.forEach(bu => {
            const p = bu.parent_id || 'root';
            if (!childrenOf[p]) childrenOf[p] = [];
            childrenOf[p].push(bu);
        });

        // Lookup counts from the breakdown response
        const countsById = Object.fromEntries((buBreakdown.business_units || []).map(b => [b.id, b]));
        const orgCounts = buBreakdown.organization_level?.counts || {};

        const handleSubmitBu = async () => {
            const payload = {
                name: buForm.name.trim(),
                code: buForm.code.trim(),
                location: buForm.location.trim(),
                risk_tier: buForm.risk_tier,
                parent_id: buForm.parent_id ? parseInt(buForm.parent_id) : null,
            };
            if (!payload.name) { flash('Name is required'); return; }
            await portalOp(async () => {
                if (buForm.id) {
                    await risk_portal_bu_update(buForm.id, payload);
                    flash('BU updated');
                } else {
                    await risk_portal_bu_create(payload);
                    flash('BU created');
                }
                setShowBuModal(false);
                loadBusinessUnits();
                loadBuBreakdown();
            }, 'BU save requires portal backend.');
        };

        const handleDeleteBu = async (bu) => {
            if (!window.confirm(`Delete BU "${bu.name}"? Cannot be undone if no risks/children depend on it.`)) return;
            await portalOp(async () => {
                await risk_portal_bu_delete(bu.id);
                flash('BU deleted');
                loadBusinessUnits();
                loadBuBreakdown();
            }, 'BU delete requires portal backend.');
        };

        const handleToggleActive = async (bu) => {
            await portalOp(async () => {
                await risk_portal_bu_update(bu.id, { is_active: !bu.is_active });
                flash(bu.is_active ? 'BU deactivated' : 'BU activated');
                loadBusinessUnits();
            }, 'BU update requires portal backend.');
        };

        const renderRow = (bu, depth) => {
            const counts = countsById[bu.id]?.counts_with_descendants || { total: 0, critical: 0, high: 0, medium: 0, low: 0, open: 0 };
            const direct = countsById[bu.id]?.counts_direct || { total: 0 };
            return (
                <React.Fragment key={bu.id}>
                    <tr style={{ opacity: bu.is_active ? 1 : 0.55 }}>
                        <td>
                            <span style={{ display: 'inline-block', paddingLeft: depth * 18 }}>
                                {depth > 0 && <span style={{ color: '#94a3b8', marginRight: 6 }}>↳</span>}
                                <b>{bu.name}</b>
                                {bu.code && <span style={{ color: '#64748b', marginLeft: 6, fontSize: 11 }}>({bu.code})</span>}
                            </span>
                        </td>
                        <td>{bu.location || '-'}</td>
                        <td>
                            <span style={{ display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, color: '#fff', background: TIER_COLORS[bu.risk_tier] || '#64748b' }}>
                                {bu.risk_tier}
                            </span>
                        </td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            <b>{counts.total}</b>
                            <span style={{ color: '#94a3b8', fontSize: 11, marginLeft: 4 }}>({direct.total} direct)</span>
                        </td>
                        <td style={{ textAlign: 'right', fontSize: 12 }}>
                            <span style={{ color: '#dc2626' }}>{counts.critical} crit</span> /
                            <span style={{ color: '#ea580c', marginLeft: 4 }}>{counts.high} high</span> /
                            <span style={{ color: '#ca8a04', marginLeft: 4 }}>{counts.medium} med</span> /
                            <span style={{ color: '#16a34a', marginLeft: 4 }}>{counts.low} low</span>
                        </td>
                        <td>
                            <span className={`rp-status-badge rp-status-${bu.is_active ? 'active' : 'inactive'}`}>
                                {bu.is_active ? 'active' : 'inactive'}
                            </span>
                        </td>
                        <td>
                            {canManage && (
                                <div style={{ display: 'flex', gap: 4 }}>
                                    <button className="rp-btn rp-btn-sm" onClick={() => {
                                        setBuForm({
                                            id: bu.id, name: bu.name, code: bu.code,
                                            location: bu.location, parent_id: bu.parent_id || '',
                                            risk_tier: bu.risk_tier,
                                        });
                                        setShowBuModal(true);
                                    }}>Edit</button>
                                    <button className="rp-btn rp-btn-sm" onClick={() => handleToggleActive(bu)}>
                                        {bu.is_active ? 'Deactivate' : 'Activate'}
                                    </button>
                                    <button className="rp-btn rp-btn-sm rp-btn-danger" onClick={() => handleDeleteBu(bu)}>Delete</button>
                                </div>
                            )}
                        </td>
                    </tr>
                    {(childrenOf[bu.id] || []).map(c => renderRow(c, depth + 1))}
                </React.Fragment>
            );
        };

        return (
            <div className="rp-view">
                <div className="rp-section-header">
                    <h2 className="rp-view-title">Business Units</h2>
                    {canManage && (
                        <button className="rp-btn rp-btn-primary" onClick={() => {
                            setBuForm({ id: null, name: '', code: '', location: '', parent_id: '', risk_tier: 'medium' });
                            setShowBuModal(true);
                        }}>Add Business Unit</button>
                    )}
                </div>
                <p style={{ color: '#475569', marginTop: -8, marginBottom: 12, fontSize: 13 }}>
                    Hierarchical structure: organization -> business units -> sub-units. Risks link to a BU; subtree filtering rolls counts up to the parent.
                </p>

                {/* Org-level summary */}
                <div className="rp-card" style={{ marginBottom: 12, background: '#f8fafc' }}>
                    <h3 style={{ marginBottom: 6 }}>ORGANIZATION TOTAL</h3>
                    <div style={{ display: 'flex', gap: 24, alignItems: 'baseline' }}>
                        <div><b style={{ fontSize: 22 }}>{(buBreakdown.business_units || []).reduce((acc, b) => acc + (b.counts_direct?.total || 0), 0) + (orgCounts.total || 0)}</b> <span style={{ color: '#64748b', fontSize: 12 }}>total risks</span></div>
                        <div style={{ color: '#64748b', fontSize: 12 }}>
                            Org-level only (no BU): <b>{orgCounts.total || 0}</b> ·
                            Across {businessUnits.length} BU{businessUnits.length === 1 ? '' : 's'}: <b>{(buBreakdown.business_units || []).reduce((acc, b) => acc + (b.counts_direct?.total || 0), 0)}</b>
                        </div>
                    </div>
                </div>

                <div className="rp-card">
                    <table className="rp-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Location</th>
                                <th>Risk Tier</th>
                                <th style={{ textAlign: 'right' }}>Risks (subtree)</th>
                                <th style={{ textAlign: 'right' }}>By Severity</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(childrenOf['root'] || []).map(bu => renderRow(bu, 0))}
                            {businessUnits.length === 0 && (
                                <tr><td colSpan={7} className="rp-empty-cell">No business units yet. Click <b>Add Business Unit</b> to create the first.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* BU modal */}
                {showBuModal && (
                    <div className="rp-modal-backdrop">
                        <div className="rp-modal">
                            <div className="rp-modal-header">{buForm.id ? 'Edit Business Unit' : 'Add Business Unit'}</div>
                            <div className="rp-modal-body">
                                <p style={{ fontSize: 12, color: 'var(--rp-text-3)', margin: '0 0 14px' }}>
                                    Only <b>Name</b> is required. Everything else is optional and can be filled in later.
                                </p>
                                <div className="rp-form-grid-2">
                                    <div className="rp-form-group">
                                        <label>Name *</label>
                                        <input className="rp-input" value={buForm.name} onChange={e => setBuForm(p => ({ ...p, name: e.target.value }))} />
                                    </div>
                                    <div className="rp-form-group">
                                        <label>Code <span style={{ fontWeight: 400, color: 'var(--rp-text-3)', fontSize: 11 }}>(auto from name if blank)</span></label>
                                        <input className="rp-input"
                                            value={buForm.code}
                                            onChange={e => {
                                                // Live-normalise: uppercase, allow only A-Z 0-9 -, collapse separators
                                                const v = e.target.value
                                                    .toUpperCase()
                                                    .replace(/[\s_./\\]+/g, '-')
                                                    .replace(/[^A-Z0-9-]/g, '')
                                                    .replace(/-{2,}/g, '-')
                                                    .slice(0, 10);
                                                setBuForm(p => ({ ...p, code: v }));
                                            }}
                                            placeholder="e.g. ENG, OPS-IN, FIN1"
                                            style={{ fontFamily: 'var(--rp-font-mono)', textTransform: 'uppercase' }}
                                            maxLength={10} />
                                        <div style={{ fontSize: 11, color: 'var(--rp-text-3)', marginTop: 4 }}>
                                            2-10 chars · A-Z, 0-9, single hyphens · must start with a letter
                                        </div>
                                    </div>
                                </div>
                                <div className="rp-form-grid-2">
                                    <div className="rp-form-group">
                                        <label>Parent BU <span style={{ fontWeight: 400, color: 'var(--rp-text-3)', fontSize: 11 }}>(optional)</span></label>
                                        <select className="rp-select" value={buForm.parent_id || ''} onChange={e => setBuForm(p => ({ ...p, parent_id: e.target.value }))}>
                                            <option value="">(top-level)</option>
                                            {businessUnits.filter(b => b.id !== buForm.id).map(b => (
                                                <option key={b.id} value={b.id}>{b.name}{b.code ? ` (${b.code})` : ''}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="rp-form-group">
                                        <label>Risk Tier</label>
                                        <select className="rp-select" value={buForm.risk_tier} onChange={e => setBuForm(p => ({ ...p, risk_tier: e.target.value }))}>
                                            <option value="low">Low</option>
                                            <option value="medium">Medium</option>
                                            <option value="high">High</option>
                                            <option value="critical">Critical</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="rp-form-group">
                                    <label>Location <span style={{ fontWeight: 400, color: 'var(--rp-text-3)', fontSize: 11 }}>(optional)</span></label>
                                    <input className="rp-input" value={buForm.location} onChange={e => setBuForm(p => ({ ...p, location: e.target.value }))} placeholder="e.g. Bangalore, EMEA" />
                                </div>
                            </div>
                            <div className="rp-modal-footer">
                                <button className="rp-btn" onClick={() => setShowBuModal(false)}>Cancel</button>
                                <button className="rp-btn rp-btn-primary" onClick={handleSubmitBu}>{buForm.id ? 'Save' : 'Create'}</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // PROJECTS - CRUD page
    // ═══════════════════════════════════════════════
    function renderProjects() {
        const canManage = role === 'risk_admin' || role === 'risk_manager';
        const projectMap = Object.fromEntries(projects.map(p => [p.id, p]));
        const buMap = Object.fromEntries(businessUnits.map(b => [b.id, b]));
        const breakdownById = Object.fromEntries((projectBreakdown.projects || []).map(p => [p.id, p]));

        const handleSubmitProject = async () => {
            const payload = {
                name: (projectForm.name || '').trim(),
                code: (projectForm.code || '').trim(),
                project_type: projectForm.project_type || 'medium',
                project_lifecycle: projectForm.project_lifecycle || 'standard',
                location: (projectForm.location || '').trim(),
                project_manager: (projectForm.project_manager || '').trim(),
                business_unit_id: projectForm.business_unit_id ? parseInt(projectForm.business_unit_id) : null,
            };
            if (!payload.name) { flash('Project name is required', 'error'); return; }
            await portalOp(async () => {
                if (projectForm.id) {
                    await risk_portal_project_update(projectForm.id, payload);
                    flash(`Project "${payload.name}" updated`, 'success');
                } else {
                    await risk_portal_project_create(payload);
                    flash(`Project "${payload.name}" created`, 'success');
                }
                setShowProjectModal(false);
                loadProjects();
                loadProjectBreakdown();
            }, 'Project save requires portal backend.');
        };

        const handleDeleteProject = async (proj) => {
            if (!window.confirm(`Delete project "${proj.name}"? This cannot be undone if no risks reference it.`)) return;
            await portalOp(async () => {
                await risk_portal_project_delete(proj.id);
                flash(`Project "${proj.name}" deleted`, 'success');
                loadProjects();
                loadProjectBreakdown();
            }, 'Project delete requires portal backend.');
        };

        const openAdd = () => {
            setProjectForm({
                id: null, name: '', code: '', project_type: 'medium', project_lifecycle: 'standard',
                business_unit_id: '', location: '', project_manager: ''
            });
            setShowProjectModal(true);
        };
        const openEdit = (p) => {
            setProjectForm({
                id: p.id, name: p.name || '', code: p.code || '',
                project_type: p.project_type || 'medium',
                project_lifecycle: p.project_lifecycle || 'standard',
                business_unit_id: p.business_unit_id || '',
                location: p.location || '',
                project_manager: p.project_manager || '',
            });
            setShowProjectModal(true);
        };

        return (
            <div className="rp-view">
                <div className="rp-section-header">
                    <h2 className="rp-view-title">Projects</h2>
                    {canManage && (
                        <button className="rp-btn rp-btn-primary" onClick={openAdd}>Add Project</button>
                    )}
                </div>
                <p style={{ color: '#475569', marginTop: -8, marginBottom: 12, fontSize: 13 }}>
                    Projects let you slice risks by initiative, programme, or product. Each project belongs to a Business Unit; risks then carry both BU and Project for executive reporting.
                </p>

                <div className="rp-card">
                    <table className="rp-table">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Code</th>
                                <th>Type</th>
                                <th>Business Unit</th>
                                <th>Manager</th>
                                <th>Location</th>
                                <th style={{ textAlign: 'right' }}>Risks</th>
                                <th style={{ textAlign: 'right' }}>By Severity</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projects.length === 0 && (
                                <tr><td colSpan={10} className="rp-empty-cell">No projects yet. Click <b>Add Project</b> to create the first.</td></tr>
                            )}
                            {projects.map(p => {
                                const bd = breakdownById[p.id]?.counts || { total: 0, critical: 0, high: 0, medium: 0, low: 0 };
                                const buName = p.business_unit || (p.business_unit_id ? (buMap[p.business_unit_id]?.name || '-') : '-');
                                return (
                                    <tr key={p.id} style={{ opacity: p.is_active === false ? 0.55 : 1 }}>
                                        <td><b>{p.name}</b></td>
                                        <td style={{ color: '#64748b', fontSize: 12 }}>{p.code || '-'}</td>
                                        <td style={{ textTransform: 'capitalize' }}>{p.project_type || '-'}</td>
                                        <td>{buName}</td>
                                        <td>{p.project_manager || '-'}</td>
                                        <td>{p.location || '-'}</td>
                                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}><b>{bd.total}</b></td>
                                        <td style={{ textAlign: 'right', fontSize: 12 }}>
                                            <span style={{ color: '#dc2626' }}>{bd.critical} crit</span> /
                                            <span style={{ color: '#ea580c', marginLeft: 4 }}>{bd.high} high</span> /
                                            <span style={{ color: '#ca8a04', marginLeft: 4 }}>{bd.medium} med</span> /
                                            <span style={{ color: '#16a34a', marginLeft: 4 }}>{bd.low} low</span>
                                        </td>
                                        <td>
                                            <span className={`rp-status-badge rp-status-${p.is_active === false ? 'inactive' : 'active'}`}>
                                                {p.is_active === false ? 'inactive' : 'active'}
                                            </span>
                                        </td>
                                        <td>
                                            {canManage && (
                                                <div style={{ display: 'flex', gap: 4 }}>
                                                    <button className="rp-btn rp-btn-sm" onClick={() => openEdit(p)}>Edit</button>
                                                    <button className="rp-btn rp-btn-sm rp-btn-danger" onClick={() => handleDeleteProject(p)}>Delete</button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {showProjectModal && (
                    <div className="rp-modal-backdrop">
                        <div className="rp-modal">
                            <div className="rp-modal-header">{projectForm.id ? 'Edit Project' : 'Add Project'}</div>
                            <div className="rp-modal-body">
                                <p style={{ fontSize: 12, color: 'var(--rp-text-3)', margin: '0 0 14px' }}>
                                    Only <b>Name</b> is required. Everything else is optional and can be filled in later.
                                </p>
                                <div className="rp-form-grid-2">
                                    <div className="rp-form-group">
                                        <label>Name *</label>
                                        <input className="rp-input" value={projectForm.name}
                                            onChange={e => setProjectForm(p => ({ ...p, name: e.target.value }))} />
                                    </div>
                                    <div className="rp-form-group">
                                        <label>Code <span style={{ fontWeight: 400, color: 'var(--rp-text-3)', fontSize: 11 }}>(optional)</span></label>
                                        <input className="rp-input" value={projectForm.code}
                                            onChange={e => setProjectForm(p => ({ ...p, code: e.target.value }))}
                                            placeholder="auto-generated if blank" />
                                    </div>
                                </div>
                                <div className="rp-form-grid-2">
                                    <div className="rp-form-group">
                                        <label>Project Type</label>
                                        <select className="rp-select" value={projectForm.project_type}
                                            onChange={e => setProjectForm(p => ({ ...p, project_type: e.target.value }))}>
                                            <option value="small">Small</option>
                                            <option value="medium">Medium</option>
                                            <option value="large">Large</option>
                                            <option value="critical">Critical</option>
                                            <option value="ongoing">Ongoing / BAU</option>
                                        </select>
                                    </div>
                                    <div className="rp-form-group">
                                        <label>Lifecycle</label>
                                        <select className="rp-select" value={projectForm.project_lifecycle}
                                            onChange={e => setProjectForm(p => ({ ...p, project_lifecycle: e.target.value }))}>
                                            <option value="standard">Standard</option>
                                            <option value="agile">Agile</option>
                                            <option value="hybrid">Hybrid</option>
                                            <option value="small_project">Small Project</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="rp-form-grid-2">
                                    <div className="rp-form-group">
                                        <label>Business Unit</label>
                                        <select className="rp-select" value={projectForm.business_unit_id || ''}
                                            onChange={e => setProjectForm(p => ({ ...p, business_unit_id: e.target.value }))}>
                                            <option value="">(default - first BU)</option>
                                            {businessUnits.map(b => (
                                                <option key={b.id} value={b.id}>{b.name}{b.code ? ` (${b.code})` : ''}</option>
                                            ))}
                                        </select>
                                    </div>
                                    <div className="rp-form-group">
                                        <label>Project Manager</label>
                                        <input className="rp-input" value={projectForm.project_manager}
                                            onChange={e => setProjectForm(p => ({ ...p, project_manager: e.target.value }))}
                                            placeholder="name or email" />
                                    </div>
                                </div>
                                <div className="rp-form-group">
                                    <label>Location <span style={{ fontWeight: 400, color: 'var(--rp-text-3)', fontSize: 11 }}>(optional)</span></label>
                                    <input className="rp-input" value={projectForm.location}
                                        onChange={e => setProjectForm(p => ({ ...p, location: e.target.value }))} />
                                </div>
                            </div>
                            <div className="rp-modal-footer">
                                <button className="rp-btn" onClick={() => setShowProjectModal(false)}>Cancel</button>
                                <button className="rp-btn rp-btn-primary" onClick={handleSubmitProject}>{projectForm.id ? 'Save' : 'Create'}</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // RBAC GUIDE - roles, stages, capability matrix
    // ═══════════════════════════════════════════════
    function renderGuide() {
        const ROLES = [
            {
                key: 'risk_admin', label: 'Risk Admin', color: '#a855f7',
                summary: 'Configures the Risk Registry. Owns workflows, taxonomies, and role assignments.',
                responsibilities: ['Configure workflow stages, SLAs, categories, and risk appetite', 'Assign and revoke risk roles for team members', 'Override stalled stages when normal RACI cannot proceed', 'Maintain compliance framework mappings']
            },
            {
                key: 'risk_manager', label: 'Risk Manager', color: '#1b2a4a',
                summary: 'Identifies and assesses risks, then hands off to owners for treatment.',
                responsibilities: ['Create and document new risks', 'Run inherent + residual assessments and KRIs', 'Re-open monitored risks when conditions change', 'Coordinate periodic reviews']
            },
            {
                key: 'risk_owner', label: 'Risk Owner', color: '#d97706',
                summary: 'Accountable individual for a risk. Designs and executes the treatment plan.',
                responsibilities: ['Define treatment strategy (mitigate / transfer / accept / avoid)', 'Plan controls, target dates, and residual targets', 'Submit treatment plans for review', 'Track action progress and evidence']
            },
            {
                key: 'risk_reviewer', label: 'Risk Reviewer', color: '#0ea5e9',
                summary: 'Validates that treatment plans are credible before they go to acceptance.',
                responsibilities: ['Review treatment plans submitted by owners', 'Approve or send plans back for revision', 'Verify control coverage against the risk', 'Flag plans that miss residual targets']
            },
            {
                key: 'business_owner', label: 'Business Owner', color: '#059669',
                summary: 'Accepts low and medium residual risk on behalf of the business unit.',
                responsibilities: ['Accept residual risk for low / medium severity', 'Acknowledge BU exposure and appetite breaches', 'Sponsor controls funded from the BU budget']
            },
            {
                key: 'risk_committee', label: 'Risk Committee', color: '#be123c',
                summary: 'Tiered escalation. Accepts high and critical residual risk that exceeds BU authority.',
                responsibilities: ['Accept residual risk for high / critical severity', 'Review aggregated portfolio exposure', 'Authorise breaches above appetite thresholds', 'Report material risks to the board']
            },
            {
                key: 'ciso', label: 'CISO (Read-Only)', color: '#7c3aed',
                summary: 'Cross-BU executive oversight. Read-only on every risk, KRI, and audit log entry.',
                responsibilities: ['Monitor enterprise risk posture across BUs', 'Review the role audit log for control assurance', 'Brief the board on aggregate exposure', 'Has no stage-advancement authority']
            },
        ];

        // Build stage list, overlaying SLA from the org's saved workflow config
        // when present so the Guide reflects the live customized values.
        const liveSla = (key) => {
            const cfgStage = (wfConfig?.stages || []).find(s => s.id === key);
            return cfgStage && cfgStage.sla_days != null ? Number(cfgStage.sla_days) : null;
        };
        const stageBlueprint = [
            {
                key: 'identification', name: 'Identification', owner: 'risk_manager', defaultSla: 3, color: '#6366f1',
                purpose: 'Capture a new risk with enough context to be assessable.',
                entry: 'A risk is registered (manually, from a scan, or imported). The system auto-emails the assigned owner with a single-use acknowledgement link.',
                exit: 'A complete risk record exists with title, threat, vulnerability, and owner. Owner acknowledgement runs in parallel.'
            },
            {
                key: 'assessment', name: 'Assessment', owner: 'risk_manager', defaultSla: 5, color: '#0ea5e9',
                purpose: 'Score inherent likelihood × impact and capture KRIs.',
                entry: 'Risk has all identification fields populated.',
                exit: 'A current RiskAssessment record exists for the risk.'
            },
            {
                key: 'treatment', name: 'Treatment', owner: 'risk_owner', defaultSla: 10, color: '#d97706',
                purpose: 'The owner designs a plan to bring residual risk into appetite.',
                entry: 'Risk has been assessed.',
                exit: 'At least one RiskTreatmentPlan exists for the risk.'
            },
            {
                key: 'review', name: 'Review', owner: 'risk_reviewer', defaultSla: 5, color: '#a855f7',
                purpose: 'Independent reviewer verifies the treatment plan is credible.',
                entry: 'A treatment plan was submitted.',
                exit: 'All treatment plans for the risk are marked verified.'
            },
            {
                key: 'acceptance', name: 'Acceptance', owner: 'business_owner', defaultSla: 5, color: '#059669',
                purpose: 'Residual exposure is formally accepted (severity-tiered: BU owner for low/medium, Risk Committee for high/critical).',
                entry: 'All treatment plans verified.',
                exit: 'Approver signs off; risk moves to monitoring.'
            },
            {
                key: 'monitoring', name: 'Monitoring', owner: 'risk_manager', defaultSla: null, color: '#1b2a4a',
                purpose: 'Watch the risk against KRIs and triggers; re-assess if conditions change.',
                entry: 'Risk accepted.',
                exit: 'Closure conditions met, or re-assessment kicks risk back to assessment.'
            },
            {
                key: 'closed', name: 'Closed', owner: null, defaultSla: null, color: '#8492a6',
                purpose: 'Terminal state. The risk has materialised, been fully mitigated, or is no longer relevant. Closed by Risk Manager (or Risk Admin override) after all closure conditions are satisfied (treatment actions verified, residual ≤ appetite, KRIs normal, acceptance recorded).',
                entry: 'Risk Manager invokes Close; system validates blockers. All stakeholders (owner, creator, acceptor) are notified.',
                exit: 'No further actions; record kept for audit.'
            },
        ];
        const STAGES = stageBlueprint.map(s => {
            const live = liveSla(s.key);
            return { ...s, sla: live != null ? live : s.defaultSla, slaSource: live != null ? 'configured' : 'default' };
        });

        const CAPABILITIES = [
            { action: 'Configure workflow / appetite / categories', risk_admin: true, risk_manager: false, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: false },
            { action: 'Manage role assignments', risk_admin: true, risk_manager: false, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: false },
            { action: 'View role audit log', risk_admin: true, risk_manager: false, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: true },
            { action: 'Create / identify a risk', risk_admin: true, risk_manager: true, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: false },
            { action: 'Assess inherent + residual', risk_admin: true, risk_manager: true, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: false },
            { action: 'Author treatment plan', risk_admin: true, risk_manager: false, risk_owner: true, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: false },
            { action: 'Approve / reject treatment plan', risk_admin: true, risk_manager: false, risk_owner: false, risk_reviewer: true, business_owner: false, risk_committee: false, ciso: false },
            { action: 'Accept residual risk (low / medium)', risk_admin: true, risk_manager: false, risk_owner: false, risk_reviewer: false, business_owner: true, risk_committee: true, ciso: false },
            { action: 'Accept residual risk (high / critical)', risk_admin: true, risk_manager: false, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: true, ciso: false },
            { action: 'Close monitored risk (all conditions met)', risk_admin: true, risk_manager: true, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: false },
            { action: 'Force-close (override blockers)', risk_admin: true, risk_manager: false, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: false },
            { action: 'Re-open monitored risk (back to assessment)', risk_admin: true, risk_manager: true, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: false },
            { action: 'Override stalled stage / SoD bypass', risk_admin: true, risk_manager: false, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: false },
            { action: 'Manage Business Units (CRUD)', risk_admin: true, risk_manager: true, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: false },
            { action: 'Resend owner-acknowledgement invite', risk_admin: true, risk_manager: true, risk_owner: false, risk_reviewer: false, business_owner: false, risk_committee: false, ciso: false },
            { action: 'Read all risks, KRIs, KPIs', risk_admin: true, risk_manager: true, risk_owner: true, risk_reviewer: true, business_owner: true, risk_committee: true, ciso: true },
        ];

        const ROLE_BY_KEY = Object.fromEntries(ROLES.map(r => [r.key, r]));

        const cellStyle = (yes) => ({
            textAlign: 'center',
            fontWeight: yes ? 600 : 400,
            color: yes ? 'var(--rp-text)' : 'var(--rp-text-3)',
            background: 'transparent',
        });

        return (
            <div className="rp-view">
                <h2 className="rp-view-title">Risk Registry: Roles, Stages & Capabilities</h2>
                <p style={{ color: '#475569', marginTop: -8, marginBottom: 16, fontSize: 13 }}>
                    A reference for who does what in the Risk Registry workflow. Every risk moves through the pipeline below; each stage has a single responsible role.
                </p>

                {/* PIPELINE - neutral numbered stepper (Atlassian/ServiceNow style).
                    This is a reference diagram, not an active risk's progress, so every
                    step is intentionally identical. Colour shows up only on a live risk's
                    Stage Progress Bar (done=green, current=brand-blue, future=grey). */}
                <div className="rp-card" style={{ marginBottom: 20 }}>
                    <h3>WORKFLOW PIPELINE</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '12px 0' }}>
                        {STAGES.map((s, i) => (
                            <React.Fragment key={s.key}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 120 }}>
                                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--rp-surface)', border: '1.5px solid var(--rp-border-2)', color: 'var(--rp-text-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13 }}>{i + 1}</div>
                                    <div style={{ fontSize: 13, fontWeight: 500, marginTop: 8, color: 'var(--rp-text)' }}>{s.name}</div>
                                    <div style={{ fontSize: 11, color: 'var(--rp-text-3)', marginTop: 2 }}>{s.owner ? ROLE_BY_KEY[s.owner]?.label : '-'}</div>
                                    {s.sla && <div style={{ fontSize: 11, color: 'var(--rp-text-3)' }}>SLA {s.sla}d</div>}
                                </div>
                                {i < STAGES.length - 1 && <div style={{ width: 24, height: 1, background: 'var(--rp-border)' }} />}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* STAGE DETAILS */}
                <div className="rp-card" style={{ marginBottom: 20 }}>
                    <h3>STAGE DETAILS</h3>
                    <table className="rp-table">
                        <thead><tr><th style={{ width: 140 }}>Stage</th><th>Purpose</th><th>Owner</th><th>SLA</th><th>Entry</th><th>Exit</th></tr></thead>
                        <tbody>
                            {STAGES.map((s, i) => (
                                <tr key={s.key}>
                                    <td><span style={{ color: 'var(--rp-text-3)', fontFamily: 'var(--rp-font-mono)', fontSize: 12, marginRight: 8 }}>{String(i + 1).padStart(2, '0')}</span>{s.name}</td>
                                    <td>{s.purpose}</td>
                                    <td>{s.owner ? <span className="rp-role-badge">{ROLE_BY_KEY[s.owner].label}</span> : '-'}</td>
                                    <td>{s.sla ? `${s.sla} days` : '-'}</td>
                                    <td style={{ fontSize: 12, color: '#475569' }}>{s.entry}</td>
                                    <td style={{ fontSize: 12, color: '#475569' }}>{s.exit}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* ROLES */}
                <div className="rp-card" style={{ marginBottom: 20 }}>
                    <h3>ROLES</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                        {ROLES.map(r => (
                            <div key={r.key} className="rp-info-tile">
                                <div className="rp-info-tile-head">
                                    <span className="rp-info-tile-dot" style={{ background: r.color }} />
                                    {r.label}
                                </div>
                                <div className="rp-info-tile-body">
                                    {r.summary}
                                    <ul>
                                        {r.responsibilities.map((t, i) => <li key={i}>{t}</li>)}
                                    </ul>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* ENTERPRISE CONTROLS */}
                <div className="rp-card" style={{ marginBottom: 20 }}>
                    <h3>ENTERPRISE CONTROLS</h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#be123c' }} />Tiered Acceptance</div>
                            <div className="rp-info-tile-body">
                                Severity routes the sign-off automatically:
                                <ul>
                                    <li><b>Low / Medium</b> &rarr; Business Owner</li>
                                    <li><b>High / Critical</b> &rarr; Risk Committee</li>
                                </ul>
                                Each role's acceptance queue is on the dashboard ("Open Acceptance Queue").
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#1d4ed8' }} />Segregation of Duties (SoD)</div>
                            <div className="rp-info-tile-body">
                                At configured SoD stages (default: <code>review</code>), the acting user cannot also be:
                                <ul>
                                    <li>the registered <b>risk owner</b></li>
                                    <li>the <b>risk creator</b></li>
                                    <li>the linked project's <b>project manager</b></li>
                                </ul>
                                Stages where SoD applies are configurable in Settings &rarr; Customisation.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#b45309' }} />Time-Bounded Roles</div>
                            <div className="rp-info-tile-body">
                                Each role grant carries optional <code>valid_from</code> / <code>valid_until</code> dates. Outside the window the role is treated as inactive - useful for vacation cover, contractors, and project-bounded grants.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#15803d' }} />Immutable Audit Logs</div>
                            <div className="rp-info-tile-body">
                                <b>Two audit trails</b>, both append-only:
                                <ul>
                                    <li><b>Role audit log</b> - every grant / modify / revoke with before-and-after snapshots</li>
                                    <li><b>Risk field-change log</b> - every mutation to likelihood, impact, severity, owner, state, etc., with the actor's email</li>
                                </ul>
                                Meets SOX and ISO 27001 A.9 evidence requirements.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#7c3aed' }} />Owner Acknowledgement</div>
                            <div className="rp-info-tile-body">
                                On every new risk with a designated owner, the system mints a single-use share token (default 30-day TTL, configurable) and emails the owner a public link. Once they click <b>Acknowledge</b> or <b>Decline</b>, the token is consumed and the creator is notified.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#0891b2' }} />Hierarchical Business Units &amp; Projects</div>
                            <div className="rp-info-tile-body">
                                Risks attach to a Business Unit (which nests into a tree) and optionally to a Project. The dashboard scope filter lets any executive role drill down by BU subtree or specific Project - every KPI, chart, and queue recomputes against the scope.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#c026d3' }} />Closure Conditions</div>
                            <div className="rp-info-tile-body">
                                A risk can be closed only when ALL of:
                                <ul>
                                    <li>Every treatment action is completed/verified</li>
                                    <li>Residual score &le; appetite threshold</li>
                                    <li>All KRIs in normal status</li>
                                    <li>Risk has been formally accepted (when an acceptance stage exists)</li>
                                </ul>
                                Only <b>Risk Admin</b> can force-close with overrides. The closer role itself is configurable per-stage.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#ea580c' }} />Notifications &amp; SLA Escalation</div>
                            <div className="rp-info-tile-body">
                                Every stage advance emails next-stage role holders ("Action required") and the risk owner (FYI). Approvals, rejections and closures notify all stakeholders. The system also detects SLA breaches per stage and dispatches escalation emails up the role chain.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#dc2626' }} />Inherent vs Residual Risk</div>
                            <div className="rp-info-tile-body">
                                Each risk carries an <b>inherent</b> rating (pre-control, captured once at creation, immutable) and a <b>residual</b> rating (current likelihood &times; impact, updated as treatments take effect). Both are visible side-by-side on the risk detail page; reports show treatment effectiveness as the delta.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#6366f1' }} />Workflow Validation on Save</div>
                            <div className="rp-info-tile-body">
                                Saving the workflow runs 15+ governance checks: type-vs-name mismatches, chicken-and-egg required-artifact loops, missing reject-back paths, missing reviewer (SoD gap), unrealistically tight Approval SLAs, etc. <b>Errors block the save</b>; warnings can be reviewed and dismissed.
                            </div>
                        </div>
                    </div>
                </div>

                {/* PER-ORG CUSTOMISATION */}
                <div className="rp-card" style={{ marginBottom: 20 }}>
                    <h3>PER-ORG CUSTOMISATION</h3>
                    <p style={{ fontSize: 12, color: 'var(--rp-text-3)', margin: '0 0 12px' }}>
                        Everything below lives in <b>Settings &rarr; Customisation</b> (Risk Admin only).
                        Underlying system values stay stable for reports and integrations; only the
                        display / behaviour layer changes per customer.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#0052cc' }} />Workflow stages</div>
                            <div className="rp-info-tile-body">
                                Add, rename, reorder, or remove stages. Set per-stage owner role, SLA, type (form / assessment / treatment / review / approval / monitoring / closed), next-stage transitions, and required-artifact gates (treatment plan, evidence, KRI, controls, owner ack, etc.).
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#0891b2' }} />State labels</div>
                            <div className="rp-info-tile-body">
                                Rename the 5 underlying states (<code>open / in_progress / accepted / closed / transferred</code>) for display, e.g. <b>open</b> &rarr; "Identified", <b>closed</b> &rarr; "Resolved". The system value never changes, so reports / integrations stay consistent.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#b91c1c' }} />Severity colours</div>
                            <div className="rp-info-tile-body">
                                Override the colours for Low / Medium / High / Critical badges, posture bar, heatmap legend, and chart segments. Useful for brand alignment or accessibility palettes (e.g. deuteranopia-safe colours). Defaults are ISO 31000 industry-standard.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#db2777' }} />Risk type routing</div>
                            <div className="rp-info-tile-body">
                                Map specific risk types to specific approver roles, e.g. <i>cybersecurity &rarr; CISO</i>, <i>compliance &rarr; Risk Committee</i>. Applied at review/treatment/approve stages; severity-tiered acceptance still wins at the acceptance stage.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#be123c' }} />Severity thresholds &amp; scale</div>
                            <div className="rp-info-tile-body">
                                Adjust the score boundaries that map a likelihood &times; impact value to Low / Medium / High / Critical. Scale max can be configured (default 5 = ISO 5&times;5 matrix; range 2-10).
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#7c3aed' }} />Role display labels</div>
                            <div className="rp-info-tile-body">
                                Rename the 7 system roles for the customer's vocabulary (e.g. "Risk Manager" &rarr; "Risk Champion"). The role permissions and audit log stay keyed off the system role; only the UI label changes.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#15803d' }} />Email branding</div>
                            <div className="rp-info-tile-body">
                                Custom from-address, brand name, and signature appended to every notification email. Defaults to the platform brand if blank.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#f59e0b' }} />SoD enforcement stages</div>
                            <div className="rp-info-tile-body">
                                Pick which stages enforce Segregation of Duties (default: <code>review</code> only). Toggle by clicking the stage chip in Settings.
                            </div>
                        </div>
                        <div className="rp-info-tile">
                            <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#ea580c' }} />Business Unit code format</div>
                            <div className="rp-info-tile-body">
                                BU codes follow a strict standard (2-10 chars, A-Z 0-9 hyphens, starts with a letter). Live-normalised as the user types and validated server-side. Auto-generates an acronym from the BU name when left blank.
                            </div>
                        </div>
                    </div>
                </div>

                {/* CAPABILITY MATRIX */}
                <div className="rp-card">
                    <h3>CAPABILITY MATRIX</h3>
                    <table className="rp-table">
                        <thead>
                            <tr>
                                <th>Action</th>
                                {ROLES.map(r => <th key={r.key} style={{ textAlign: 'center' }}>{r.label}</th>)}
                            </tr>
                        </thead>
                        <tbody>
                            {CAPABILITIES.map((c, i) => (
                                <tr key={i}>
                                    <td>{c.action}</td>
                                    {ROLES.map(r => (
                                        <td key={r.key} style={cellStyle(c[r.key])}>{c[r.key] ? '✓' : '-'}</td>
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    <p style={{ fontSize: 11, color: '#64748b', marginTop: 8, marginBottom: 0 }}>
                        ✓ = role can perform the action. <b>Risk Admin</b> can act on any stage as an override; the other roles are gated to the stage they own.
                    </p>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // PENDING REVIEWS (for risk_reviewer)
    // ═══════════════════════════════════════════════
    function renderPendingReviews() {
        return (
            <div className="rp-view">
                <h2 className="rp-view-title">Pending Reviews</h2>
                {pendingTreatments.length > 0 ? (
                    <div className="rp-card">
                        <h3>TREATMENT PLANS AWAITING APPROVAL <span className="rp-badge-danger">{pendingTreatments.length}</span></h3>
                        <table className="rp-table">
                            <thead><tr><th>Risk</th><th>Description</th><th>Strategy</th><th>Created</th><th>Actions</th></tr></thead>
                            <tbody>
                                {pendingTreatments.map(tp => (
                                    <tr key={tp.id}>
                                        <td><span className="rp-risk-id rp-clickable" onClick={() => loadRiskDetail(tp.risk_id)}>{tp.risk_id}</span></td>
                                        <td>{tp.description}</td>
                                        <td><span className="rp-type-badge">{tp.strategy}</span></td>
                                        <td>{fmtDate(tp.created_at)}</td>
                                        <td>
                                            <div style={{ display: 'flex', gap: 4 }}>
                                                <button className="rp-btn-approve" onClick={() => handleApproveTreatment(tp.id)}>Approve</button>
                                                <button className="rp-btn-reject" onClick={() => handleRejectTreatment(tp.id)}>Reject</button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="rp-card">
                        <p className="rp-empty-msg">No treatment plans pending review.</p>
                    </div>
                )}
                <button className="rp-btn" onClick={() => setView('risks')} style={{ marginTop: 12 }}>View All Risks</button>
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // PENDING ACCEPTANCE VIEW (Business Owner / Risk Committee)
    // Shows risks at the acceptance stage that the current user can sign off.
    // Severity-tiered: BO sees low/medium, Committee sees high/critical.
    // ═══════════════════════════════════════════════
    function renderPendingAcceptance() {
        // Pull from the dashboard's top_risks + my_risks pools and the risk list,
        // filtering to "at acceptance and within my severity tier". This avoids
        // an extra endpoint and stays in sync with everything else.
        const allCandidates = [
            ...(dashData?.top_risks || []),
            ...(dashData?.my_risks || []),
            ...(risks?.results || []),
        ];
        const seen = new Set();
        const dedup = [];
        for (const r of allCandidates) {
            const key = r.risk_id || r.id;
            if (!key || seen.has(key)) continue;
            seen.add(key); dedup.push(r);
        }
        // Severity tier per role: BO = low/medium, Committee = high/critical, Admin/CISO = all.
        const tier = role === 'business_owner' ? ['low', 'medium']
            : role === 'risk_committee' ? ['high', 'critical']
                : ['low', 'medium', 'high', 'critical'];
        const tierSet = new Set(tier);
        // The risk has to be active, severity within tier, and at the acceptance stage
        // (state in_progress is the post-review pre-accept window in the standard flow).
        const queue = dedup.filter(r => {
            const sev = (r.residual_severity || r.severity || (r.inherent_rating || '').toLowerCase()).toLowerCase();
            const st = (r.state || '').toLowerCase();
            return st !== 'closed' && st !== 'transferred' && tierSet.has(sev);
        });

        const tierLabel = role === 'business_owner' ? 'low / medium severity'
            : role === 'risk_committee' ? 'high / critical severity'
                : 'all severities';
        const headline = role === 'business_owner' ? 'Risks Awaiting Your Acceptance (Business Owner)'
            : role === 'risk_committee' ? 'Risks Awaiting Committee Acceptance'
                : 'Pending Acceptance Queue';

        return (
            <div className="rp-view">
                <div className="rp-section-header" style={{ marginBottom: 12 }}>
                    <h2 className="rp-view-title" style={{ marginBottom: 0 }}>{headline}</h2>
                    <button className="rp-btn" onClick={() => setView('risks')}>View Risk Register</button>
                </div>
                <Banner variant="info">
                    These risks have been treated and reviewed; you (or someone with your role) need to formally
                    accept the residual exposure on behalf of the business. Tier filter: <b>{tierLabel}</b>.
                    Only risks where treatment is verified and the workflow is at the acceptance stage are shown.
                </Banner>
                {queue.length === 0 ? (
                    <div className="rp-card">
                        <EmptyState
                            title="Nothing to accept right now"
                            hint="Risks reach this queue once their treatment plans are verified by a Reviewer. Check back as the team progresses risks through the workflow."
                            action={<button className="rp-btn" onClick={() => setView('dashboard')}>Back to Dashboard</button>}
                        />
                    </div>
                ) : (
                    <div className="rp-card">
                        <h3>QUEUE <span style={{ fontSize: 11, color: 'var(--rp-text-3)', fontWeight: 400 }}>({queue.length})</span></h3>
                        <table className="rp-table">
                            <thead>
                                <tr>
                                    <th>Risk ID</th><th>Description</th><th>Type</th>
                                    <th>Severity</th><th>Score</th><th>Owner</th>
                                </tr>
                            </thead>
                            <tbody>
                                {queue.map(r => {
                                    const rating = normalizeRating(r.inherent_rating || r.severity);
                                    return (
                                        <tr key={r.risk_id || r.id} className="rp-clickable"
                                            onClick={() => loadRiskDetail(r.id || r.risk_id)}>
                                            <td><span className="rp-risk-id">{r.risk_id}</span></td>
                                            <td>{r.risk}</td>
                                            <td><span className="rp-type-badge">{(r.risk_type || '').replace(/_/g, ' ') || '-'}</span></td>
                                            <td><span className="rp-rating" style={{ color: ratingColor(rating) }}>{rating}</span></td>
                                            <td><span className="rp-score-badge" style={{ background: ratingColor(rating) }}>{r.inherent_score ?? r.residual_score ?? r.risk_score ?? '-'}</span></td>
                                            <td>{r.risk_owner || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // SETTINGS / WORKFLOW VIEW
    // ═══════════════════════════════════════════════
    function renderSettings() {
        const stages = wfConfig?.stages || [];
        // Org-customisable stage type colours override the defaults
        const STAGE_TYPE_COLORS = { form: '#6366f1', assessment: '#0ea5e9', treatment: '#d97706', review: '#a855f7', approval: '#059669', monitoring: '#1b2a4a', closed: '#8492a6', ...(orgSettings?.stage_type_colors || {}) };
        // Org-customisable role labels override the built-in display names
        const customLabels = orgSettings?.role_labels || {};
        const builtin = { risk_admin: 'Risk Admin', risk_manager: 'Risk Manager', risk_owner: 'Risk Owner', risk_reviewer: 'Risk Reviewer', business_owner: 'Business Owner', risk_committee: 'Risk Committee', ciso: 'CISO' };
        const ROLE_LABELS = Object.fromEntries(Object.entries(builtin).map(([k, v]) => [k, (customLabels[k] && customLabels[k].trim()) || v]));
        const canEditWorkflow = role === 'risk_admin';

        // Local edit-state - every change is immediate visually; persisted on Save
        const updateStageField = (stageId, field, rawValue) => {
            setWfConfig(prev => {
                const next = { ...(prev || {}) };
                next.stages = (prev?.stages || []).map(s => {
                    if (s.id !== stageId) return s;
                    let value = rawValue;
                    if (field === 'sla_days') {
                        if (rawValue === '' || rawValue === null) value = null;
                        else {
                            const n = parseInt(rawValue, 10);
                            value = Number.isFinite(n) && n > 0 ? n : null;
                        }
                    }
                    return { ...s, [field]: value };
                });
                return next;
            });
        };

        const moveStage = (stageId, dir) => {
            setWfConfig(prev => {
                const list = [...(prev?.stages || [])];
                const i = list.findIndex(s => s.id === stageId);
                const j = i + dir;
                if (i < 0 || j < 0 || j >= list.length) return prev;
                [list[i], list[j]] = [list[j], list[i]];
                list.forEach((s, idx) => { s.order = idx + 1; });
                return { ...(prev || {}), stages: list };
            });
        };

        const deleteStage = (stageId) => {
            const target = (wfConfig?.stages || []).find(s => s.id === stageId);
            if (!target) return;
            if (!window.confirm(`Delete stage "${target.name}"? Other stages that point to this one will reroute to End.`)) return;
            setWfConfig(prev => {
                const remaining = (prev?.stages || []).filter(s => s.id !== stageId);
                // Strip the deleted ID from any next_stages references
                remaining.forEach(s => {
                    if (Array.isArray(s.next_stages)) {
                        s.next_stages = s.next_stages.filter(n => n !== stageId);
                    }
                });
                return { ...(prev || {}), stages: remaining };
            });
        };

        const addStage = () => {
            const newId = `stage_${Date.now().toString(36)}`;
            const newStage = {
                id: newId, name: 'New Stage', order: (wfConfig?.stages?.length || 0) + 1,
                stage_type: 'form', owner_role: 'risk_manager', next_stages: [], sla_days: 5,
            };
            setWfConfig(prev => ({ ...(prev || {}), stages: [...(prev?.stages || []), newStage] }));
        };

        const handleSeedDefaults = async () => {
            try {
                const res = await risk_portal_seed_defaults();
                const d = extract(res);
                flash(`Seeded: ${d?.categories_created || 0} categories, ${d?.appetites_created || 0} appetites, workflow: ${d?.workflow_created ? 'created' : 'exists'}`);
                loadCategories();
                loadAppetite();
            } catch (e) { flash('Seed failed: ' + (e?.response?.data?.error_msg || e.message)); }
        };

        return (
            <div className="rp-view">
                <div className="rp-section-header" style={{ marginBottom: 16 }}>
                    <h2 className="rp-view-title" style={{ marginBottom: 0 }}>{view === 'workflow' ? 'Workflow Configuration' : 'Portal Settings'}</h2>
                    <div style={{ display: 'flex', gap: 8 }}>
                        {canEditWorkflow && <button className="rp-btn rp-btn-primary" onClick={handleSeedDefaults}>Seed Default Data</button>}
                        {canEditWorkflow && <button className="rp-btn rp-btn-primary" onClick={() => handleSaveWorkflow({ stages: wfConfig?.stages, role_config: wfConfig?.role_config })}>Save Workflow</button>}
                    </div>
                </div>
                {canEditWorkflow && (
                    <Banner variant="info">
                        Customise the entire workflow: rename stages, change owner roles, edit SLAs, reroute transitions, and add or remove stages. Click <b>Save Workflow</b> to persist. Changes apply to new transitions; risks already in a stage keep their existing deadlines and stage state.
                    </Banner>
                )}
                {!canEditWorkflow && (
                    <Banner variant="warning">
                        Read-only. Only <b>Risk Admin</b> can edit the workflow configuration.
                    </Banner>
                )}

                {/* Visual Pipeline - neutral numbered stepper to match the Guide diagram */}
                <div className="rp-card">
                    <h3>RISK LIFECYCLE PIPELINE</h3>
                    <div className="rp-pipeline">
                        {stages.map((s, i) => (
                            <React.Fragment key={s.id}>
                                <div className="rp-pipeline-node">
                                    <div className="rp-pipeline-dot" style={{ background: 'var(--rp-surface)', border: '1.5px solid var(--rp-border-2)', color: 'var(--rp-text-2)', boxShadow: 'none', fontWeight: 600 }}>
                                        {i + 1}
                                    </div>
                                    <div className="rp-pipeline-label">{s.name}</div>
                                </div>
                                {i < stages.length - 1 && <div className="rp-pipeline-connector" />}
                            </React.Fragment>
                        ))}
                    </div>
                </div>

                {/* Stage Detail Cards - fully editable */}
                <div className="rp-card">
                    <div className="rp-section-header" style={{ marginBottom: 8 }}>
                        <h3 style={{ marginBottom: 0 }}>STAGE CONFIGURATION</h3>
                        {canEditWorkflow && <button className="rp-btn rp-btn-primary" onClick={addStage}>+ Add Stage</button>}
                    </div>
                    <div className="rp-stage-grid">
                        {stages.map((s, i) => {
                            const ownerLabel = ROLE_LABELS[s.owner_role] || s.owner_role || 'System';
                            const inputCss = { width: '100%', padding: '4px 6px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4 };
                            return (
                                <div key={s.id} className="rp-stage-card" style={{ borderTopColor: 'var(--rp-border)' }}>
                                    <div className="rp-stage-card-header">
                                        <span className="rp-stage-num" style={{ background: 'var(--rp-surface-3)', color: 'var(--rp-text-2)', border: '1px solid var(--rp-border)' }}>{i + 1}</span>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            {canEditWorkflow ? (
                                                <>
                                                    <input type="text" value={s.name || ''}
                                                        onChange={(e) => updateStageField(s.id, 'name', e.target.value)}
                                                        style={{ ...inputCss, fontWeight: 700, marginBottom: 4 }} />
                                                    <select value={s.stage_type || 'form'}
                                                        onChange={(e) => updateStageField(s.id, 'stage_type', e.target.value)}
                                                        style={{ ...inputCss, fontSize: 11, color: 'var(--rp-text-2)' }}>
                                                        <option value="form">form</option>
                                                        <option value="assessment">assessment</option>
                                                        <option value="treatment">treatment</option>
                                                        <option value="review">review</option>
                                                        <option value="approval">approval</option>
                                                        <option value="monitoring">monitoring</option>
                                                        <option value="closed">closed</option>
                                                    </select>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="rp-stage-card-name">{s.name}</div>
                                                    <span className="rp-stage-card-type" style={{ color: 'var(--rp-text-3)', background: 'var(--rp-surface-2)', border: '1px solid var(--rp-border)' }}>{s.stage_type}</span>
                                                </>
                                            )}
                                        </div>
                                        {canEditWorkflow && (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                <button title="Move up" onClick={() => moveStage(s.id, -1)} disabled={i === 0}
                                                    style={{ background: 'transparent', border: 0, cursor: i === 0 ? 'not-allowed' : 'pointer', color: i === 0 ? '#cbd5e1' : '#64748b', padding: 2 }}>▲</button>
                                                <button title="Move down" onClick={() => moveStage(s.id, 1)} disabled={i === stages.length - 1}
                                                    style={{ background: 'transparent', border: 0, cursor: i === stages.length - 1 ? 'not-allowed' : 'pointer', color: i === stages.length - 1 ? '#cbd5e1' : '#64748b', padding: 2 }}>▼</button>
                                            </div>
                                        )}
                                    </div>
                                    <div className="rp-stage-card-body">
                                        <div className="rp-stage-card-row">
                                            <span className="rp-stage-card-label">Owner</span>
                                            {canEditWorkflow ? (
                                                <select value={s.owner_role || ''}
                                                    onChange={(e) => updateStageField(s.id, 'owner_role', e.target.value || null)}
                                                    style={{ ...inputCss, width: 'auto', minWidth: 140 }}>
                                                    <option value="">System / no owner</option>
                                                    {Object.entries(ROLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                                                </select>
                                            ) : (
                                                <span className="rp-stage-card-value">{ownerLabel}</span>
                                            )}
                                        </div>
                                        <div className="rp-stage-card-row">
                                            <span className="rp-stage-card-label">SLA</span>
                                            {canEditWorkflow ? (
                                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                                    <input type="number" min="0"
                                                        placeholder={s.stage_type === 'monitoring' || s.stage_type === 'closed' ? 'no SLA' : 'days'}
                                                        value={s.sla_days ?? ''}
                                                        onChange={(e) => updateStageField(s.id, 'sla_days', e.target.value)}
                                                        style={{ width: 70, padding: '3px 6px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4, textAlign: 'right' }} />
                                                    <span style={{ fontSize: 11, color: '#64748b' }}>{s.sla_days ? 'days' : ''}</span>
                                                </span>
                                            ) : (
                                                <span className="rp-stage-card-sla">{s.sla_days ? `${s.sla_days} days` : '-'}</span>
                                            )}
                                        </div>
                                        <div className="rp-stage-card-row">
                                            <span className="rp-stage-card-label">Next</span>
                                            {canEditWorkflow ? (
                                                <select value={(s.next_stages || [])[0] || ''}
                                                    onChange={(e) => updateStageField(s.id, 'next_stages', e.target.value ? [e.target.value] : [])}
                                                    style={{ ...inputCss, width: 'auto', minWidth: 140 }}>
                                                    <option value="">End (terminal)</option>
                                                    {stages.filter(x => x.id !== s.id).map(x => (
                                                        <option key={x.id} value={x.id}>{x.name || x.id}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <span className="rp-stage-card-value">{s.next_stages?.[0] ? stages.find(x => x.id === s.next_stages[0])?.name || s.next_stages[0] : 'End'}</span>
                                            )}
                                        </div>
                                        {/* Required artifacts - what must exist before a risk can advance INTO this stage.
                                            When unset, defaults are inferred from stage_type. */}
                                        <div className="rp-stage-card-row" style={{ alignItems: 'flex-start', flexDirection: 'column', gap: 4 }}>
                                            <span className="rp-stage-card-label" title="What must be present on the risk before it can advance INTO this stage. Defaults are inferred from the stage type if you leave this empty.">Required</span>
                                            {canEditWorkflow ? (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                                    {[
                                                        { key: 'assessment', label: 'Assessment' },
                                                        { key: 'treatment_plan', label: 'Treatment Plan' },
                                                        { key: 'verified_treatment', label: 'Verified Plan' },
                                                        { key: 'evidence', label: 'Evidence' },
                                                        { key: 'kri', label: 'KRI' },
                                                        { key: 'controls', label: 'Controls' },
                                                        { key: 'owner', label: 'Owner Set' },
                                                        { key: 'acknowledgement', label: 'Owner Ack' },
                                                    ].map(opt => {
                                                        const list = Array.isArray(s.required_artifacts) ? s.required_artifacts : [];
                                                        const on = list.includes(opt.key);
                                                        return (
                                                            <button key={opt.key} type="button"
                                                                onClick={() => {
                                                                    const next = on
                                                                        ? list.filter(x => x !== opt.key)
                                                                        : [...list, opt.key];
                                                                    updateStageField(s.id, 'required_artifacts', next);
                                                                }}
                                                                style={{
                                                                    fontSize: 10, padding: '2px 8px', borderRadius: 10,
                                                                    cursor: 'pointer',
                                                                    border: `1px solid ${on ? 'var(--rp-accent)' : 'var(--rp-border)'}`,
                                                                    background: on ? 'var(--rp-accent-light)' : '#fff',
                                                                    color: on ? 'var(--rp-accent)' : 'var(--rp-text-3)',
                                                                    fontWeight: on ? 600 : 400,
                                                                }}>
                                                                {on ? '✓ ' : ''}{opt.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <span className="rp-stage-card-value" style={{ fontSize: 11 }}>
                                                    {(s.required_artifacts || []).length
                                                        ? (s.required_artifacts || []).join(', ')
                                                        : <i style={{ color: 'var(--rp-text-3)' }}>by stage type</i>}
                                                </span>
                                            )}
                                        </div>
                                        {canEditWorkflow && (
                                            <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e2e8f0', display: 'flex', justifyContent: 'flex-end' }}>
                                                <button className="rp-btn rp-btn-sm rp-btn-danger" onClick={() => deleteStage(s.id)} title="Delete this stage">Delete Stage</button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Role Summary - editable permissions matrix */}
                <div className="rp-card">
                    <div className="rp-section-header" style={{ marginBottom: 8 }}>
                        <h3 style={{ marginBottom: 0 }}>ROLE RESPONSIBILITIES</h3>
                        {canEditWorkflow && <span style={{ fontSize: 11, color: '#64748b' }}>Click any permission to toggle. Save Workflow to persist.</span>}
                    </div>
                    {(() => {
                        const PERM_KEYS = ['can_configure', 'can_assess', 'can_treat', 'can_approve'];
                        const togglePerm = (roleKey, permKey) => {
                            setWfConfig(prev => {
                                const rc2 = { ...((prev || {}).role_config || {}) };
                                const cur = (rc2[roleKey] && rc2[roleKey].permissions) || (DEFAULT_ROLE_CONFIGS[roleKey] && DEFAULT_ROLE_CONFIGS[roleKey].permissions) || {};
                                const nextPerms = { ...cur, [permKey]: !cur[permKey] };
                                rc2[roleKey] = { ...(rc2[roleKey] || {}), permissions: nextPerms };
                                return { ...(prev || {}), role_config: rc2 };
                            });
                        };
                        return (
                            <table className="rp-table">
                                <thead>
                                    <tr><th>Role</th><th>Stages Owned</th><th>Permissions</th></tr>
                                </thead>
                                <tbody>
                                    {Object.entries(ROLE_LABELS).map(([key, label]) => {
                                        const owned = stages.filter(s => s.owner_role === key).map(s => s.name);
                                        const cfgPerms = wfConfig?.role_config?.[key]?.permissions;
                                        const perms = cfgPerms || DEFAULT_ROLE_CONFIGS[key]?.permissions || {};
                                        return (
                                            <tr key={key}>
                                                <td><span className="rp-role-badge">{label}</span></td>
                                                <td>{owned.length > 0 ? owned.join(', ') : <span style={{ color: 'var(--rp-text-3)' }}>-</span>}</td>
                                                <td>
                                                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                        {PERM_KEYS.map(pk => {
                                                            const label = pk.replace('can_', '');
                                                            const on = !!perms[pk];
                                                            if (!canEditWorkflow) {
                                                                return on ? <span key={pk} className="rp-perm-tag">{label}</span> : null;
                                                            }
                                                            return (
                                                                <button key={pk} type="button" onClick={() => togglePerm(key, pk)}
                                                                    title={`Click to ${on ? 'revoke' : 'grant'} ${label}`}
                                                                    style={{
                                                                        fontSize: 11, padding: '2px 8px', borderRadius: 12, cursor: 'pointer',
                                                                        border: `1px solid ${on ? '#10b981' : '#cbd5e1'}`,
                                                                        background: on ? '#ecfdf5' : '#ffffff',
                                                                        color: on ? '#065f46' : '#94a3b8',
                                                                        fontWeight: on ? 600 : 400,
                                                                    }}>
                                                                    {on ? '✓ ' : ''}{label}
                                                                </button>
                                                            );
                                                        })}
                                                        {!canEditWorkflow && Object.values(perms).every(v => !v) && <span style={{ color: 'var(--rp-text-3)' }}>View only</span>}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        );
                    })()}
                </div>

                {/* Customisation Card - covers SoD stages, email branding, severity, scale, AI keywords, role labels, stage colours */}
                {renderCustomisationCard(canEditWorkflow)}
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // CUSTOMISATION CARD - exposes per-org overrides
    // ═══════════════════════════════════════════════
    function renderCustomisationCard(canEdit) {
        const settings = orgSettings || {};
        const eb = settings.email_branding || {};
        const th = settings.severity_thresholds || [4, 9, 15];
        const sm = settings.scale_max || 5;
        const sodStages = settings.sod_stages || ['review'];
        const allStageIds = (wfConfig?.stages || []).map(s => s.id);
        const roleLabels = settings.role_labels || {};
        const stageColors = settings.stage_type_colors || {};
        const aiKw = settings.ai_keywords || {};

        const inputCss = { padding: '4px 8px', fontSize: 12, border: '1px solid #cbd5e1', borderRadius: 4 };
        const localUpdate = (patch) => {
            // Optimistic UI: update orgSettings immediately, persist via Save button
            setOrgSettings(prev => ({ ...(prev || {}), ...patch }));
        };

        const typeRouting = settings.type_routing || {};
        const stateLabels = settings.state_labels || {};
        const ratingColors = settings.rating_colors || {};
        const saveAll = () => {
            saveOrgSettings({
                sod_stages: sodStages,
                email_branding: eb,
                severity_thresholds: th,
                scale_max: sm,
                role_labels: roleLabels,
                stage_type_colors: stageColors,
                ai_keywords: aiKw,
                share_token_ttl_days: settings.share_token_ttl_days || 30,
                type_routing: typeRouting,
                state_labels: stateLabels,
                rating_colors: ratingColors,
            });
        };

        return (
            <div className="rp-card" style={{ marginTop: 16 }}>
                <div className="rp-section-header" style={{ marginBottom: 12 }}>
                    <h3 style={{ marginBottom: 0 }}>CUSTOMISATION</h3>
                    {canEdit && <button className="rp-btn rp-btn-primary" onClick={saveAll}>Save Customisation</button>}
                </div>
                {!canEdit && <p style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>Read-only. Only Risk Admin can edit these settings.</p>}

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16 }}>

                    {/* Severity matrix */}
                    <div className="rp-info-tile">
                        <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#be123c' }} />Severity Thresholds</div>
                        <div className="rp-info-tile-body" style={{ marginBottom: 8 }}>Score boundaries: low &le; X, medium &le; Y, high &le; Z, critical &gt; Z</div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 12 }}>
                            <label>Low max <input type="number" disabled={!canEdit} value={th[0]} onChange={e => localUpdate({ severity_thresholds: [parseInt(e.target.value) || 0, th[1], th[2]] })} style={{ ...inputCss, width: 60 }} /></label>
                            <label>Med max <input type="number" disabled={!canEdit} value={th[1]} onChange={e => localUpdate({ severity_thresholds: [th[0], parseInt(e.target.value) || 0, th[2]] })} style={{ ...inputCss, width: 60 }} /></label>
                            <label>High max <input type="number" disabled={!canEdit} value={th[2]} onChange={e => localUpdate({ severity_thresholds: [th[0], th[1], parseInt(e.target.value) || 0] })} style={{ ...inputCss, width: 60 }} /></label>
                        </div>
                    </div>

                    {/* L/I scale */}
                    <div className="rp-info-tile">
                        <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#1d4ed8' }} />Likelihood / Impact Scale</div>
                        <div className="rp-info-tile-body" style={{ marginBottom: 8 }}>Maximum value (default 5 = ISO 5x5 matrix). Range: 2-10.</div>
                        <label style={{ fontSize: 12 }}>1 to <input type="number" min="2" max="10" disabled={!canEdit} value={sm} onChange={e => localUpdate({ scale_max: Math.max(2, Math.min(10, parseInt(e.target.value) || 5)) })} style={{ ...inputCss, width: 60 }} /></label>
                    </div>

                    {/* Share token TTL */}
                    <div className="rp-info-tile">
                        <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#0891b2' }} />Share Link Lifetime</div>
                        <div className="rp-info-tile-body" style={{ marginBottom: 8 }}>How long owner-acknowledgement and share links stay valid. Default 30 days. Range 1-365.</div>
                        <label style={{ fontSize: 12 }}>
                            <input type="number" min="1" max="365" disabled={!canEdit}
                                value={settings.share_token_ttl_days ?? 30}
                                onChange={e => localUpdate({ share_token_ttl_days: Math.max(1, Math.min(365, parseInt(e.target.value) || 30)) })}
                                style={{ ...inputCss, width: 80 }} /> days
                        </label>
                    </div>

                    {/* Email branding */}
                    <div className="rp-info-tile">
                        <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#15803d' }} />Email Branding</div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <label style={{ fontSize: 12 }}>From address<br /><input type="email" disabled={!canEdit} placeholder="noreply@yourdomain.com" value={eb.from_email || ''} onChange={e => localUpdate({ email_branding: { ...eb, from_email: e.target.value } })} style={{ ...inputCss, width: '100%' }} /></label>
                            <label style={{ fontSize: 12 }}>Brand name<br /><input type="text" disabled={!canEdit} placeholder="Your Company Risk Registry" value={eb.brand_name || ''} onChange={e => localUpdate({ email_branding: { ...eb, brand_name: e.target.value } })} style={{ ...inputCss, width: '100%' }} /></label>
                            <label style={{ fontSize: 12 }}>Signature (optional)<br /><textarea disabled={!canEdit} placeholder="Appended to every email body" value={eb.signature || ''} onChange={e => localUpdate({ email_branding: { ...eb, signature: e.target.value } })} rows={2} style={{ ...inputCss, width: '100%', fontFamily: 'inherit' }} /></label>
                        </div>
                    </div>

                    {/* SoD stages */}
                    <div className="rp-info-tile">
                        <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#f59e0b' }} />SoD Enforcement Stages</div>
                        <div className="rp-info-tile-body" style={{ marginBottom: 8 }}>At these stages, the actor cannot also be the registered risk owner.</div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {allStageIds.map(sid => {
                                const on = sodStages.includes(sid);
                                return (
                                    <button key={sid} type="button" disabled={!canEdit}
                                        onClick={() => {
                                            const next = on ? sodStages.filter(x => x !== sid) : [...sodStages, sid];
                                            localUpdate({ sod_stages: next });
                                        }}
                                        style={{
                                            fontSize: 11, padding: '3px 10px', borderRadius: 12, cursor: canEdit ? 'pointer' : 'default',
                                            border: `1px solid ${on ? '#f59e0b' : '#cbd5e1'}`,
                                            background: on ? '#fef3c7' : '#fff',
                                            color: on ? '#92400e' : '#94a3b8',
                                            fontWeight: on ? 600 : 400,
                                        }}>
                                        {on ? '✓ ' : ''}{sid}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Type Routing */}
                    <div className="rp-info-tile">
                        <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#db2777' }} />Risk Type Routing</div>
                        <div className="rp-info-tile-body" style={{ marginBottom: 8 }}>
                            Override the stage owner for risks of a specific type. Applies at review/treatment/approve stages - acceptance still uses severity-tiered routing.
                        </div>
                        {Object.entries(typeRouting).map(([rt, role]) => (
                            <div key={rt} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4, fontSize: 12 }}>
                                <input disabled={!canEdit} value={rt} onChange={e => {
                                    const next = { ...typeRouting };
                                    delete next[rt];
                                    if (e.target.value.trim()) next[e.target.value.trim().toLowerCase()] = role;
                                    localUpdate({ type_routing: next });
                                }} placeholder="risk type" style={{ ...inputCss, flex: 1 }} />
                                <span style={{ color: '#94a3b8' }}>-></span>
                                <select disabled={!canEdit} value={role} onChange={e => localUpdate({ type_routing: { ...typeRouting, [rt]: e.target.value } })} style={{ ...inputCss, minWidth: 140 }}>
                                    {['risk_manager', 'risk_owner', 'risk_reviewer', 'business_owner', 'risk_committee', 'ciso', 'risk_admin'].map(r => (
                                        <option key={r} value={r}>{r}</option>
                                    ))}
                                </select>
                                {canEdit && (
                                    <button type="button" onClick={() => {
                                        const next = { ...typeRouting };
                                        delete next[rt];
                                        localUpdate({ type_routing: next });
                                    }} style={{ ...inputCss, padding: '2px 8px', cursor: 'pointer', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}>×</button>
                                )}
                            </div>
                        ))}
                        {canEdit && (
                            <button type="button" onClick={() => {
                                const placeholder = `new_type_${Object.keys(typeRouting).length + 1}`;
                                localUpdate({ type_routing: { ...typeRouting, [placeholder]: 'risk_committee' } });
                            }} style={{ ...inputCss, marginTop: 6, fontSize: 11, padding: '3px 10px', cursor: 'pointer', background: '#fff', color: '#9d174d' }}>+ Add type rule</button>
                        )}
                        {Object.keys(typeRouting).length === 0 && (
                            <div style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>No overrides - every type uses the workflow's configured stage owner.</div>
                        )}
                    </div>

                    {/* Role label overrides */}
                    <div className="rp-info-tile">
                        <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#7c3aed' }} />Role Display Labels</div>
                        <div className="rp-info-tile-body" style={{ marginBottom: 8 }}>Custom name shown in the UI. Default if blank.</div>
                        {Object.keys({ risk_admin: 1, risk_manager: 1, risk_owner: 1, risk_reviewer: 1, business_owner: 1, risk_committee: 1, ciso: 1 }).map(rk => (
                            <div key={rk} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 12, alignItems: 'center' }}>
                                <span style={{ width: 130, color: '#64748b' }}>{rk}</span>
                                <input type="text" disabled={!canEdit} placeholder="(default)" value={roleLabels[rk] || ''}
                                    onChange={e => localUpdate({ role_labels: { ...roleLabels, [rk]: e.target.value } })}
                                    style={{ ...inputCss, flex: 1 }} />
                            </div>
                        ))}
                    </div>

                    {/* State label overrides - underlying state values stay stable for reports/integrations */}
                    <div className="rp-info-tile">
                        <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#0891b2' }} />Risk State Labels</div>
                        <div className="rp-info-tile-body" style={{ marginBottom: 8 }}>
                            Display name for each underlying state. The system value (left) stays stable for reports and integrations; only the label changes in the UI and emails.
                        </div>
                        {['open', 'in_progress', 'accepted', 'closed', 'transferred'].map(sv => (
                            <div key={sv} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 12, alignItems: 'center' }}>
                                <span style={{ width: 130, color: '#64748b', fontFamily: 'var(--rp-font-mono)' }}>{sv}</span>
                                <input type="text" disabled={!canEdit}
                                    placeholder={({ open: 'Open', in_progress: 'In Progress', accepted: 'Accepted', closed: 'Closed', transferred: 'Transferred' })[sv]}
                                    value={(settings.state_labels || {})[sv] || ''}
                                    onChange={e => localUpdate({ state_labels: { ...(settings.state_labels || {}), [sv]: e.target.value } })}
                                    style={{ ...inputCss, flex: 1 }} />
                            </div>
                        ))}
                    </div>

                    {/* Severity color overrides - for accessibility palettes or brand alignment */}
                    <div className="rp-info-tile">
                        <div className="rp-info-tile-head"><span className="rp-info-tile-dot" style={{ background: '#b91c1c' }} />Severity Colors</div>
                        <div className="rp-info-tile-body" style={{ marginBottom: 8 }}>
                            Override the colours used for severity badges, posture bar, heatmap legend, and chart segments.
                            Defaults are ISO 31000 industry-standard. Useful for brand alignment or accessibility palettes
                            (e.g. deuteranopia-friendly colours).
                        </div>
                        {['critical', 'high', 'medium', 'low'].map(sv => {
                            const def = ({ critical: '#b91c1c', high: '#ea580c', medium: '#eab308', low: '#16a34a' })[sv];
                            const cur = (settings.rating_colors || {})[sv] || def;
                            return (
                                <div key={sv} style={{ display: 'flex', gap: 8, marginBottom: 4, fontSize: 12, alignItems: 'center' }}>
                                    <span style={{ width: 80, color: '#64748b', textTransform: 'capitalize' }}>{sv}</span>
                                    <input type="color" disabled={!canEdit} value={cur}
                                        onChange={e => localUpdate({ rating_colors: { ...(settings.rating_colors || {}), [sv]: e.target.value } })}
                                        style={{ width: 36, height: 28, border: '1px solid var(--rp-border)', borderRadius: 4, padding: 0, cursor: canEdit ? 'pointer' : 'default' }} />
                                    <input type="text" disabled={!canEdit}
                                        value={cur}
                                        onChange={e => {
                                            const v = e.target.value.trim();
                                            if (/^#[0-9a-fA-F]{6}$/.test(v) || v === '') {
                                                localUpdate({ rating_colors: { ...(settings.rating_colors || {}), [sv]: v || def } });
                                            }
                                        }}
                                        style={{ ...inputCss, flex: 1, fontFamily: 'var(--rp-font-mono)' }} />
                                    <span className="rp-rating" style={{ color: cur, fontSize: 11, minWidth: 60, textAlign: 'right' }}>preview</span>
                                </div>
                            );
                        })}
                        {canEdit && (
                            <button type="button"
                                onClick={() => localUpdate({ rating_colors: {} })}
                                style={{ ...inputCss, marginTop: 6, fontSize: 11, padding: '3px 10px', cursor: 'pointer', background: '#fff', color: '#64748b' }}>
                                Reset to ISO 31000 defaults
                            </button>
                        )}
                    </div>

                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // REPORTS VIEW (CISO)
    // ═══════════════════════════════════════════════
    function renderScenarios() {
        return (
            <div className="rp-view">
                <div className="rp-section-header" style={{ marginBottom: 16 }}>
                    <h2 className="rp-view-title" style={{ marginBottom: 0 }}>Scenario & Stress Testing</h2>
                    <button className="rp-btn rp-btn-primary" onClick={() => { setScenarioForm({ name: '', description: '', likelihood: 3, impact_financial: '', impact_operational: 3, impact_reputational: 3, impact_regulatory: 3, assumptions: '', mitigation_strategy: '' }); setShowScenarioModal(true); }}>+ New Scenario</button>
                </div>
                <p className="rp-subtitle">Model "what-if" scenarios for emerging risks. Link to existing risks to see aggregate impact.</p>
                {scenarios.length > 0 ? (
                    <div className="rp-scenario-grid">
                        {scenarios.map(s => (
                            <div key={s.id} className="rp-card">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <h3 style={{ textTransform: 'none', letterSpacing: 0, fontSize: 14, fontWeight: 700, color: 'var(--rp-text)' }}>{s.name}</h3>
                                    <button className="rp-btn-sm-danger" onClick={async () => { if (window.confirm('Delete scenario?')) { await risk_portal_delete_scenario(s.id); flash('Deleted'); loadScenarios(); } }}>×</button>
                                </div>
                                <p style={{ fontSize: 12, color: 'var(--rp-text-2)', marginBottom: 8 }}>{s.description}</p>
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 11 }}>
                                    <span>Likelihood: <strong>{s.likelihood}/5</strong></span>
                                    {s.impact_financial && <span>Financial: <strong>${Number(s.impact_financial).toLocaleString()}</strong></span>}
                                    <span>Operational: <strong>{s.impact_operational}/5</strong></span>
                                    <span>Reputational: <strong>{s.impact_reputational}/5</strong></span>
                                    <span>Regulatory: <strong>{s.impact_regulatory}/5</strong></span>
                                    <span>Linked Risks: <strong>{s.linked_risks_count}</strong></span>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : <div className="rp-card"><p className="rp-empty-msg">No scenarios defined. Create a "what-if" scenario to model emerging risks.</p></div>}

                {showScenarioModal && (
                    <div className="rp-modal-overlay" onClick={() => setShowScenarioModal(false)}>
                        <div className="rp-modal rp-modal-lg" onClick={e => e.stopPropagation()}>
                            <div className="rp-modal-header"><h3>Create Scenario</h3><button className="rp-modal-close" onClick={() => setShowScenarioModal(false)}>×</button></div>
                            <div className="rp-modal-body">
                                <div className="rp-form-group"><label>Scenario Name *</label><input className="rp-input" value={scenarioForm.name} onChange={e => setScenarioForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g., Ransomware on critical infrastructure" /></div>
                                <div className="rp-form-group"><label>Description *</label><textarea className="rp-textarea" rows={3} value={scenarioForm.description} onChange={e => setScenarioForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe the scenario and its conditions..." /></div>
                                <div className="rp-form-row">
                                    <div className="rp-form-group"><label>Likelihood (1-5)</label><select className="rp-select" value={scenarioForm.likelihood} onChange={e => setScenarioForm(p => ({ ...p, likelihood: +e.target.value }))}><option value={1}>1</option><option value={2}>2</option><option value={3}>3</option><option value={4}>4</option><option value={5}>5</option></select></div>
                                    <div className="rp-form-group"><label>Financial Impact ($)</label><input className="rp-input" type="number" value={scenarioForm.impact_financial} onChange={e => setScenarioForm(p => ({ ...p, impact_financial: e.target.value }))} /></div>
                                </div>
                                <div className="rp-form-row">
                                    <div className="rp-form-group"><label>Operational (1-5)</label><select className="rp-select" value={scenarioForm.impact_operational} onChange={e => setScenarioForm(p => ({ ...p, impact_operational: +e.target.value }))}><option value={1}>1</option><option value={2}>2</option><option value={3}>3</option><option value={4}>4</option><option value={5}>5</option></select></div>
                                    <div className="rp-form-group"><label>Reputational (1-5)</label><select className="rp-select" value={scenarioForm.impact_reputational} onChange={e => setScenarioForm(p => ({ ...p, impact_reputational: +e.target.value }))}><option value={1}>1</option><option value={2}>2</option><option value={3}>3</option><option value={4}>4</option><option value={5}>5</option></select></div>
                                </div>
                                <div className="rp-form-group"><label>Assumptions</label><textarea className="rp-textarea" rows={2} value={scenarioForm.assumptions} onChange={e => setScenarioForm(p => ({ ...p, assumptions: e.target.value }))} /></div>
                                <div className="rp-form-group"><label>Mitigation Strategy</label><textarea className="rp-textarea" rows={2} value={scenarioForm.mitigation_strategy} onChange={e => setScenarioForm(p => ({ ...p, mitigation_strategy: e.target.value }))} /></div>
                            </div>
                            <div className="rp-modal-footer">
                                <button className="rp-btn" onClick={() => setShowScenarioModal(false)}>Cancel</button>
                                <button className="rp-btn rp-btn-primary" onClick={async () => { try { await risk_portal_create_scenario(scenarioForm); setShowScenarioModal(false); flash('Scenario created'); loadScenarios(); } catch (e) { flash('Failed'); } }}>Create Scenario</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    function renderNovaAI() {
        const SAMPLE = "Our main payment vendor could go bankrupt, disrupting payment processing for 3 weeks affecting 50,000 customers and exposing the business to significant revenue loss and customer trust damage.";
        const hasInput = !!aiInput.trim();
        return (
            <div className="rp-view">
                <h2 className="rp-view-title">NOVA AI: Risk Intelligence</h2>
                <p className="rp-subtitle">AI-powered risk analysis using NOVA AI. Enter a risk description to analyze.</p>

                <div className="rp-card">
                    <div className="rp-form-group">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label>Risk Description</label>
                            {!hasInput && (
                                <button className="rp-link-btn" onClick={() => setAiInput(SAMPLE)} type="button">Try sample text -></button>
                            )}
                        </div>
                        <textarea className="rp-textarea" rows={4} value={aiInput} onChange={e => setAiInput(e.target.value)}
                            placeholder="Describe a risk... e.g., 'Our main payment vendor could go bankrupt, disrupting payment processing for 3 weeks affecting 50,000 customers'" />
                        {!hasInput && (
                            <div style={{ fontSize: 11, color: 'var(--rp-text-3)', marginTop: 4 }}>
                                Type a risk description to enable the analysis buttons.
                            </div>
                        )}
                    </div>
                    <div className="rp-ai-actions">
                        <button className="rp-btn rp-btn-primary" onClick={() => handleAiCategorize(aiInput)} disabled={!!aiLoading || !hasInput} title={!hasInput ? 'Enter a risk description first' : ''}>
                            {aiLoading === 'categorize' ? 'Analyzing...' : 'Auto-Categorize'}
                        </button>
                        <button className="rp-btn rp-btn-primary" onClick={() => handleAiDuplicates(aiInput)} disabled={!!aiLoading || !hasInput} title={!hasInput ? 'Enter a risk description first' : ''}>
                            {aiLoading === 'duplicates' ? 'Analyzing...' : 'Detect Duplicates'}
                        </button>
                        <button className="rp-btn rp-btn-primary" onClick={() => handleAiStatement(aiInput)} disabled={!!aiLoading || !hasInput} title={!hasInput ? 'Enter a risk description first' : ''}>
                            {aiLoading === 'statement' ? 'Analyzing...' : 'Generate ISO Statement'}
                        </button>
                        <button className="rp-btn rp-btn-primary" onClick={() => handleAiTreatment(aiInput, '', '')} disabled={!!aiLoading || !hasInput} title={!hasInput ? 'Enter a risk description first' : ''}>
                            {aiLoading === 'treatment' ? 'Analyzing...' : 'Suggest Treatment'}
                        </button>
                    </div>
                </div>

                {/* Loading indicator */}
                {aiLoading && (
                    <div className="rp-card" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div className="rp-spinner" style={{ width: 20, height: 20, marginBottom: 0, borderWidth: 2 }} />
                        <span style={{ fontSize: 13, color: 'var(--rp-text-2)' }}>Calling NOVA AI - typically 2-5 seconds...</span>
                    </div>
                )}

                {/* Inline error banner */}
                {aiError && !aiLoading && (
                    <Banner variant="error">
                        {aiError}
                        <button className="rp-link-btn" onClick={() => setAiError(null)} style={{ marginLeft: 12 }}>Dismiss</button>
                    </Banner>
                )}

                {/* AI Results */}
                {aiResult && (
                    <div className="rp-card">
                        <div className="rp-section-header">
                            <h3>NOVA AI RESPONSE</h3>
                            <button className="rp-btn-sm-ghost" onClick={() => setAiResult(null)}>Clear</button>
                        </div>
                        <div className="rp-ai-result" style={{ borderLeft: '3px solid var(--rp-brand)' }}>
                            {aiResult.type === 'categorize' && (<>
                                <div className="rp-ai-label">Suggested Category</div>
                                <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 8 }}>
                                    <span className="rp-type-badge" style={{ fontSize: 14, padding: '4px 14px' }}>{aiResult.category}</span>
                                    <span className="rp-score-badge" style={{ background: '#6366f1' }}>{Math.round((aiResult.confidence || 0) * 100)}% confidence</span>
                                    <span className="rp-ai-method">via {aiResult.method}</span>
                                </div>
                                {aiResult.reasoning && <p style={{ color: 'var(--rp-text-2)', fontSize: 13 }}>{aiResult.reasoning}</p>}
                            </>)}
                            {aiResult.type === 'duplicates' && (<>
                                <div className="rp-ai-label">{aiResult.count || 0} potential duplicate(s) found</div>
                                <table className="rp-table rp-table-sm" style={{ marginTop: 8 }}>
                                    <thead><tr><th>Risk ID</th><th>Description</th><th>Severity</th><th>Similarity</th><th>Reason</th></tr></thead>
                                    <tbody>
                                        {(aiResult.duplicates || []).map((d, i) => (
                                            <tr key={i} className="rp-clickable" onClick={() => loadRiskDetail(d.id || d.risk_id)}>
                                                <td><span className="rp-risk-id">{d.risk_id}</span></td>
                                                <td>{d.risk}</td>
                                                <td>{d.severity}</td>
                                                <td><span className="rp-score-badge" style={{ background: d.similarity > 70 ? '#D24646' : d.similarity > 50 ? '#DE9E30' : '#D9BF21' }}>{d.similarity}%</span></td>
                                                <td style={{ fontSize: 11, color: 'var(--rp-text-3)' }}>{d.reason || '-'}</td>
                                            </tr>
                                        ))}
                                        {(aiResult.duplicates || []).length === 0 && <tr><td colSpan={5} className="rp-empty-cell">No duplicates found</td></tr>}
                                    </tbody>
                                </table>
                            </>)}
                            {aiResult.type === 'statement' && (<>
                                <div className="rp-ai-label">ISO 31000 Risk Statement</div>
                                <div className="rp-ai-statement" style={{ marginTop: 8 }}>
                                    <div className="rp-form-group"><label>RISK STATEMENT</label><div style={{ background: 'var(--rp-surface-2)', padding: 10, borderRadius: 'var(--rp-r)', fontSize: 13, lineHeight: 1.6 }}>{aiResult.risk_statement}</div></div>
                                    <div className="rp-form-row">
                                        <div className="rp-form-group"><label>THREAT</label><div style={{ fontSize: 12, color: 'var(--rp-text-2)' }}>{aiResult.threat || '-'}</div></div>
                                        <div className="rp-form-group"><label>VULNERABILITY</label><div style={{ fontSize: 12, color: 'var(--rp-text-2)' }}>{aiResult.vulnerability || '-'}</div></div>
                                    </div>
                                    <div className="rp-form-group"><label>IMPACT</label><div style={{ fontSize: 12, color: 'var(--rp-text-2)' }}>{aiResult.impact || '-'}</div></div>
                                    <div style={{ display: 'flex', gap: 16, marginTop: 8 }}>
                                        <span className="rp-type-badge">{aiResult.suggested_category}</span>
                                        <span>L: <strong>{aiResult.suggested_likelihood}</strong>/5</span>
                                        <span>I: <strong>{aiResult.suggested_impact}</strong>/5</span>
                                    </div>
                                    {aiResult.treatment_recommendation && (
                                        <div className="rp-form-group" style={{ marginTop: 8 }}><label>TREATMENT RECOMMENDATION</label><div style={{ fontSize: 12, color: 'var(--rp-text-2)' }}>{aiResult.treatment_recommendation}</div></div>
                                    )}
                                </div>
                            </>)}
                            {aiResult.type === 'treatment' && (<>
                                <div className="rp-ai-label">AI Treatment Recommendation</div>
                                <div className="rp-ai-statement" style={{ marginTop: 8 }}>
                                    <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                                        <span className="rp-treatment-strategy">{aiResult.recommended_strategy}</span>
                                        {aiResult.cost_estimate && <span className="rp-type-badge">Cost: {aiResult.cost_estimate}</span>}
                                        {aiResult.timeline && <span className="rp-type-badge">Timeline: {aiResult.timeline}</span>}
                                    </div>
                                    <div className="rp-form-group"><label>TREATMENT PLAN</label><div style={{ background: 'var(--rp-surface-2)', padding: 10, borderRadius: 'var(--rp-r)', fontSize: 13, lineHeight: 1.6 }}>{aiResult.treatment_plan}</div></div>
                                    {aiResult.controls?.length > 0 && (
                                        <div className="rp-form-group"><label>SUGGESTED CONTROLS</label>
                                            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'var(--rp-text-2)' }}>
                                                {aiResult.controls.map((c, i) => <li key={i}>{c}</li>)}
                                            </ul>
                                        </div>
                                    )}
                                    {aiResult.estimated_effectiveness && (
                                        <div style={{ display: 'flex', gap: 16, marginTop: 8, fontSize: 12 }}>
                                            <span>Effectiveness: <strong>{aiResult.estimated_effectiveness}%</strong></span>
                                            {aiResult.estimated_residual_likelihood && <span>Residual L: <strong>{aiResult.estimated_residual_likelihood}</strong></span>}
                                            {aiResult.estimated_residual_impact && <span>Residual I: <strong>{aiResult.estimated_residual_impact}</strong></span>}
                                        </div>
                                    )}
                                </div>
                            </>)}
                        </div>
                    </div>
                )}

                {/* Feature descriptions */}
                <div className="rp-charts-row" style={{ marginTop: 16 }}>
                    <div className="rp-card">
                        <h3 style={{ textTransform: 'none', letterSpacing: 0 }}>Auto-Categorize</h3>
                        <p style={{ fontSize: 12, color: 'var(--rp-text-3)' }}>NOVA AI analyzes the risk description and classifies it into ISO categories (cyber, financial, operational, compliance, etc.) with a confidence score.</p>
                    </div>
                    <div className="rp-card">
                        <h3 style={{ textTransform: 'none', letterSpacing: 0 }}>Detect Duplicates</h3>
                        <p style={{ fontSize: 12, color: 'var(--rp-text-3)' }}>Finds semantically similar risks in your registry using Jaccard pre-filter + NOVA AI ranking. Catches paraphrased duplicates that keyword matching misses.</p>
                    </div>
                    <div className="rp-card">
                        <h3 style={{ textTransform: 'none', letterSpacing: 0 }}>Generate ISO Statement</h3>
                        <p style={{ fontSize: 12, color: 'var(--rp-text-3)' }}>Converts plain text into a formal ISO 31000 risk statement with threat, vulnerability, impact analysis, and suggested likelihood/impact scores.</p>
                    </div>
                    <div className="rp-card">
                        <h3 style={{ textTransform: 'none', letterSpacing: 0 }}>Suggest Treatment</h3>
                        <p style={{ fontSize: 12, color: 'var(--rp-text-3)' }}>Recommends ISO 31000 treatment strategy (mitigate/transfer/accept/avoid) with specific controls, estimated effectiveness, cost, and timeline.</p>
                    </div>
                </div>
            </div>
        );
    }

    function renderReviewSchedules() {
        return (
            <div className="rp-view">
                <div className="rp-section-header" style={{ marginBottom: 16 }}>
                    <h2 className="rp-view-title" style={{ marginBottom: 0 }}>Risk Review Schedules</h2>
                    <button className="rp-btn rp-btn-primary" onClick={async () => {
                        const name = prompt('Schedule name (e.g., Quarterly Cyber Risk Review):');
                        if (!name) return;
                        try {
                            await risk_portal_create_review_schedule({
                                name, frequency: 'quarterly',
                                next_review_date: new Date(Date.now() + 90 * 86400000).toISOString().split('T')[0],
                            });
                            flash('Schedule created');
                            loadReviewSchedules();
                        } catch (e) { flash('Failed'); }
                    }}>+ New Schedule</button>
                </div>
                <table className="rp-table">
                    <thead><tr><th>Schedule</th><th>Frequency</th><th>Next Review</th><th>Last Review</th><th>Assigned To</th><th></th></tr></thead>
                    <tbody>
                        {reviewSchedules.map(s => (
                            <tr key={s.id}>
                                <td><strong>{s.name}</strong></td>
                                <td><span className="rp-type-badge">{s.frequency}</span></td>
                                <td>{fmtDate(s.next_review_date)}</td>
                                <td>{s.last_review_date ? fmtDate(s.last_review_date) : '-'}</td>
                                <td>{s.assigned_to || '-'}</td>
                                <td><button className="rp-btn-sm-danger" onClick={async () => { if (window.confirm('Delete?')) { await risk_portal_delete_review_schedule(s.id); flash('Deleted'); loadReviewSchedules(); } }}>×</button></td>
                            </tr>
                        ))}
                        {reviewSchedules.length === 0 && <tr><td colSpan={6} className="rp-empty-cell">No review schedules configured.</td></tr>}
                    </tbody>
                </table>
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // COMPLIANCE COVERAGE VIEW
    // ═══════════════════════════════════════════════
    function renderComplianceCoverage() {
        return (
            <div className="rp-view">
                <h2 className="rp-view-title">Compliance Coverage</h2>
                <p style={{ fontSize: 12, color: 'var(--rp-text-3)', marginBottom: 16 }}>
                    Shows how many framework controls are addressed by risks in your registry. Unmapped controls represent compliance gaps.
                </p>
                {complianceCoverage.length > 0 ? (
                    <div className="rp-stage-grid">
                        {complianceCoverage.map(fw => (
                            <div key={fw.framework_id} className="rp-card" style={{ marginBottom: 16 }}>
                                <h3>{fw.framework_name} <span style={{ fontSize: 11, color: 'var(--rp-text-3)' }}>({fw.framework_type})</span></h3>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '12px 0' }}>
                                    <div style={{ flex: 1, background: '#e5e7eb', borderRadius: 4, height: 20, overflow: 'hidden' }}>
                                        <div style={{ width: `${fw.coverage_pct}%`, background: fw.coverage_pct >= 80 ? '#19A07E' : fw.coverage_pct >= 50 ? '#D9BF21' : '#D24646', height: '100%', borderRadius: 4, transition: 'width 0.3s' }} />
                                    </div>
                                    <span style={{ fontWeight: 600, fontSize: 14, minWidth: 50 }}>{fw.coverage_pct}%</span>
                                </div>
                                <div style={{ display: 'flex', gap: 16, fontSize: 12, color: 'var(--rp-text-2)' }}>
                                    <span>Total Controls: <strong>{fw.total_controls}</strong></span>
                                    <span>Mapped: <strong style={{ color: '#19A07E' }}>{fw.mapped_controls}</strong></span>
                                    <span>Gaps: <strong style={{ color: '#D24646' }}>{fw.total_controls - fw.mapped_controls}</strong></span>
                                </div>
                                {fw.unmapped_controls?.length > 0 && (
                                    <details className="rp-history" style={{ marginTop: 10 }}>
                                        <summary style={{ fontSize: 12, cursor: 'pointer', color: '#D24646' }}>
                                            View {fw.unmapped_controls.length} unmapped controls (gaps)
                                        </summary>
                                        <table className="rp-table rp-table-sm" style={{ marginTop: 8 }}>
                                            <thead><tr><th>Ref</th><th>Control</th></tr></thead>
                                            <tbody>
                                                {fw.unmapped_controls.map(c => (
                                                    <tr key={c.id}><td><strong>{c.control_ref}</strong></td><td>{c.title}</td></tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </details>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="rp-card"><p className="rp-empty-msg">No frameworks configured. Set up audit frameworks in the Internal Audit module first.</p></div>
                )}
            </div>
        );
    }

    function renderReports() {
        // Load board report on first view
        if (!boardReport) loadBoardReport();
        const br = boardReport;
        return (
            <div className="rp-view">
                <h2 className="rp-view-title">Board & Executive Reports</h2>
                {br ? (<>
                    <p className="rp-subtitle">Generated: {fmtTime(br.generated_at)}</p>
                    {/* KPI Summary */}
                    <div className="rp-kpi-row">
                        <KpiCard label="Total Risks" value={br.total_risks} color="#1b2a4a" />
                        <KpiCard label="Critical" value={br.by_severity?.critical || 0} color="#D24646" />
                        <KpiCard label="High" value={br.by_severity?.high || 0} color="#DE9E30" />
                        <KpiCard label="Medium" value={br.by_severity?.medium || 0} color="#D9BF21" />
                        <KpiCard label="Appetite Breaches" value={br.appetite_breaches || 0} color="#D24646" />
                    </div>
                    <div className="rp-charts-row">
                        <div className="rp-card">
                            <h3>RISKS BY STATE</h3>
                            {renderBarChart(br.by_state || {}, br.total_risks, '#1b2a4a')}
                        </div>
                        <div className="rp-card">
                            <h3>TREATMENT PROGRESS</h3>
                            {renderBarChart(br.treatment_summary || {}, Object.values(br.treatment_summary || {}).reduce((a, b) => a + b, 0), '#19A07E')}
                        </div>
                        <div className="rp-card">
                            <h3>KRI STATUS</h3>
                            {renderBarChart(br.kri_summary || {}, Object.values(br.kri_summary || {}).reduce((a, b) => a + b, 0), '#6366f1')}
                        </div>
                    </div>
                    <div className="rp-card rp-card-wide">
                        <h3>TOP 20 RISKS</h3>
                        <table className="rp-table">
                            <thead><tr><th>Risk ID</th><th>Description</th><th>Type</th><th>Severity</th><th>Score</th><th>State</th><th>Owner</th></tr></thead>
                            <tbody>
                                {(br.top_risks || []).map((r, i) => (
                                    <tr key={i}>
                                        <td><span className="rp-risk-id">{r.risk_id}</span></td>
                                        <td>{(r.risk || '').substring(0, 80)}</td>
                                        <td><span className="rp-type-badge">{(r.risk_type || '').replace(/_/g, ' ')}</span></td>
                                        <td><span className="rp-score-badge" style={{ background: RATING_COLORS[r.severity === 'critical' ? 'Critical' : r.severity === 'high' ? 'High' : r.severity === 'medium' ? 'Medium' : 'Low'] }}>{r.severity}</span></td>
                                        <td>{r.risk_score}</td>
                                        <td><span className="rp-state-badge">{r.state_label || (r.state || '').replace(/_/g, ' ')}</span></td>
                                        <td>{r.risk_owner || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Export */}
                    <button className="rp-btn" onClick={() => {
                        const rows = (br.top_risks || []).map(r => [r.risk_id, `"${(r.risk || '').replace(/"/g, '""')}"`, r.risk_type, r.severity, r.risk_score, r.state, r.risk_owner].join(','));
                        const csv = 'Risk ID,Description,Type,Severity,Score,State,Owner\n' + rows.join('\n');
                        const blob = new Blob([csv], { type: 'text/csv' }); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'board_risk_report.csv'; a.click();
                    }}>Export Board Report (CSV)</button>
                </>) : <p className="rp-empty-msg">Loading board report...</p>}
                <div className="rp-reports-grid" style={{ marginTop: 20 }}>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // MODALS
    // ═══════════════════════════════════════════════
    function renderAssessModal() {
        const f = assessForm;
        const inherentScore = f.inherent_likelihood * f.inherent_impact;
        const residualScore = f.residual_likelihood * f.residual_impact;
        const calcRating = (s) => s >= 16 ? 'Critical' : s >= 10 ? 'High' : s >= 5 ? 'Medium' : 'Low';
        return (
            <div className="rp-modal-overlay" onClick={() => setShowAssessModal(false)}>
                <div className="rp-modal" onClick={e => e.stopPropagation()}>
                    <div className="rp-modal-header"><h3>Risk Assessment</h3><button className="rp-modal-close" onClick={() => setShowAssessModal(false)}>×</button></div>
                    <div className="rp-modal-body">
                        <div className="rp-form-row">
                            <div className="rp-form-group">
                                <h4>Inherent Risk (Before Controls)</h4>
                                <label>Likelihood</label>
                                <select className="rp-select" value={f.inherent_likelihood} onChange={e => setAssessForm(p => ({ ...p, inherent_likelihood: +e.target.value }))}>
                                    {LIKELIHOOD_LABELS.map((l, i) => <option key={i} value={i + 1}>{i + 1} - {l}</option>)}
                                </select>
                                <label>Impact</label>
                                <select className="rp-select" value={f.inherent_impact} onChange={e => setAssessForm(p => ({ ...p, inherent_impact: +e.target.value }))}>
                                    {IMPACT_LABELS.map((l, i) => <option key={i} value={i + 1}>{i + 1} - {l}</option>)}
                                </select>
                                <div className="rp-assess-preview">
                                    Score: <span className="rp-score-badge" style={{ background: RATING_COLORS[calcRating(inherentScore)] }}>{inherentScore}</span>
                                    <span className="rp-rating" style={{ color: RATING_COLORS[calcRating(inherentScore)] }}>{calcRating(inherentScore)}</span>
                                </div>
                            </div>
                            <div className="rp-form-group">
                                <h4>Residual Risk (After Controls)</h4>
                                <label>Likelihood</label>
                                <select className="rp-select" value={f.residual_likelihood} onChange={e => setAssessForm(p => ({ ...p, residual_likelihood: +e.target.value }))}>
                                    {LIKELIHOOD_LABELS.map((l, i) => <option key={i} value={i + 1}>{i + 1} - {l}</option>)}
                                </select>
                                <label>Impact</label>
                                <select className="rp-select" value={f.residual_impact} onChange={e => setAssessForm(p => ({ ...p, residual_impact: +e.target.value }))}>
                                    {IMPACT_LABELS.map((l, i) => <option key={i} value={i + 1}>{i + 1} - {l}</option>)}
                                </select>
                                <div className="rp-assess-preview">
                                    Score: <span className="rp-score-badge" style={{ background: RATING_COLORS[calcRating(residualScore)] }}>{residualScore}</span>
                                    <span className="rp-rating" style={{ color: RATING_COLORS[calcRating(residualScore)] }}>{calcRating(residualScore)}</span>
                                </div>
                            </div>
                        </div>
                        <div className="rp-form-row">
                            <div className="rp-form-group">
                                <label>Control Effectiveness</label>
                                <select className="rp-select" value={f.control_effectiveness} onChange={e => setAssessForm(p => ({ ...p, control_effectiveness: +e.target.value }))}>
                                    <option value={0}>None (0%)</option><option value={25}>Weak (25%)</option><option value={50}>Moderate (50%)</option><option value={75}>Strong (75%)</option><option value={100}>Effective (100%)</option>
                                </select>
                            </div>
                            <div className="rp-form-group">
                                <label>Risk Velocity</label>
                                <select className="rp-select" value={f.risk_velocity} onChange={e => setAssessForm(p => ({ ...p, risk_velocity: e.target.value }))}>
                                    <option value="immediate">Immediate</option><option value="days">Days</option><option value="weeks">Weeks</option><option value="months">Months</option><option value="years">Years</option>
                                </select>
                            </div>
                        </div>
                        <div className="rp-form-group">
                            <label>Notes</label>
                            <textarea className="rp-textarea" rows={3} value={f.notes} onChange={e => setAssessForm(p => ({ ...p, notes: e.target.value }))} placeholder="Assessment notes..." />
                        </div>
                    </div>
                    <div className="rp-modal-footer">
                        <button className="rp-btn" onClick={() => setShowAssessModal(false)}>Cancel</button>
                        <button className="rp-btn rp-btn-primary" onClick={handleAssess}>Save Assessment</button>
                    </div>
                </div>
            </div>
        );
    }

    function renderTreatmentModal() {
        const f = treatmentForm;
        return (
            <div className="rp-modal-overlay" onClick={() => setShowTreatmentModal(false)}>
                <div className="rp-modal rp-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="rp-modal-header"><h3>Create Treatment Plan</h3><button className="rp-modal-close" onClick={() => setShowTreatmentModal(false)}>×</button></div>
                    <div className="rp-modal-body">
                        <div className="rp-form-row">
                            <div className="rp-form-group">
                                <label>Treatment Strategy (ISO 31000)</label>
                                <select className="rp-select" value={f.strategy} onChange={e => setTreatmentForm(p => ({ ...p, strategy: e.target.value }))}>
                                    <option value="mitigate">Mitigate - Reduce likelihood/impact</option>
                                    <option value="transfer">Transfer - Insurance/outsourcing</option>
                                    <option value="accept">Accept - Within appetite</option>
                                    <option value="avoid">Avoid - Eliminate the activity</option>
                                    <option value="exploit">Exploit - Increase positive outcome</option>
                                </select>
                            </div>
                        </div>
                        <div className="rp-form-group">
                            <label>Description</label>
                            <textarea className="rp-textarea" rows={3} value={f.description} onChange={e => setTreatmentForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe the treatment plan..." />
                        </div>
                        <h4 style={{ marginBottom: 4 }}>Action Items</h4>
                        <p style={{ fontSize: 12, color: 'var(--rp-text-3)', margin: '0 0 10px' }}>
                            Each action is a discrete task. Due dates drive overdue alerts, escalation, and closure conditions.
                        </p>
                        {(f.actions || []).length > 0 && (
                            <div className="rp-action-row" style={{ marginBottom: 4 }}>
                                <div style={{ flex: 2, fontSize: 11, fontWeight: 600, color: 'var(--rp-text-3)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Title</div>
                                <div style={{ flex: 2, fontSize: 11, fontWeight: 600, color: 'var(--rp-text-3)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Owner email</div>
                                <div style={{ flex: 1, fontSize: 11, fontWeight: 600, color: 'var(--rp-text-3)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Priority</div>
                                <div style={{ flex: 1, fontSize: 11, fontWeight: 600, color: 'var(--rp-text-3)', textTransform: 'uppercase', letterSpacing: 0.3 }}>Due date</div>
                                <div style={{ width: 24 }}></div>
                            </div>
                        )}
                        {(f.actions || []).map((act, i) => (
                            <div key={i} className="rp-action-row">
                                <input className="rp-input" placeholder="What needs to happen?" value={act.title} onChange={e => { const a = [...f.actions]; a[i] = { ...a[i], title: e.target.value }; setTreatmentForm(p => ({ ...p, actions: a })); }} />
                                <input className="rp-input" placeholder="who.owner@example.com" value={act.owner_email} onChange={e => { const a = [...f.actions]; a[i] = { ...a[i], owner_email: e.target.value }; setTreatmentForm(p => ({ ...p, actions: a })); }} />
                                <select className="rp-select-sm" value={act.priority} onChange={e => { const a = [...f.actions]; a[i] = { ...a[i], priority: e.target.value }; setTreatmentForm(p => ({ ...p, actions: a })); }}>
                                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option><option value="critical">Critical</option>
                                </select>
                                <input className="rp-input-sm" type="date" value={act.due_date || ''} title="Due date - drives overdue alerts and escalation" onChange={e => { const a = [...f.actions]; a[i] = { ...a[i], due_date: e.target.value }; setTreatmentForm(p => ({ ...p, actions: a })); }} />
                                <button className="rp-btn-icon" title="Remove this action" onClick={() => { const a = f.actions.filter((_, j) => j !== i); setTreatmentForm(p => ({ ...p, actions: a })); }}>×</button>
                            </div>
                        ))}
                        <button className="rp-btn rp-btn-sm" onClick={() => setTreatmentForm(p => ({ ...p, actions: [...(p.actions || []), { title: '', owner_email: '', priority: 'medium', due_date: '' }] }))}>+ Add Action</button>
                    </div>
                    <div className="rp-modal-footer">
                        <button className="rp-btn" onClick={() => setShowTreatmentModal(false)}>Cancel</button>
                        <button className="rp-btn rp-btn-primary" onClick={handleCreateTreatment}>Create Treatment</button>
                    </div>
                </div>
            </div>
        );
    }

    function renderInviteModal() {
        return (
            <div className="rp-modal-overlay" onClick={() => setShowInviteModal(false)}>
                <div className="rp-modal" onClick={e => e.stopPropagation()}>
                    <div className="rp-modal-header"><h3>Invite User</h3><button className="rp-modal-close" onClick={() => setShowInviteModal(false)}>×</button></div>
                    <div className="rp-modal-body">
                        <div className="rp-form-group"><label>Email</label><input className="rp-input" value={inviteForm.email} onChange={e => setInviteForm(p => ({ ...p, email: e.target.value }))} /></div>
                        <div className="rp-form-row">
                            <div className="rp-form-group"><label>First Name</label><input className="rp-input" value={inviteForm.first_name} onChange={e => setInviteForm(p => ({ ...p, first_name: e.target.value }))} /></div>
                            <div className="rp-form-group"><label>Last Name</label><input className="rp-input" value={inviteForm.last_name} onChange={e => setInviteForm(p => ({ ...p, last_name: e.target.value }))} /></div>
                        </div>
                        <div className="rp-form-group">
                            <label>Role</label>
                            <select className="rp-select" value={inviteForm.role} onChange={e => setInviteForm(p => ({ ...p, role: e.target.value }))}>
                                <option value="risk_admin">Risk Admin</option>
                                <option value="risk_manager">Risk Manager</option>
                                <option value="risk_owner">Risk Owner</option>
                                <option value="risk_reviewer">Risk Reviewer</option>
                                <option value="business_owner">Business Owner</option>
                                <option value="risk_committee">Risk Committee</option>
                                <option value="ciso">CISO (Read-Only)</option>
                            </select>
                        </div>
                    </div>
                    <div className="rp-modal-footer">
                        <button className="rp-btn" onClick={() => setShowInviteModal(false)}>Cancel</button>
                        <button className="rp-btn rp-btn-primary" onClick={handleInvite}>Send Invite</button>
                    </div>
                </div>
            </div>
        );
    }

    function renderCategoryModal() {
        return (
            <div className="rp-modal-overlay" onClick={() => setShowCategoryModal(false)}>
                <div className="rp-modal" onClick={e => e.stopPropagation()}>
                    <div className="rp-modal-header"><h3>Add Category</h3><button className="rp-modal-close" onClick={() => setShowCategoryModal(false)}>×</button></div>
                    <div className="rp-modal-body">
                        <div className="rp-form-group"><label>Name</label><input className="rp-input" value={categoryForm.name} onChange={e => setCategoryForm(p => ({ ...p, name: e.target.value }))} /></div>
                        <div className="rp-form-group"><label>Description</label><textarea className="rp-textarea" rows={2} value={categoryForm.description} onChange={e => setCategoryForm(p => ({ ...p, description: e.target.value }))} /></div>
                        <div className="rp-form-group"><label>Color</label><input type="color" value={categoryForm.color} onChange={e => setCategoryForm(p => ({ ...p, color: e.target.value }))} /></div>
                    </div>
                    <div className="rp-modal-footer">
                        <button className="rp-btn" onClick={() => setShowCategoryModal(false)}>Cancel</button>
                        <button className="rp-btn rp-btn-primary" onClick={handleCreateCategory}>Create</button>
                    </div>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // CONTROL MAPPING MODAL (Phase 2)
    // ═══════════════════════════════════════════════
    function renderControlMapModal() {
        return (
            <div className="rp-modal-overlay" onClick={() => setShowControlMapModal(false)}>
                <div className="rp-modal rp-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="rp-modal-header">
                        <h3>Map Control from Framework Library</h3>
                        <button className="rp-modal-close" onClick={() => setShowControlMapModal(false)}>×</button>
                    </div>
                    <div className="rp-modal-body">
                        <div className="rp-form-row">
                            <div className="rp-form-group">
                                <label>Framework</label>
                                <select className="rp-select" value={controlFrameworkId} onChange={e => { setControlFrameworkId(e.target.value); }}>
                                    <option value="">All Frameworks</option>
                                    {frameworks.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                                </select>
                            </div>
                            <div className="rp-form-group">
                                <label>Search</label>
                                <div style={{ display: 'flex', gap: '6px' }}>
                                    <input className="rp-input" placeholder="Search controls..." value={controlSearch} onChange={e => setControlSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && loadFrameworkControls()} />
                                    <button className="rp-btn rp-btn-primary" onClick={loadFrameworkControls}>Search</button>
                                </div>
                            </div>
                        </div>
                        <table className="rp-table rp-table-sm">
                            <thead><tr><th>Code</th><th>Control</th><th>Framework</th><th>Domain</th><th></th></tr></thead>
                            <tbody>
                                {frameworkControls.map(c => (
                                    <tr key={c.id}>
                                        <td><strong>{c.code}</strong></td>
                                        <td>{c.name}<br /><span style={{ fontSize: '10px', color: 'var(--rp-text-3)' }}>{c.description}</span></td>
                                        <td>{c.framework}</td>
                                        <td>{c.domain}</td>
                                        <td><button className="rp-btn rp-btn-sm rp-btn-primary" onClick={() => handleMapControl(c)}>Map</button></td>
                                    </tr>
                                ))}
                                {frameworkControls.length === 0 && <tr><td colSpan={5} className="rp-empty-cell">No controls found. Search or select a framework above.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    // ═══════════════════════════════════════════════
    // KRI CREATE MODAL (Phase 2)
    // ═══════════════════════════════════════════════
    // ═══════════════════════════════════════════════
    // ═══════════════════════════════════════════════
    // STAGE PROGRESS BAR + LIFECYCLE ACTIONS
    // ═══════════════════════════════════════════════
    function renderStageProgressBar(r) {
        // Drive the timeline from the org's saved RiskWorkflowConfig so customer
        // workflow customisations (renamed stages, dropped stages, rerouted
        // transitions, owner-role overrides) flow through to the live risk view.
        // Fall back to the built-in 7-stage ISO blueprint if the config is empty.
        const DEFAULT_BLUEPRINT = [
            { id: 'identification', name: 'Identify', owner_role: 'risk_manager', next_stages: ['assessment'] },
            { id: 'assessment', name: 'Assess', owner_role: 'risk_manager', next_stages: ['treatment'] },
            { id: 'treatment', name: 'Treat', owner_role: 'risk_owner', next_stages: ['review'] },
            { id: 'review', name: 'Review', owner_role: 'risk_reviewer', next_stages: ['acceptance', 'treatment'] },
            { id: 'acceptance', name: 'Accept', owner_role: 'business_owner', next_stages: ['monitoring', 'treatment'] },
            { id: 'monitoring', name: 'Monitor', owner_role: 'risk_manager', next_stages: ['closed', 'assessment'] },
            { id: 'closed', name: 'Closed', owner_role: null, next_stages: [] },
        ];
        const cfgStages = (wfConfig?.stages && wfConfig.stages.length > 0)
            ? [...wfConfig.stages].sort((a, b) => (a.order || 0) - (b.order || 0))
            : DEFAULT_BLUEPRINT;
        const STAGES = cfgStages.map(s => s.id);
        const STAGE_LABELS = Object.fromEntries(cfgStages.map(s => [s.id, s.name || s.id]));
        const STAGE_OWNER_MAP = Object.fromEntries(cfgStages.map(s => [s.id, s.owner_role || null]));
        const TRANSITIONS = Object.fromEntries(cfgStages.map(s => [s.id, Array.isArray(s.next_stages) ? s.next_stages : []]));

        // Stepper colours encode STATE not stage identity (Atlassian/ServiceNow convention):
        // past = success green, current = brand blue, future = neutral grey.
        const STATE_DONE = '#19A07E';
        const STATE_FUTURE = 'var(--rp-border-2)';
        const ws = workflowState;
        let currentStage = ws?.current_stage;
        if (!currentStage || !STAGES.includes(currentStage)) {
            // Map legacy state -> first matching stage in the (possibly customised) workflow
            const fallback = r.state === 'closed' ? 'closed' :
                r.state === 'accepted' ? 'monitoring' :
                    r.state === 'in_progress' ? 'treatment' : 'identification';
            currentStage = STAGES.includes(fallback) ? fallback : STAGES[0];
        }
        const currentIdx = STAGES.indexOf(currentStage);
        const activeRole = role;
        const stageOwner = STAGE_OWNER_MAP[currentStage];
        const canAct = activeRole === stageOwner || activeRole === 'risk_admin' || activeRole === 'ciso';
        const nextStages = TRANSITIONS[currentStage] || [];

        // SLA calculation
        let slaDays = null, slaOverdue = false;
        if (ws?.stage_sla_deadline) {
            const deadline = new Date(ws.stage_sla_deadline);
            const now = new Date();
            slaDays = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
            slaOverdue = slaDays < 0;
        }

        return (
            <div className="rp-detail-section rp-stage-section">
                {/* Progress pipeline */}
                <div className="rp-stage-pipeline">
                    {STAGES.map((stage, i) => {
                        const isPast = i < currentIdx;
                        const isCurrent = i === currentIdx;
                        const isFuture = i > currentIdx;
                        const dotBg = isPast ? STATE_DONE : isCurrent ? 'var(--rp-accent)' : STATE_FUTURE;
                        const labelColor = isPast ? STATE_DONE : isCurrent ? 'var(--rp-accent)' : 'var(--rp-text-3)';
                        return (
                            <React.Fragment key={stage}>
                                <div className={`rp-stage-pip ${isPast ? 'rp-stage-past' : ''} ${isCurrent ? 'rp-stage-current' : ''} ${isFuture ? 'rp-stage-future' : ''}`}>
                                    <div className="rp-stage-pip-dot" style={{ background: dotBg }}>
                                        {isPast ? <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3"><polyline points="20 6 9 17 4 12" /></svg> : <span>{i + 1}</span>}
                                    </div>
                                    <div className="rp-stage-pip-label" style={{ color: labelColor }}>{STAGE_LABELS[stage]}</div>
                                </div>
                                {i < STAGES.length - 1 && <div className={`rp-stage-pip-line ${isPast ? 'rp-stage-pip-line-done' : ''}`} />}
                            </React.Fragment>
                        );
                    })}
                </div>

                {/* Stage info bar */}
                <div className="rp-stage-info-bar">
                    <div>
                        <span className="rp-stage-current-label" style={{ background: 'var(--rp-accent)' }}>{STAGE_LABELS[currentStage]}</span>
                        {ws?.owner_role && <span className="rp-stage-owner-label">Owner: {(ws.owner_role || '').replace(/_/g, ' ')}</span>}
                        {slaDays !== null && (
                            <span className={`rp-sla-badge ${slaOverdue ? 'rp-sla-overdue' : slaDays <= 2 ? 'rp-sla-warning' : 'rp-sla-ok'}`}>
                                {slaOverdue ? `${Math.abs(slaDays)}d overdue` : `${slaDays}d remaining`}
                            </span>
                        )}
                        {ws?.escalation_status && ws.escalation_status !== 'none' && (
                            <span className="rp-escalation-badge">{ws.escalation_status.replace(/_/g, ' ')}</span>
                        )}
                    </div>

                    {/* Action buttons based on role + stage */}
                    <div className="rp-stage-actions">
                        {canAct && nextStages.length > 0 && nextStages.map(ns => (
                            <button key={ns} className="rp-btn rp-btn-primary" onClick={() => handleAdvanceStage(ns)}>
                                Advance to {STAGE_LABELS[ns] || ns} ->
                            </button>
                        ))}

                        {/* Accept button for business_owner at acceptance stage */}
                        {(activeRole === 'business_owner' || activeRole === 'ciso' || activeRole === 'risk_admin') && currentStage === 'acceptance' && (
                            <button className="rp-btn-approve" onClick={handleAcceptRisk}>Accept Residual Risk</button>
                        )}

                        {/* Close button for risk_manager/ciso at monitoring stage */}
                        {(activeRole === 'risk_manager' || activeRole === 'ciso' || activeRole === 'risk_admin') && currentStage === 'monitoring' && (
                            <button className="rp-btn" style={{ borderColor: '#8492a6', color: '#8492a6' }} onClick={() => handleCloseRisk(false)}>Close Risk</button>
                        )}
                    </div>
                </div>

                {/* Stage Guidance Card */}
                {ws?.guidance && currentStage !== 'closed' && (
                    <div className="rp-card" style={{ marginTop: 12, background: 'var(--rp-surface)', borderLeft: `3px solid var(--rp-accent)` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <div>
                                <h4 style={{ margin: '0 0 4px 0', fontSize: 14, color: 'var(--rp-text)', fontWeight: 600 }}>{ws.guidance.title}</h4>
                                <p style={{ margin: '0 0 8px 0', fontSize: 12, color: 'var(--rp-text-2)' }}>{ws.guidance.description}</p>
                            </div>
                            <span style={{ fontSize: 11, color: 'var(--rp-text-3)', whiteSpace: 'nowrap' }}>Owner: {ws.guidance.owner_role}</span>
                        </div>
                        {ws.guidance.what_to_do?.length > 0 && (
                            <ul style={{ margin: '0 0 8px 0', padding: '0 0 0 16px', fontSize: 12, color: 'var(--rp-text-2)' }}>
                                {ws.guidance.what_to_do.map((item, i) => (
                                    <li key={i} style={{ marginBottom: 2, listStyle: item.startsWith('  ') ? 'circle' : 'disc', marginLeft: item.startsWith('  ') ? 12 : 0 }}>{item.trim()}</li>
                                ))}
                            </ul>
                        )}
                        <p style={{ margin: 0, fontSize: 11, color: '#6366f1', fontWeight: 500 }}>{ws.guidance.next_step}</p>
                    </div>
                )}

                {/* Preflight Checklist */}
                {ws?.preflight?.length > 0 && currentStage !== 'closed' && (
                    <div style={{ marginTop: 8, padding: '8px 12px', background: '#f9fafb', borderRadius: 6, border: '1px solid var(--rp-border)' }}>
                        <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--rp-text-2)', marginBottom: 4, display: 'block' }}>Readiness Checklist</span>
                        {ws.preflight.map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: item.done ? '#19A07E' : '#D24646', marginBottom: 2 }}>
                                <span>{item.done ? '\u2713' : '\u2717'}</span>
                                <span>{item.label}</span>
                                {item.detail && <span style={{ fontSize: 10, color: 'var(--rp-text-3)' }}>({item.detail})</span>}
                            </div>
                        ))}
                    </div>
                )}

                {/* Rejection Banner */}
                {ws?.last_rejection && currentStage === 'treatment' && (
                    <div style={{ marginTop: 8, padding: '12px 16px', background: '#FFF8E1', border: '1px solid #FFD54F', borderRadius: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                            <span style={{ fontSize: 14 }}>!</span>
                            <strong style={{ fontSize: 12, color: '#b45309' }}>Treatment Rejected -- Revise Required</strong>
                        </div>
                        <p style={{ margin: 0, fontSize: 12, color: '#78350f' }}>
                            <strong>Reason:</strong> {ws.last_rejection.reason || 'No reason provided'}<br />
                            <strong>Rejected by:</strong> {ws.last_rejection.rejected_by} on {ws.last_rejection.rejected_at ? new Date(ws.last_rejection.rejected_at).toLocaleDateString() : ''}
                        </p>
                    </div>
                )}

                {/* Escalation Warning at Acceptance */}
                {currentStage === 'acceptance' && ws?.preflight?.some(p => p.label?.includes('appetite') && !p.done) && (
                    <div style={{ marginTop: 8, padding: '12px 16px', background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 6 }}>
                        <strong style={{ fontSize: 12, color: '#991b1b' }}>Risk Exceeds Appetite Threshold</strong>
                        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#7f1d1d' }}>
                            The residual risk score exceeds the organizational appetite. Clicking "Accept" will escalate this risk to the CISO for sign-off.
                            Alternatively, you can send it back to Treatment for further risk reduction.
                        </p>
                    </div>
                )}

                {/* Close blockers */}
                {closeBlockers && (
                    <div className="rp-close-blockers">
                        <h4>Cannot close. {closeBlockers.blockers.length} condition(s) unmet:</h4>
                        <ul>{closeBlockers.blockers.map((b, i) => <li key={i}>{b}</li>)}</ul>
                        {closeBlockers.can_force && (
                            <button className="rp-btn rp-btn-primary" onClick={() => handleCloseRisk(true)}>Force Close (CISO Override)</button>
                        )}
                        <button className="rp-btn" onClick={() => setCloseBlockers(null)} style={{ marginLeft: 8 }}>Dismiss</button>
                    </div>
                )}
            </div>
        );
    }

    // CREATE RISK MODAL
    // ═══════════════════════════════════════════════
    function renderCreateRiskModal() {
        const f = riskForm;
        const today = new Date().toISOString().split('T')[0];
        return (
            <>
                <div className="rp-modal-overlay" onClick={() => setShowCreateRiskModal(false)}>
                    <div className="rp-modal rp-modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="rp-modal-header"><h3>Identify Risk</h3><button className="rp-modal-close" onClick={() => setShowCreateRiskModal(false)}>x</button></div>
                        <div className="rp-modal-body">
                            <p style={{ fontSize: 11, color: 'var(--rp-text-3)', marginBottom: 12 }}>Stage 1: Identification. Assessment, treatment, and acceptance are handled in subsequent workflow stages.</p>
                            <div className="rp-form-group">
                                <label>Risk Description *</label>
                                <textarea className="rp-textarea" rows={3} value={f.risk} onChange={e => setRiskForm(p => ({ ...p, risk: e.target.value }))} placeholder="What could go wrong? Describe the risk event..." />
                            </div>
                            <div className="rp-form-row">
                                <div className="rp-form-group">
                                    <label>Threat *</label>
                                    <input className="rp-input" value={f.threat} onChange={e => setRiskForm(p => ({ ...p, threat: e.target.value }))} placeholder="What could cause this risk?" />
                                </div>
                                <div className="rp-form-group">
                                    <label>Vulnerability *</label>
                                    <input className="rp-input" value={f.vulnerability} onChange={e => setRiskForm(p => ({ ...p, vulnerability: e.target.value }))} placeholder="What weakness could be exploited?" />
                                </div>
                            </div>
                            <div className="rp-form-row">
                                <div className="rp-form-group">
                                    <label>Category *</label>
                                    <select className="rp-select" value={f.risk_type} onChange={e => setRiskForm(p => ({ ...p, risk_type: e.target.value }))}>
                                        <option value="">Select category...</option>
                                        {categories && categories.length > 0 ? (
                                            categories.filter(c => c.is_active !== false).map(c => (
                                                <option key={c.id || c.name} value={c.name || c.id}>{c.name}</option>
                                            ))
                                        ) : (
                                            <>
                                                <option value="financial">Financial</option><option value="operational">Operational</option>
                                                <option value="technical">Technical</option><option value="compliance_legal">Compliance / Legal</option>
                                                <option value="environmental">Environmental</option><option value="reputational">Reputational</option>
                                                <option value="strategic">Strategic</option>
                                            </>
                                        )}
                                    </select>
                                </div>
                                <div className="rp-form-group">
                                    <label>Risk Owner Email *</label>
                                    <input className="rp-input" type="email" value={f.risk_owner} onChange={e => setRiskForm(p => ({ ...p, risk_owner: e.target.value }))}
                                        placeholder={role === 'risk_owner' ? 'your.email@company.com' : 'who will own this risk?'} />
                                    {role !== 'risk_owner' && <small style={{ fontSize: 11, color: '#64748b' }}>Assign to the person accountable for this risk. They'll receive an acknowledgement email.</small>}
                                </div>
                            </div>
                            <div className="rp-form-row">
                                <div className="rp-form-group">
                                    <label>Target Date *</label>
                                    <input className="rp-input" type="date" min={today} value={f.target_date} onChange={e => setRiskForm(p => ({ ...p, target_date: e.target.value }))} />
                                </div>
                                <div className="rp-form-group">
                                    <label>Business Unit</label>
                                    <select className="rp-select" value={f.business_unit || ''} onChange={e => setRiskForm(p => ({ ...p, business_unit: e.target.value ? parseInt(e.target.value) : null }))}>
                                        <option value="">Organization-level (no BU)</option>
                                        {businessUnits.filter(b => b.is_active).map(b => (
                                            <option key={b.id} value={b.id}>{b.parent_id ? '↳ ' : ''}{b.name}{b.code ? ` (${b.code})` : ''}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="rp-form-row">
                                <div className="rp-form-group">
                                    <label>Project (optional)</label>
                                    <select className="rp-select" value={f.project || ''} onChange={e => setRiskForm(p => ({ ...p, project: e.target.value ? parseInt(e.target.value) : null }))}>
                                        <option value="">Not project-bound</option>
                                        {projects.filter(p => p.is_active !== false).map(p => (
                                            <option key={p.id} value={p.id}>{p.name}{p.code ? ` (${p.code})` : ''}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <details className="rp-history" style={{ marginTop: 8 }}>
                                <summary style={{ fontSize: 12, color: 'var(--rp-text-3)', cursor: 'pointer' }}>Initial Scoring (optional, can be done at Assessment stage)</summary>
                                <div className="rp-form-row" style={{ marginTop: 8 }}>
                                    <div className="rp-form-group">
                                        <label>Likelihood (1-5)</label>
                                        <select className="rp-select" value={f.likelihood} onChange={e => setRiskForm(p => ({ ...p, likelihood: +e.target.value }))}>
                                            <option value={1}>1 - Rare</option><option value={2}>2 - Unlikely</option>
                                            <option value={3}>3 - Possible</option><option value={4}>4 - Likely</option>
                                            <option value={5}>5 - Almost Certain</option>
                                        </select>
                                    </div>
                                    <div className="rp-form-group">
                                        <label>Impact (1-5)</label>
                                        <select className="rp-select" value={f.impact} onChange={e => setRiskForm(p => ({ ...p, impact: +e.target.value }))}>
                                            <option value={1}>1 - Insignificant</option><option value={2}>2 - Minor</option>
                                            <option value={3}>3 - Moderate</option><option value={4}>4 - Major</option>
                                            <option value={5}>5 - Catastrophic</option>
                                        </select>
                                    </div>
                                </div>
                            </details>
                        </div>
                        <div className="rp-modal-footer">
                            <button className="rp-btn" onClick={() => setShowCreateRiskModal(false)}>Cancel</button>
                            <button className="rp-btn rp-btn-primary" onClick={handleCreateRisk}>Create Risk</button>
                        </div>
                    </div>
                </div>

                {loader && <FullPageLoader loading={loader} />}
            </>

        );
    }

    // ═══════════════════════════════════════════════
    // EDIT RISK MODAL
    // ═══════════════════════════════════════════════
    function renderEditRiskModal() {
        const f = editRiskForm;
        if (!f) return null;
        const upd = (patch) => setEditRiskForm(p => ({ ...p, ...patch }));
        return (
            <>
                <div className="rp-modal-overlay" onClick={() => { setShowEditRiskModal(false); setEditRiskForm(null); }}>
                    <div className="rp-modal rp-modal-lg" onClick={e => e.stopPropagation()}>
                        <div className="rp-modal-header">
                            <h3>Edit Risk {f.risk_id ? <span className="rp-risk-id">{f.risk_id}</span> : ''}</h3>
                            <button className="rp-modal-close" onClick={() => { setShowEditRiskModal(false); setEditRiskForm(null); }}>x</button>
                        </div>
                        <div className="rp-modal-body">
                            <div className="rp-form-group">
                                <label>Risk Description *</label>
                                <textarea className="rp-textarea" rows={3} value={f.risk} onChange={e => upd({ risk: e.target.value })} placeholder="What could go wrong? Describe the risk event..." />
                            </div>
                            <div className="rp-form-row">
                                <div className="rp-form-group">
                                    <label>Threat</label>
                                    <input className="rp-input" value={f.threat} onChange={e => upd({ threat: e.target.value })} placeholder="What could cause this risk?" />
                                </div>
                                <div className="rp-form-group">
                                    <label>Vulnerability</label>
                                    <input className="rp-input" value={f.vulnerability} onChange={e => upd({ vulnerability: e.target.value })} placeholder="What weakness could be exploited?" />
                                </div>
                            </div>
                            <div className="rp-form-row">
                                <div className="rp-form-group">
                                    <label>Category</label>
                                    <select className="rp-select" value={f.risk_type} onChange={e => upd({ risk_type: e.target.value })}>
                                        <option value="">Select category...</option>
                                        {categories && categories.length > 0 ? (
                                            categories.filter(c => c.is_active !== false).map(c => (
                                                <option key={c.id || c.name} value={c.name || c.id}>{c.name}</option>
                                            ))
                                        ) : (
                                            <>
                                                <option value="financial">Financial</option><option value="operational">Operational</option>
                                                <option value="technical">Technical</option><option value="compliance_legal">Compliance / Legal</option>
                                                <option value="environmental">Environmental</option><option value="reputational">Reputational</option>
                                                <option value="strategic">Strategic</option>
                                            </>
                                        )}
                                    </select>
                                </div>
                                <div className="rp-form-group">
                                    <label>Risk Owner Email *</label>
                                    <input className="rp-input" type="email" value={f.risk_owner} onChange={e => upd({ risk_owner: e.target.value })} placeholder="who owns this risk?" />
                                </div>
                            </div>
                            <div className="rp-form-row">
                                <div className="rp-form-group">
                                    <label>State</label>
                                    <select className="rp-select" value={f.state} onChange={e => upd({ state: e.target.value })}>
                                        <option value="open">Open</option>
                                        <option value="in_progress">In Progress</option>
                                        <option value="accepted">Accepted</option>
                                        <option value="closed">Closed</option>
                                        <option value="transferred">Transferred</option>
                                    </select>
                                </div>
                                <div className="rp-form-group">
                                    <label>Target Date</label>
                                    <input className="rp-input" type="date" value={f.target_date} onChange={e => upd({ target_date: e.target.value })} />
                                </div>
                            </div>
                            <div className="rp-form-row">
                                <div className="rp-form-group">
                                    <label>Likelihood (1-5)</label>
                                    <select className="rp-select" value={f.likelihood} onChange={e => upd({ likelihood: +e.target.value })}>
                                        <option value={1}>1 - Rare</option><option value={2}>2 - Unlikely</option>
                                        <option value={3}>3 - Possible</option><option value={4}>4 - Likely</option>
                                        <option value={5}>5 - Almost Certain</option>
                                    </select>
                                </div>
                                <div className="rp-form-group">
                                    <label>Impact (1-5)</label>
                                    <select className="rp-select" value={f.impact} onChange={e => upd({ impact: +e.target.value })}>
                                        <option value={1}>1 - Insignificant</option><option value={2}>2 - Minor</option>
                                        <option value={3}>3 - Moderate</option><option value={4}>4 - Major</option>
                                        <option value={5}>5 - Catastrophic</option>
                                    </select>
                                </div>
                            </div>
                            <details className="rp-history" style={{ marginTop: 8 }}>
                                <summary style={{ fontSize: 12, color: 'var(--rp-text-3)', cursor: 'pointer' }}>Treatment (optional)</summary>
                                <div className="rp-form-row" style={{ marginTop: 8 }}>
                                    <div className="rp-form-group">
                                        <label>Treatment Option</label>
                                        <select className="rp-select" value={f.treatment_option} onChange={e => upd({ treatment_option: e.target.value })}>
                                            <option value="">None</option>
                                            <option value="mitigate">Mitigate</option>
                                            <option value="accept">Accept</option>
                                            <option value="transfer">Transfer</option>
                                            <option value="avoid">Avoid</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="rp-form-group">
                                    <label>Treatment Plan</label>
                                    <textarea className="rp-textarea" rows={2} value={f.treatment_plan} onChange={e => upd({ treatment_plan: e.target.value })} placeholder="How will this risk be treated?" />
                                </div>
                            </details>
                        </div>
                        <div className="rp-modal-footer">
                            <button className="rp-btn" onClick={() => { setShowEditRiskModal(false); setEditRiskForm(null); }}>Cancel</button>
                            <button className="rp-btn rp-btn-primary" onClick={handleUpdateRisk}>Save Changes</button>
                        </div>
                    </div>
                </div>

                {loader && <FullPageLoader loading={loader} />}
            </>
        );
    }

    // ═══════════════════════════════════════════════
    // SHARE RISK MODAL
    // ═══════════════════════════════════════════════
    function renderShareModal() {
        return (
            <div className="rp-modal-overlay" onClick={() => setShowShareModal(false)}>
                <div className="rp-modal rp-modal-lg" onClick={e => e.stopPropagation()}>
                    <div className="rp-modal-header"><h3>Share Risk with Owner</h3><button className="rp-modal-close" onClick={() => setShowShareModal(false)}>×</button></div>
                    <div className="rp-modal-body">
                        <p style={{ fontSize: 12, color: 'var(--rp-text-3)', marginBottom: 14 }}>
                            Generate a secure link so anyone in the company can view and update this risk without a dashboard login. The recipient will be notified via email.
                        </p>
                        <div className="rp-form-row">
                            <div className="rp-form-group">
                                <label>Recipient Email *</label>
                                <input className="rp-input" type="email" value={shareForm.recipient_email} onChange={e => setShareForm(p => ({ ...p, recipient_email: e.target.value }))} placeholder="owner@company.com" />
                            </div>
                            <div className="rp-form-group">
                                <label>Recipient Name *</label>
                                <input className="rp-input" value={shareForm.recipient_name} onChange={e => setShareForm(p => ({ ...p, recipient_name: e.target.value }))} placeholder="John Doe" />
                            </div>
                        </div>
                        <div className="rp-form-row">
                            <div className="rp-form-group">
                                <label>Permission</label>
                                <select className="rp-select" value={shareForm.permission} onChange={e => setShareForm(p => ({ ...p, permission: e.target.value }))}>
                                    <option value="update">View & Update (risk owner can update treatment, state, controls)</option>
                                    <option value="view">View Only</option>
                                </select>
                            </div>
                            <div className="rp-form-group">
                                <label>Expires In</label>
                                <select className="rp-select" value={shareForm.expires_days} onChange={e => setShareForm(p => ({ ...p, expires_days: +e.target.value }))}>
                                    <option value={7}>7 days</option><option value={14}>14 days</option>
                                    <option value={30}>30 days</option><option value={60}>60 days</option>
                                    <option value={90}>90 days</option>
                                </select>
                            </div>
                        </div>
                        <div className="rp-form-group">
                            <label>Message (optional)</label>
                            <textarea className="rp-textarea" rows={2} value={shareForm.message} onChange={e => setShareForm(p => ({ ...p, message: e.target.value }))} placeholder="Add a note for the recipient..." />
                        </div>
                        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                            <button className="rp-btn rp-btn-primary" onClick={handleShareRisk}>Send Share Link</button>
                        </div>

                        {/* Generated URL */}
                        {shareUrl && (
                            <div className="rp-share-url-box">
                                <label>Share Link (sent via email)</label>
                                <div className="rp-share-url-row">
                                    <input className="rp-input" readOnly value={shareUrl} onClick={e => { e.target.select(); navigator.clipboard?.writeText(shareUrl); flash('Link copied!'); }} />
                                    <button className="rp-btn" onClick={() => { navigator.clipboard?.writeText(shareUrl); flash('Link copied!'); }}>Copy</button>
                                    <a className="rp-btn" href={shareUrl} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}>Open ↗</a>
                                </div>
                            </div>
                        )}

                        {/* Existing shares */}
                        {riskShares.length > 0 && (
                            <div style={{ marginTop: 16 }}>
                                <h4 style={{ fontSize: 12, fontWeight: 600, color: 'var(--rp-text-3)', marginBottom: 8, textTransform: 'uppercase' }}>Active Share Links</h4>
                                <table className="rp-table rp-table-sm">
                                    <thead><tr><th>Recipient</th><th>Permission</th><th>Expires</th><th>Views</th><th></th></tr></thead>
                                    <tbody>
                                        {riskShares.map(s => (
                                            <tr key={s.id} style={{ opacity: s.is_active ? 1 : 0.4 }}>
                                                <td>{s.recipient_name || s.recipient_email}</td>
                                                <td><span className="rp-type-badge">{s.permission}</span></td>
                                                <td>{s.expires_at ? fmtDate(s.expires_at) : 'Never'}</td>
                                                <td>{s.access_count}</td>
                                                <td>{s.is_active && <button className="rp-btn-sm-danger" onClick={() => handleRevokeShare(s.id)}>Revoke</button>}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    function renderKriCreateModal() {
        return (
            <div className="rp-modal-overlay" onClick={() => setShowKriCreateModal(false)}>
                <div className="rp-modal" onClick={e => e.stopPropagation()}>
                    <div className="rp-modal-header"><h3>Add Key Risk Indicator</h3><button className="rp-modal-close" onClick={() => setShowKriCreateModal(false)}>×</button></div>
                    <div className="rp-modal-body">
                        <div className="rp-form-group"><label>Name</label><input className="rp-input" value={kriForm.name} onChange={e => setKriForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Failed logins per day" /></div>
                        <div className="rp-form-group"><label>Description</label><textarea className="rp-textarea" rows={2} value={kriForm.description} onChange={e => setKriForm(p => ({ ...p, description: e.target.value }))} /></div>
                        <div className="rp-form-row">
                            <div className="rp-form-group"><label>Unit</label><input className="rp-input" value={kriForm.unit} onChange={e => setKriForm(p => ({ ...p, unit: e.target.value }))} placeholder="count, %, $" /></div>
                            <div className="rp-form-group"><label>Current Value</label><input className="rp-input" type="number" step="any" value={kriForm.current_value} onChange={e => setKriForm(p => ({ ...p, current_value: e.target.value }))} /></div>
                        </div>
                        <div className="rp-form-row">
                            <div className="rp-form-group"><label>Warning Threshold</label><input className="rp-input" type="number" step="any" value={kriForm.threshold_warning} onChange={e => setKriForm(p => ({ ...p, threshold_warning: e.target.value }))} /></div>
                            <div className="rp-form-group"><label>Breach Threshold</label><input className="rp-input" type="number" step="any" value={kriForm.threshold_breach} onChange={e => setKriForm(p => ({ ...p, threshold_breach: e.target.value }))} /></div>
                        </div>
                    </div>
                    <div className="rp-modal-footer">
                        <button className="rp-btn" onClick={() => setShowKriCreateModal(false)}>Cancel</button>
                        <button className="rp-btn rp-btn-primary" onClick={handleCreateKriForRisk}>Create KRI</button>
                    </div>
                </div>
            </div>
        );
    }
}

// ═══════════════════════════════════════════════
// KPI CARD SUB-COMPONENT
// ═══════════════════════════════════════════════
function KpiCard({ label, value, color }) {
    return (
        <div className="rp-kpi-card">
            <div className="rp-kpi-value" style={{ color }}>{value ?? 0}</div>
            <div className="rp-kpi-label">{label}</div>
            <div className="rp-kpi-bar" style={{ background: color }} />
        </div>
    );
}
